/**
 * WallSketch Edit Session
 *
 * Owns all interactive editing state for the WallSketchEditorTool.
 *
 * UX model (inspired by Figma's pen tool + Blender's mesh edit mode):
 *  - Left-click a node or edge → enters edit mode + selects.
 *  - Left-drag a node → move it (with snapping).
 *  - Left-drag an edge → translate the whole edge (both endpoints).
 *  - Right-drag a node → extrude: drag out a new edge to a target.
 *  - Right-drag an edge → split: insert a new node at the drag start, drag out a new edge.
 *  - Hover an edge → preview node sphere at the closest point (visualises split position).
 *  - Delete / Backspace → remove selected node/edge.
 *  - Esc → revert all session edits, exit edit mode.
 *  - Enter → commit + persist via `edit_skeleton`.
 *
 * Snap modifiers (active during drag):
 *  - default → snap to nodes, on-edge points, axis (X/Y from drag origin),
 *              and parallel/perpendicular to the source edge (extrude only).
 *  - Shift   → snap only to existing geometry (nodes + edge points). Drops the soft guides.
 *  - Ctrl    → no snapping at all (free movement).
 *
 * Edits mutate the scene skeleton in place; ESC reverts to the snapshot
 * captured on entry. Each discrete operation pushes its own undo snapshot.
 */

import * as THREE from 'three';
import { BoxSelectController } from '../modules/selection/BoxSelectController';
import { PixelHitTester } from '../modules/selection/PixelHitTester';
import { WallSketchSelection } from '../modules/selection/WallSketchSelection';
import { ICON_TRASH_SVG, ICON_REFRESH_SVG, ICON_RIGHT_ANGLE_SVG, ICON_PARALLEL_SVG, ICON_RULER_SVG } from '../modules/contextMenu/ContextMenu';
import { orthogonalizeEdges, parallelizeEdges, OrthoEdge, OrthogonalizeInput, OrthogonalizeResult } from './wallSketchOrthogonalize';
import { getSnapStepDisplay, metersToDisplay, displayToMeters } from '../modules/line-detection/distanceUnits';
import { RadialMenu, RadialMenuItem } from '../modules/radialMenu/RadialMenu';
import { measureEdgeThickness, FloorSkeleton } from '../../../utils/SkeletonProcessingPipeline';
import { Viewer } from '../../../viewer/viewer';
import { isAnnotationPickBlocked } from '../../../viewer/AnnotationBlacklistStack';
import { CameraMode } from '../../../defines';
import { PointoramaWallSketch, SkeletonNodeData, SkeletonEdgeData } from '../../../utils/PointoramaWallSketch';
import { applyWeldedEdgeSizes } from '../../../utils/skeleton/sdfPartition';
import { mergeEdgeMetadata, mergeNodeMetadata } from '../../../utils/SkeletonMetadataMerge';
import { SphereMarker } from '../../../utils/SphereMarker';
import { TriangleHandleMarker, BowHandleMarker, EditHandleVisual } from '../../../utils/HandleGlyphMarkers';
import { Utils } from '../../../utils';
import { ToolUndoRedoManager, Snapshot } from '../ToolUndoRedoManager';
import { DragEventData } from '../../BaseTool';
import { WallSketchSnapEngine, SnapKind, SnapResult, SnapModifier, SnapNode, SnapEdge, ConstructionLine, bucketConstructionLines, projectMouseToFloor, closestPointOnInfiniteLine } from './WallSketchSnapEngine';
import { WallSketchSnapVisuals, SnapLineRender } from './WallSketchSnapVisuals';
import {
  closestPointOnEdge,
  splitBulge,
  arcMidpoint,
  edgeIsCurved,
  edgeLength as arcEdgeLength,
  pointAtArcLength,
  segmentArcIntersections,
  bulgeFromSagitta,
  sagittaFromBulge,
  radiusFromBulge,
  ARC_MAX_INCLUDED_ANGLE,
  BULGE_STRAIGHT_EPS,
  tessellateBandPair,
} from '../../../utils/skeleton/arcGeometry';

// ─── Brand palette (mirrors the CSS custom properties in index.css) ──────
/** neon-green-500 — primary brand green. */
const BRAND_NEON_GREEN = new THREE.Color('#7EDD64');
/** neon-green-300 — lighter brand green for secondary accents. */
const BRAND_NEON_GREEN_LIGHT = new THREE.Color('#B5FFA1');
/** french-violet-500 — brand accent that pops against the green walls. */
const BRAND_FRENCH_VIOLET = new THREE.Color('#854DD6');
/** oil-blue-500 — brand blue for guides/previews. */
const BRAND_OIL_BLUE = new THREE.Color('#269FC6');

// Edit visuals — drawn from the brand palette. Nodes/edges use french violet
// so they pop against the green wall fill (the old orange did the same job).
const EDIT_MODE_COLOR = BRAND_FRENCH_VIOLET;
const PREVIEW_NODE_COLOR = BRAND_NEON_GREEN_LIGHT;
const SNAP_NODE_COLOR = BRAND_NEON_GREEN;
const EXTRUDE_PREVIEW_COLOR = BRAND_OIL_BLUE;
const BOW_HANDLE_COLOR = BRAND_FRENCH_VIOLET; // curvature (bow) handle — distinct from green width + blue move handles
/**
 * Screen-space CLEARANCE of the bow handle beyond the wall's outer FACE on
 * its park side. The world offset additionally includes that side's wall
 * thickness, so the glyph always sits fully outside the wall body — clear of
 * the move handle (centerline) and the width triangles (on the faces) — on
 * thin and thick walls alike.
 */
const BOW_HANDLE_CLEARANCE_PX = 34;
/** The bow glyph renders larger than the sphere/triangle handles so the
 * curve-the-wall affordance reads at a glance. */
const BOW_HANDLE_BASE_SCALE = 2.4;
const BOW_HANDLE_HOVER_SCALE = 3.0;
/** Grab radius matched to the enlarged glyph (the generic 11 px handle radius
 * would only cover its center). */
const BOW_HANDLE_GRAB_RADIUS_PX = 18;
/** Bow drags snap back to a straight wall when the sagitta is within this many
 * pixels of zero (converted to world meters, floored at 2 cm). */
const BOW_SNAP_STRAIGHT_PX = 10;
const WALL_HANDLE_COLOR = BRAND_NEON_GREEN; // brand green — hover box + side handles

/**
 * Render order for editor helper visuals (handles, preview/snap markers, snap
 * lines). Must sit above the skeleton meshes (renderOrder ≤ 5) so handles,
 * hover dots and snap lines are never hidden behind the 2D/3D wall mesh. The
 * dashed line helpers already use 999; markers share the band.
 */
const HELPER_RENDER_ORDER = 999;

/**
 * Snap thresholds expressed in **screen pixels** so they feel consistent at
 * any zoom level. The session converts these to world meters per drag frame
 * via the camera projection (see `computeSnapRadii`). Tight by design.
 */
const SNAP_NODE_RADIUS_PX = 12; // "land exactly on this node"
const SNAP_LINE_TOLERANCE_PX = 14; // distance to a construction line that engages a snap
/** Pixel distance from cursor to a node's projected center for hover to engage. */
const HOVER_NODE_RADIUS_PX = 14;
/** Pixel distance from cursor to a projected edge segment for hover to engage. */
const HOVER_EDGE_RADIUS_PX = 12;
/**
 * Pixel distance from cursor to a wall's centerline at which the side
 * handles stay visible. Larger than `HOVER_EDGE_RADIUS_PX` because the
 * handles sit OFF the centerline (on each offset side) — without the
 * extra slack a cursor just outside the wall fill drops below the edge
 * hover threshold and the balls vanish even though the user is clearly
 * still pointing at the wall.
 */
const HOVER_WALL_HANDLE_RADIUS_PX = 32;
/**
 * Pixel grab radius for the wall side handles — the distance within which the
 * cursor counts as "over this handle" for hover-highlight and resize-drag.
 * Kept close to the handle ball's on-screen radius (the `SphereMarker` is sized
 * to ~9 px: `updateScale` targets a 15 px unit radius × `scaleFactor` 1.5 ×
 * geometry radius 0.4) plus a small forgiveness margin, so a grab requires the
 * cursor to actually be on the ball rather than merely near the wall. The
 * handle lights up (hover-highlight) only inside this radius, so the user can
 * see exactly what they'll grab. Left/right stay disambiguated by
 * nearest-distance.
 */
const GRAB_WALL_HANDLE_RADIUS_PX = 11;
/** Resting `SphereMarker.scaleFactor` for the wall side + move handles. */
const WALL_HANDLE_BASE_SCALE = 1.5;
/** Enlarged scale applied to a handle while the cursor is over it (hover cue). */
const WALL_HANDLE_HOVER_SCALE = 2.1;
/** Strict mode (Shift) tightens by this factor — only existing-node lock counts there. */
const STRICT_TIGHTEN = 0.5;
/** Floor on world-meter snap radius regardless of zoom; prevents zero-radius at extreme zoom-out. */
const MIN_SNAP_RADIUS_M = 0.001;
/** Ceiling on world-meter snap radius regardless of zoom; prevents huge radii at extreme zoom-in. */
const MAX_SNAP_RADIUS_M = 1.5;

function clampNumber(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Proper intersection of segments p1→p2 and p3→p4. Returns the crossing point
 * plus its parameters `t` (along p1→p2) and `u` (along p3→p4), or null when the
 * segments are parallel or only meet at/through an endpoint. Endpoints are
 * excluded (`eps < t,u < 1-eps`) so shared corners and T-touches aren't treated
 * as crossings — only walls that genuinely pass THROUGH each other.
 */
function segmentSegmentIntersection(p1: { x: number; y: number }, p2: { x: number; y: number }, p3: { x: number; y: number }, p4: { x: number; y: number }, eps = 1e-6): { point: { x: number; y: number }; t: number; u: number } | null {
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-12) return null; // parallel / degenerate
  const dx = p3.x - p1.x;
  const dy = p3.y - p1.y;
  const t = (dx * d2y - dy * d2x) / denom;
  const u = (dx * d1y - dy * d1x) / denom;
  if (t <= eps || t >= 1 - eps || u <= eps || u >= 1 - eps) return null;
  return { point: { x: p1.x + t * d1x, y: p1.y + t * d1y }, t, u };
}

/**
 * Intersection of two INFINITE lines, each given by a point + direction vector.
 * Returns the crossing point, or null when the lines are parallel (within eps
 * on the direction cross product). Unlike {@link segmentSegmentIntersection}
 * this does not clamp to segment bounds — used to slide a node to where two
 * wall centerlines cross, even past either segment's current extent.
 */
function infiniteLineIntersection(p1: { x: number; y: number }, d1: { x: number; y: number }, p2: { x: number; y: number }, d2: { x: number; y: number }): { x: number; y: number } | null {
  const denom = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(denom) < 1e-9) return null; // parallel
  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / denom;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
}

/**
 * Neighbour classification threshold for the perpendicular wall move: a
 * neighbour wall within 20° of parallel to the moved wall can never absorb
 * the slide (its line never crosses the translated centerline), so it is a
 * blocker (extrude) rather than a slideable follower. Shared between the
 * per-frame slide and the drag-end vanished-wall collapse so both agree on
 * which neighbour lines an endpoint travels along.
 */
const PERP_MOVE_PARALLEL_COS = Math.cos((20 * Math.PI) / 180);

/**
 * Append every segment of a closed ring to `out` as `SnapEdge` records,
 * with positions translated to session-relative coords (subtracting the
 * helpers-group reference). `idPrefix` distinguishes ring kinds in the
 * resulting snap ids (room-edge / wall-shell / wall-cavity).
 */
function pushRingAsSegments(out: SnapEdge[], ring: { x: number; y: number }[], idPrefix: string, refX: number, refY: number): void {
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    out.push({
      id: `${idPrefix}:${i}`,
      start: { x: a.x - refX, y: a.y - refY },
      end: { x: b.x - refX, y: b.y - refY },
    });
  }
}

export interface WallSketchUndoData {
  nodes: SkeletonNodeData[];
  edges: SkeletonEdgeData[];
}

/**
 * What the hosting tool allows the session to do. The session is shared
 * between the full editor (everything on), the wall CREATE tool (creation
 * gestures only — no selection, so every grab extrudes/draws and right-click
 * deletes the hovered element) and the wall EDIT tool (selection + editing,
 * no wall-creating gestures). Right-click-delete-hovered is always available.
 */
export interface WallSketchSessionCapabilities {
  /** Wall-creating gestures: draw-wall, extrude-from-node, extrude-from-edge. */
  allowCreate: boolean;
  /**
   * Selection and everything downstream of it: click/box select, the
   * selection-gated move gestures, width/bow/move handles, the radial context
   * menu, insert-vertex on a selected edge, opening-glyph selection, and
   * `wall_sketch_element_selected` emits.
   */
  allowSelect: boolean;
}

export const DEFAULT_SESSION_CAPABILITIES: WallSketchSessionCapabilities = {
  allowCreate: true,
  allowSelect: true,
};

export interface WallSketchEditSessionDeps {
  viewer: Viewer;
  getSceneWallSketch: () => PointoramaWallSketch | null;
  getUndoRedo: () => ToolUndoRedoManager | null;
  /** Hook for the tool to clear its `generatedWallSketch` field after a restore. */
  onWallSketchRestored?: () => void;
  /** Forwards the tool's protected dispatchStateChanged so the session can switch hint phases. */
  dispatchPhase: (phase: string) => void;
  /**
   * Build a pipeline-style FloorSkeleton from the current scene skeleton +
   * the rooms on the active floor. Returns null when no skeleton exists or
   * the floor has no rooms. The tool owns this because constructing a
   * FloorSkeleton requires room polygons and the room→wall conversion that
   * lives in `FloorProcessors`. Used by the on-demand wall-width recompute
   * triggered from the context menu.
   */
  buildFloorSkeleton?: () => FloorSkeleton | null;
  /** Gesture capabilities of the hosting tool. Defaults to everything on. */
  capabilities?: WallSketchSessionCapabilities;
}

type DragKind = 'none' | 'move-node' | 'move-edge' | 'move-edge-perp' | 'extrude-from-node' | 'extrude-from-edge' | 'draw-wall' | 'box-select' | 'resize-wall-side' | 'bow-edge';

type WallSide = 'left' | 'right';

interface DragState {
  kind: DragKind;
  /** Node being moved (move-node) or extruded from (extrude-from-node). */
  nodeId?: string;
  /** Edge being moved (move-edge), extruded from (extrude-from-edge), or resized (resize-wall-side). */
  edgeId?: string;
  /** For resize-wall-side: which side of the wall is being adjusted. */
  side?: WallSide;
  /**
   * For extrude-from-edge: split point on the edge captured at drag start,
   * stored in **session-relative** 2D coords. Becomes the start of the new
   * edge once drag commits. Also set for draw-wall (the snapped press point)
   * so the shared extrude preview draws from it.
   */
  splitPoint?: THREE.Vector2;
  /**
   * For draw-wall: the resolved snap at the PRESS point. Commit resolves it
   * to an existing/new node (connecting to whatever it snapped on) and draws
   * the wall from there to the release snap.
   */
  startSnap?: SnapResult;
  /**
   * Origin used by axis/parallel/perpendicular snaps. **Relative coords.**
   * For moves: the geometry's anchor (node center / click point on edge).
   */
  dragOrigin: THREE.Vector2;
  /**
   * Offset between the cursor's floor-projection at drag start and the drag
   * origin. Subtracted from the live cursor each frame so the snap candidate
   * represents where the geometry's anchor would land — not the raw cursor.
   * **Relative coords** (just a delta — same in both spaces).
   */
  cursorOffset: THREE.Vector2;
  /**
   * For move-node: original relative position so we can compute deltas.
   * For move-edge: { nodeId → original relative position } for both endpoints.
   */
  originalPositions: Map<string, { x: number; y: number }>;
  /** True once we've pushed the pre-drag undo snapshot (deferred until first move). */
  snapshotted: boolean;
  /** Last snap result (for visual indicator). */
  lastSnap: SnapResult | null;
  /**
   * Mouse button that started the drag (0=left, 2=right). Releasing with the
   * OTHER button is a cancel — see `onDragEnd`. Defaults to left when unset.
   */
  startButton?: number;
  /**
   * For extrude drags: the skeleton's node/edge data captured at drag start.
   * Each move re-applies this base PLUS a temporary preview edge so the wall
   * skinning renders live with the new line. Restored before commit (so commit
   * runs on clean state) and on cancel. Undefined for non-extrude drags.
   */
  previewBaseNodes?: SkeletonNodeData[];
  previewBaseEdges?: SkeletonEdgeData[];
  /** True while a live preview edge is currently applied to the skeleton. */
  previewApplied?: boolean;
  /** For move-edge-perp and bow-edge: the edge's unit chord direction and
   * left-normal, in relative coords, captured at drag start. The move is
   * constrained to `moveNormal`. */
  moveDir?: { x: number; y: number };
  moveNormal?: { x: number; y: number };
  /** For bow-edge: chord length captured at drag start (endpoints don't move). */
  bowChordLen?: number;
}

export class WallSketchEditSession {
  private deps: WallSketchEditSessionDeps;
  /** Resolved gesture capabilities (deps.capabilities with defaults applied). */
  private readonly caps: WallSketchSessionCapabilities;

  // Live hover state — driven by the mousemove-driven hover handler. Used by
  // the tooltip and the no-drag click handler. Will get clobbered during a
  // mousedown→dragStart window because mousemoves keep firing — that's why
  // drag-start can NOT rely on this. See `clickTarget*` below.
  private hoveredNodeId: string | null = null;
  private hoveredEdgeId: string | null = null;
  /** Identifier of the door/window opening whose 2D glyph is under the cursor,
   * or null. Lets clicking a glyph select the underlying opening annotation —
   * the same behavior the DoorWindowTool offers. Independent of node/edge
   * hover (an opening glyph can overlap a wall edge). */
  private hoveredAnnotationId: string | null = null;
  /**
   * One-shot flag: set when a right-button release cancelled an in-progress
   * drag, consumed by the next `onRightClick` so the cancel doesn't also fire
   * the delete-node/edge gesture on whatever sits under the cursor.
   */
  private suppressNextRightClick = false;
  /** Closest point on the hovered edge, in world coords. Null when no edge hover. */
  private hoveredEdgePoint: THREE.Vector3 | null = null;
  /**
   * Whether the hovered edge was hit on its thin skeleton centerline (vs the
   * thick wall band). Creating a node by clicking an edge requires a centerline
   * hit, so clicking the wall skin selects but never splits. Edge selection and
   * extrude-from-edge still accept band hits.
   */
  private hoveredEdgeOnCenterline = false;

  // Click-target snapshot — captured at mousedown and held until mouseup/end.
  // BaseTool requires 5px of movement before firing onDragStart, and during
  // those 5px the live hover can drift off the node sphere, leaving
  // hoveredNodeId null exactly when onDragStart fires. Reading from these
  // snapshot fields instead means a slightly-jittery click still drags the
  // node the user originally pressed on.
  private clickTargetNodeId: string | null = null;
  private clickTargetEdgeId: string | null = null;
  private clickTargetEdgePoint: THREE.Vector3 | null = null;

  // Edit-mode state
  private editing = false;
  private originalData: WallSketchUndoData | null = null;
  private sessionPushCount = 0;
  // Selection state + visuals (cyan border on selected node markers, cyan
  // LineSegments2 overlay on selected edges) is owned by the active
  // PointoramaWallSketch itself; the session resolves to it via a getter.
  // setData() rebuilds need a `reapplyVisuals()` call so new markers + line
  // geometry pick up the highlight.
  private get selection(): WallSketchSelection | null {
    return this.deps.getSceneWallSketch()?.getSelection() ?? null;
  }

  /**
   * Edge ids whose wall handles are LOCKED on — every currently-selected edge.
   * Handles stay on these regardless of hover until they're deselected, and
   * each selected edge gets its own left/right pair. (Selecting an edge also
   * auto-selects its endpoint nodes; that's fine — we key purely off the
   * selected EDGE set here.)
   */
  private selectedEdgeIds(): string[] {
    const sel = this.selection;
    if (!sel) return [];
    return [...sel.getSelectedEdgeIds()];
  }
  /** Unsubscribe handle for the current skeleton's selection-changed event. */
  private unsubscribeSelection: (() => void) | null = null;

  /**
   * Session-local origin for all snap math. Captured on edit-mode entry from
   * the first node's position. All coord work (snap candidate, snap node
   * positions, drag origin, cursor offset, original positions) happens
   * relative to this point so the math stays close to (0, 0). At point-cloud
   * scales (UTM coords ~500km from world origin), float32 vertex buffers and
   * raycast intermediates lose ~6cm of precision — keeping snap math relative
   * eliminates that source of jitter.
   */
  private referencePoint: THREE.Vector2 | null = null;

  /**
   * Shared parent group for all session-owned visual helpers (preview node
   * sphere, snap marker, extrude preview line, construction guides). The
   * group is parented to viewer.scene.scene with its position set to the
   * session reference point on edit-mode entry — so children below it can
   * use *relative-coord* positions instead of absolute UTM-scale world
   * coords. SphereMarkers and small line geometries break down at UTM
   * coords because their auto-scale + float32 vertex buffers lose precision
   * — same root cause as the skeleton's nodeGroup uses a relative parent.
   *
   * Created on first use (typically when the user enters edit mode and the
   * first helper renders), positioned on edit-mode entry, removed on exit.
   */
  private helpersGroup: THREE.Group | null = null;

  // Drag state
  private drag: DragState | null = null;

  // Construction lines cached for the duration of a single drag. They
  // depend only on skeleton topology + drag context, both of which are
  // static during the interaction, so building them once on entry and
  // reusing per frame is correct AND avoids a class of frame-to-frame
  // snap-target flicker that arose when bucketing happened against a
  // candidate-filtered subset every frame. Cleared in `endDrag`.
  private cachedDragConstructionLines: ConstructionLine[] | null = null;

  // Snap engine
  private snapEngine = new WallSketchSnapEngine();

  // Pixel-space projection + hit-test helpers. Used by hover (project node
  // centers + edge endpoints to canvas pixels and distance-test the cursor)
  // and box-select commit (project + Cohen-Sutherland against the box rect).
  private readonly hitTester: PixelHitTester;

  // Helper visuals (preview node on edge hover, snap indicator during drag,
  // extrude target ghost). All independent of the scene skeleton, parented
  // to viewer.scene.scene.
  private previewMarker: SphereMarker | null = null;
  private snapMarker: SphereMarker | null = null;
  private extrudeLine: THREE.Line | null = null;

  // Wall-edge drag handles — one left/right triangle-glyph PAIR per wall
  // shown, each pointing outward from the wall body ("pull to thicken").
  // A wall is "shown" when hovered, or when its edge is selected (handles lock
  // to selected edges until deselected; multiple selected edges each get a
  // pair). Dragging a handle adjusts that side's `leftSize`/`rightSize` live,
  // with the same snap engine the menu-driven set-width mode uses. Keyed by
  // edge id; pairs are pooled and hidden rather than destroyed between frames.
  private wallHandlePairs = new Map<string, { left: TriangleHandleMarker; right: TriangleHandleMarker }>();
  // Translucent box drawn over the hovered wall, sized to its full thickness
  // (leftSize + rightSize) and length, so the user can see which wall is
  // hovered and how thick it is. Updated/hidden alongside the handles.
  private hoverBoxMesh: THREE.Mesh | null = null;
  private hoverBoxMat: THREE.MeshBasicMaterial | null = null;
  private hoveredHandle: { edgeId: string; side: WallSide } | null = null;
  // Per-shown-edge MOVE handle (one sphere at the centerline midpoint). Dragging
  // it slides the whole wall perpendicular to itself, sliding endpoint nodes
  // along slideable neighbours and extruding new edges where a neighbour can't
  // slide. Pooled like the width handles. Snapshot of the picked handle at
  // mousedown lives in `clickTargetMoveEdgeId`.
  private edgeMoveHandles = new Map<string, SphereMarker>();
  private hoveredMoveEdgeId: string | null = null;
  private clickTargetMoveEdgeId: string | null = null;

  // Bow (curvature) handle — one arc-ribbon glyph per shown edge, parked a
  // screen-space offset beside the wall's mid-arc point (radially outward
  // past the apex for curved edges, left of the midpoint for straight
  // ones). Dragging it bows the wall into a circular arc; same
  // pooled-marker lifecycle as the move handles above.
  private bowHandles = new Map<string, BowHandleMarker>();
  private hoveredBowEdgeId: string | null = null;
  private clickTargetBowEdgeId: string | null = null;
  private clickTargetHandle: { edgeId: string; side: WallSide } | null = null;

  // Box-select. Wraps the same AreaSelector the surface tools rely on, so
  // we get a consistent visual + the modifier-flag bookkeeping (Shift/Ctrl)
  // for replace/add/remove behavior in one place. The controller hands back
  // the box's pixel rect on commit; the session does the actual hit-test.
  private boxSelect: BoxSelectController | null = null;

  // Context menu — icon-only popup that appears next to the cursor when the
  // user finishes a "terminal" selection (plain click on geometry, box-drag
  // end with hits). Anchored in world space so it sticks to its origin as
  // the camera moves. Closed automatically when the selection becomes empty.
  private contextMenu: RadialMenu | null = null;

  // Width-resize visuals — dashed perpendicular line + numeric label. Used
  // by the wall-edge drag handles (`resize-wall-side` drag) to mirror the
  // perpendicular distance the user is pulling out from the centerline.
  // Hidden between drags.
  private setWidthVisual: THREE.Line | null = null;
  private setWidthVisualMat: THREE.LineDashedMaterial | null = null;
  private setWidthLabel: HTMLDivElement | null = null;
  /**
   * Renders all per-snap visuals: dashed guides, source highlights, glyphs,
   * tick marks. Shape varies by `ConstructionLine.kind`. Lazily constructed
   * on first use so we don't spin up a full bundle for users who never
   * snap. Disposed on session exit.
   */
  private snapVisuals: WallSketchSnapVisuals | null = null;

  // Edit-mode visual cache
  private originalLineColor: THREE.Color | null = null;
  private previousCursor = '';

  constructor(deps: WallSketchEditSessionDeps) {
    this.deps = deps;
    this.caps = deps.capabilities ?? DEFAULT_SESSION_CAPABILITIES;
    this.hitTester = new PixelHitTester(deps.viewer);
    // Selection lives on the PointoramaWallSketch — we attach to it on edit
    // entry (when the active skeleton is known) and detach on exit. The
    // edit-mode orange "deselected node" tint is set there too.
  }

  /** Translate a user-facing string via the viewer's TranslationService. */
  private t(source: string, options?: { swc?: boolean; args?: Record<string, string | number> }): string {
    return this.deps.viewer.translationService.t(source, options);
  }

  get isEditing(): boolean {
    return this.editing;
  }

  /** An in-flight wall-creating gesture — draw-wall or either extrude —
   * whether driven by a drag or by the create tool's click-click flow. */
  get isDrawingWall(): boolean {
    const kind = this.drag?.kind;
    return kind === 'draw-wall' || kind === 'extrude-from-node' || kind === 'extrude-from-edge';
  }

  /**
   * Whether interactive selection / box-drag should be available right now.
   * Currently restricted to the 2D top-down (orthographic) view — the snap
   * + selection math assumes a top-down floor-plane projection, which
   * works cleanly in ortho but produces unintuitive results in the 3D
   * perspective view. Gates `onClick` (selection) and box-select drag.
   * Move/extrude drags ignore this gate; they'll still misbehave in 3D
   * but are at least bounded by the existing click-target requirement.
   */
  private isInteractiveViewActive(): boolean {
    return this.deps.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC;
  }

  // ─── Hover ──────────────────────────────────────────────────────────

  /**
   * Measured (as-built) floors hide the interior skeleton graph — ALWAYS
   * (the renderer's hide rule in PointoramaWallSketch.applyGroupVisibility;
   * selecting the outline must not pop the stale interior skeleton into
   * view). The invisible geometry must not be hoverable, clickable or
   * box-selectable either, no matter the session's `editing` flag (an
   * empty-space box-select drag enters edit mode implicitly and the session
   * stays editing afterwards). Only OUTLINE-role edges are interactive: the
   * measured outer wall renders via the skin's boundary (red error segments
   * when broken), and the outline draw/continue/select flows depend on it.
   */
  private measuredOutlineOnly(skeleton: PointoramaWallSketch): boolean {
    return this.deps.viewer.floorplanRepresentation?.isOriginalActive(skeleton.groupIdentifier) ?? false;
  }

  /** Whether an edge is interactive under {@link measuredOutlineOnly}. */
  private isEdgeInteractive(skeleton: PointoramaWallSketch, edgeId: string): boolean {
    return !this.measuredOutlineOnly(skeleton) || skeleton.getEdge(edgeId)?.role === 'OUTLINE';
  }

  /** Whether a node is interactive under {@link measuredOutlineOnly} — it is
   *  when any of its edges is an OUTLINE edge. */
  private isNodeInteractive(skeleton: PointoramaWallSketch, nodeId: string): boolean {
    if (!this.measuredOutlineOnly(skeleton)) return true;
    const node = skeleton.getNode(nodeId);
    return !!node && node.edgeIds.some((eid) => skeleton.getEdge(eid)?.role === 'OUTLINE');
  }

  updateHover(event: MouseEvent): { nodeId: string | null; edgeId: string | null } {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) {
      this.clearHoverState();
      this.hidePreviewMarker();
      return { nodeId: null, edgeId: null };
    }

    // Hover detection runs in **screen-pixel space** via the skeleton's
    // shared pickers. The raycaster's per-mesh inverse-world-matrix
    // transform loses float32 precision against scaled markers at UTM
    // world coords + deep zoom; pixel-space dodges the precision dance.
    // See `PointoramaWallSketch.pickEdgeAtPixel` / `pickNodeAtPixel`.
    const camera = this.deps.viewer.scene.getActiveCamera();
    const mousePx = this.hitTester.mousePx(event);

    // Measured (as-built) floors hide the skeleton graph while idle — the
    // shared interactivity gate (also applied by box select) keeps the
    // invisible geometry from being hovered/edited then.
    const edgeInteractive = (edgeId: string): boolean => this.isEdgeInteractive(skeleton, edgeId);
    const nodeInteractive = (nodeId: string): boolean => this.isNodeInteractive(skeleton, nodeId);

    const rawNodePick = skeleton.pickNodeAtPixel(mousePx, this.hitTester, HOVER_NODE_RADIUS_PX);
    const nodePick = rawNodePick && nodeInteractive(rawNodePick.nodeId) ? rawNodePick : null;
    if (nodePick) {
      this.hoveredNodeId = nodePick.nodeId;
      this.hoveredEdgeId = null;
      this.hoveredEdgePoint = null;
      this.hoveredAnnotationId = null;
      this.hidePreviewMarker();
      // Keep handles locked to the selected edges even while the cursor is over
      // a node (e.g. one of an edge's endpoints) — they only release on
      // deselect, not on node hover. No hover box over a node.
      if (this.editing && !this.drag && this.caps.allowSelect) {
        this.updateWallHandles(this.selectedEdgeIds(), null);
        this.hoveredHandle = this.wallHandlePairs.size > 0 ? this.pickWallHandle(event) : null;
      }
      this.syncHoverToWallSketch();
      return { nodeId: nodePick.nodeId, edgeId: null };
    }

    // Door/window glyph symbols (leaf + swing arc / glass lines) — these extend
    // past the opening's drawn rectangle, so hit-test their polylines directly.
    // A glyph hit selects the underlying opening, same as in the DoorWindowTool.
    // Checked before edges so a glyph drawn over a wall still selects the opening.
    // Skipped when selection is off (create tool): a glyph can't be clicked
    // there, and letting it win the pick would shadow the wall underneath it.
    let glyphPick = this.caps.allowSelect ? skeleton.pickOpeningGlyphAtPixel(mousePx, this.hitTester, HOVER_EDGE_RADIUS_PX) : null;
    if (glyphPick) {
      // Respect the central pick gate (e.g. sketches are only selectable in
      // the floorplan persona's Design tab) — a blocked glyph falls through
      // to the wall pick underneath it.
      const opening = this.deps.viewer.scene.openingSketches.find((o) => o.identifier === glyphPick?.openingId);
      if (opening && isAnnotationPickBlocked(this.deps.viewer, opening, 'OPENING_SKETCH')) glyphPick = null;
    }
    if (glyphPick) {
      this.hoveredNodeId = null;
      this.hoveredEdgeId = null;
      this.hoveredEdgePoint = null;
      this.hoveredAnnotationId = glyphPick.openingId;
      this.hidePreviewMarker();
      this.updateWallHandles(this.editing && !this.drag ? this.selectedEdgeIds() : null);
      this.hoveredHandle = null;
      this.syncHoverToWallSketch();
      return { nodeId: null, edgeId: null };
    }
    this.hoveredAnnotationId = null;

    // Cursor on the floor plane — used for the preview-marker world point
    // after we pick a hovered edge.
    const floorForHover = projectMouseToFloor(event, this.deps.viewer.renderer.domElement, camera, skeleton.getFloorHeight());
    const rawEdgePick = skeleton.pickEdgeAtPixel(mousePx, this.hitTester, { centerlineRadiusPx: HOVER_EDGE_RADIUS_PX });
    const edgePick = rawEdgePick && edgeInteractive(rawEdgePick.edgeId) ? rawEdgePick : null;
    const bestEdgeId = edgePick?.edgeId ?? null;

    this.hoveredNodeId = null;
    if (bestEdgeId) {
      this.hoveredEdgeId = bestEdgeId;
      this.hoveredEdgeOnCenterline = edgePick?.hitKind === 'centerline';
      // Compute the world-space split point on the edge for the preview
      // marker — projects the cursor onto the segment.
      const edge = skeleton.getEdge(bestEdgeId);
      const ea = edge ? skeleton.getNode(edge.startNodeId) : undefined;
      const eb = edge ? skeleton.getNode(edge.endNodeId) : undefined;
      if (floorForHover && ea && eb && edge) {
        const projected = closestPointOnEdge({ x: floorForHover.x, y: floorForHover.y }, { x: ea.position.x, y: ea.position.y }, { x: eb.position.x, y: eb.position.y }, edge.bulge);
        this.hoveredEdgePoint = new THREE.Vector3(projected.point.x, projected.point.y, skeleton.getFloorHeight());
      } else {
        this.hoveredEdgePoint = null;
      }
    } else {
      this.hoveredEdgeId = null;
      this.hoveredEdgePoint = null;
      this.hoveredEdgeOnCenterline = false;
    }

    // The preview marker signals "a wall-creating gesture here branches off
    // this edge", so it's a CREATE-capability affordance, shown only on a
    // centerline hit — matching where the split will actually land. The full
    // editor keeps it to edit mode (its click-to-insert-vertex needs a
    // selected edge); the create tool shows it on plain hover since every
    // edge drag creates there. The edit tool (no creation) never shows it.
    const markerEligible = this.caps.allowCreate && (this.caps.allowSelect ? this.editing : true);
    if (markerEligible && this.hoveredEdgeId && this.hoveredEdgePoint && this.hoveredEdgeOnCenterline && !this.drag) {
      this.showPreviewMarker(this.hoveredEdgePoint);
    } else {
      this.hidePreviewMarker();
    }

    // Wall-edge handles follow the hovered edge — but use a more
    // forgiving radius than plain edge hover. A cursor just outside the
    // visible wall fill is still clearly pointing AT the wall, and the
    // balls would feel flaky if they disappeared the instant the cursor
    // left the centerline's 12 px hover band. Re-pick at the larger
    // `HOVER_WALL_HANDLE_RADIUS_PX` and use that result for the handles
    // only; preview-marker + selection still use the strict pick above.
    if (this.editing && !this.drag && this.caps.allowSelect) {
      // Show handles on every SELECTED edge (locked on until deselected) plus
      // the currently-hovered wall. The hover HIGHLIGHT is NOT locked (it keeps
      // following the wall pick above). `thicknessPadPx` extends the wall's hit
      // quad along its thickness axis so a side handle still shows when the
      // cursor sits just off the wall fill.
      // Same measured-floor interactivity filter as the strict pick above —
      // this forgiving re-pick would otherwise light up handles on the
      // hidden (non-outline) skeleton.
      const rawHoveredWall = bestEdgeId ?? skeleton.pickEdgeAtPixel(mousePx, this.hitTester, { centerlineRadiusPx: HOVER_WALL_HANDLE_RADIUS_PX, thicknessPadPx: HOVER_WALL_HANDLE_RADIUS_PX })?.edgeId ?? null;
      const hoveredWall = rawHoveredWall && edgeInteractive(rawHoveredWall) ? rawHoveredWall : null;
      const shown = new Set(this.selectedEdgeIds());
      if (hoveredWall) shown.add(hoveredWall);
      // Sticky bow handle: it parks OUTSIDE the wall body (beyond the hover
      // pad), so travelling from the wall onto the glyph would otherwise drop
      // the handles mid-flight and make it ungrabbable. Keep any edge whose
      // CURRENT bow handle (from the previous hover frame) is under the
      // cursor in the shown set.
      const stickyBowEdgeId = this.bowHandles.size > 0 ? this.pickBowHandle(event) : null;
      if (stickyBowEdgeId) shown.add(stickyBowEdgeId);
      this.updateWallHandles(shown, hoveredWall);
      this.hoveredHandle = shown.size > 0 ? this.pickWallHandle(event) : null;
      // BOW handle (arc apex / beside the midpoint) — shown on the same edges.
      // Width handle wins when both are under the cursor (it's checked first).
      this.updateBowHandles(shown);
      this.hoveredBowEdgeId = !this.hoveredHandle && shown.size > 0 ? this.pickBowHandle(event) : null;
      // Edge MOVE handle (centerline midpoint) — shown on the same edges. The
      // width and bow handles win when stacked (they're checked first).
      this.updateEdgeMoveHandles(shown);
      this.hoveredMoveEdgeId = !this.hoveredHandle && !this.hoveredBowEdgeId && shown.size > 0 ? this.pickEdgeMoveHandle(event) : null;
    } else {
      this.updateWallHandles(null);
      this.hoveredHandle = null;
      this.updateBowHandles(null);
      this.hoveredBowEdgeId = null;
      this.updateEdgeMoveHandles(null);
      this.hoveredMoveEdgeId = null;
    }

    this.syncHoverToWallSketch();
    return { nodeId: null, edgeId: this.hoveredEdgeId };
  }

  hasHoverTarget(): boolean {
    return this.hoveredNodeId !== null || this.hoveredEdgeId !== null;
  }

  /** Identifier of the door/window opening whose glyph is under the cursor, or
   * null. Read after `updateHover` to drive the hover cursor. */
  getHoveredAnnotationId(): string | null {
    return this.hoveredAnnotationId;
  }

  private clearHoverState(): void {
    this.hoveredNodeId = null;
    this.hoveredEdgeId = null;
    this.hoveredEdgePoint = null;
    this.hoveredAnnotationId = null;
    this.syncHoverToWallSketch();
  }

  /**
   * Push the session's private hover triple onto the skeleton's shared
   * `WallSketchHover` state so consumers outside the session (the
   * WallSketchEditorTool tooltip, any other tool that wants to read
   * "what's under the cursor") see one canonical hovered target.
   *
   * The session intentionally keeps its own private fields rather than
   * reading from the skeleton: click handlers do snapshot-style reads
   * (clickTargetNodeId etc.) at mousedown time and shouldn't be coupled
   * to whatever external code may have mutated the skeleton hover in
   * the same frame.
   */
  private syncHoverToWallSketch(): void {
    // Light up the handle under the cursor (and dim the rest) on every hover
    // update — this is the single chokepoint all `updateHover` paths funnel
    // through, so it stays in sync with the freshly-resolved pick state.
    this.refreshHandleHighlights();
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return;
    const hover = skeleton.getHover();
    if (this.hoveredNodeId) {
      hover.setNode(this.hoveredNodeId);
    } else if (this.hoveredEdgeId) {
      hover.setEdge(this.hoveredEdgeId, this.hoveredEdgePoint);
    } else {
      hover.clear();
    }
  }

  // ─── Mode entry / exit ──────────────────────────────────────────────

  /**
   * Click handler — selects what's hovered. Empty click clears selection
   * and (if no drag/extrude is in progress) leaves edit mode untouched so
   * the user can still pan/zoom and come back.
   */
  /**
   * Captured at the actual click pixel, before BaseTool's 5px drag threshold
   * has any chance to advance the cursor. We snapshot the hover target into
   * separate fields (`clickTarget*`) because the live hover state will get
   * overwritten by every mousemove between now and `onDragStart` — including
   * mousemoves that drift slightly off the node sphere.
   */
  onMouseDown(event: MouseEvent): void {
    // Clear any stale cancel-suppression from an earlier gesture so it can't
    // swallow a later, legitimate right-click. (Set in onDragEnd, normally
    // consumed by the matching onRightClick, but a >threshold right-release
    // skips that — this resets it on the next fresh interaction.)
    if (!this.drag) this.suppressNextRightClick = false;

    this.updateHover(event);
    this.clickTargetNodeId = this.hoveredNodeId;
    this.clickTargetEdgeId = this.hoveredEdgeId;
    this.clickTargetEdgePoint = this.hoveredEdgePoint?.clone() ?? null;
    this.clickTargetHandle = this.hoveredHandle;
    this.clickTargetBowEdgeId = this.hoveredBowEdgeId;
    this.clickTargetMoveEdgeId = this.hoveredMoveEdgeId;

    // A left press on EMPTY floor can only become a box select or a wall
    // draw — never a camera gesture — so block orbit controls RIGHT NOW.
    // Waiting for the drag threshold (where startBoxSelect blocks) loses the
    // race against the 2D controls' ctrl+left PAN binding: the camera pans
    // during the first pixels and keeps drifting on its damped deltas.
    // Released on a sub-threshold click (onClick) or drag end (endDrag).
    if (
      event.button === 0 &&
      this.isInteractiveViewActive() &&
      !this.clickTargetNodeId &&
      !this.clickTargetEdgeId &&
      !this.clickTargetHandle &&
      !this.clickTargetBowEdgeId &&
      !this.clickTargetMoveEdgeId
    ) {
      this.emptyPressOrbitBlock = true;
      this.deps.viewer.inputHandler?.blockOrbitControls?.();
    }
  }

  /** True while the empty-press orbit block (see onMouseDown) is held. */
  private emptyPressOrbitBlock = false;

  /** Release the empty-press orbit block after the gesture resolved. */
  private releaseEmptyPressOrbitBlock(): void {
    if (!this.emptyPressOrbitBlock) return;
    this.emptyPressOrbitBlock = false;
    this.deps.viewer.inputHandler?.unblockOrbitControls?.();
  }

  onClick(event: MouseEvent): void {
    // A sub-threshold click resolves the empty-press orbit block (a real
    // drag resolves it via endDrag's unblock instead).
    this.releaseEmptyPressOrbitBlock();
    // Selection is currently only available in the 2D ortho view (snap +
    // hit-testing math assumes a floor-plane projection).
    if (!this.isInteractiveViewActive()) return;
    // Click IS selection (select / toggle / insert-vertex / open menu) — a
    // no-selection host (the wall create tool) has nothing for it to do.
    if (!this.caps.allowSelect) return;

    // Refresh hover at the actual click position so a click without preceding
    // mousemove (cursor entered the canvas mid-click, etc.) still selects.
    this.updateHover(event);

    // Clicking a door/window glyph selects the underlying opening annotation
    // (mirrors the DoorWindowTool). This is annotation selection, not skeleton
    // node/edge selection, so it bypasses the edit-selection logic below.
    if (this.hoveredAnnotationId) {
      this.selectOpeningAnnotation(this.hoveredAnnotationId, event.ctrlKey || event.metaKey);
      this.clearClickTarget();
      return;
    }

    try {
      // Empty-space click clears the selection — but only if no modifier is
      // held. Modifier-empty clicks are no-ops (avoid surprise clears when
      // the user is mid-multi-select and misses a target).
      if (!this.hoveredNodeId && !this.hoveredEdgeId) {
        if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
          this.setSelection(null, null);
        }
        return;
      }

      // Whether this click is the one that's entering edit mode. The radial
      // menu is suppressed for that first click: popping it open here means the
      // very next pointer interaction (e.g. grabbing a thickness/move handle)
      // just closes the menu instead of starting the drag, which feels broken.
      const enteringEditMode = !this.editing;
      if (enteringEditMode) {
        this.enterEditMode();
      }

      const ctrl = event.ctrlKey || event.metaKey; // treat Cmd as Ctrl on macOS

      // Insert a point: a plain click on an edge that is ALREADY selected splits
      // it at the click location (the second click on a selected wall adds a
      // vertex). Same split the processor does — node + two half-edges inheriting
      // the wall's thickness/rooms — so the neighbouring edges keep their angles.
      // Only a hit on the thin skeleton centerline counts — clicking the thick
      // wall skin selects the wall but must not create a node on it.
      const skeletonForSplit = this.deps.getSceneWallSketch();
      if (skeletonForSplit && this.hoveredEdgeId && this.hoveredEdgePoint && this.hoveredEdgeOnCenterline && !this.hoveredNodeId && !ctrl && !event.shiftKey && this.selection?.getSelectedEdgeIds().has(this.hoveredEdgeId)) {
        const splitNodeId = this.splitEdgeAt(skeletonForSplit, this.hoveredEdgeId, this.toRelative(this.hoveredEdgePoint));
        if (splitNodeId) {
          // Select the freshly-inserted node so the user can immediately drag it.
          this.setSelection(splitNodeId, null);
          this.contextMenu?.hide();
        }
        return;
      }

      const nodeIds = this.hoveredNodeId ? [this.hoveredNodeId] : [];
      const edgeIds = this.hoveredEdgeId ? [this.hoveredEdgeId] : [];

      if (ctrl) {
        // Ctrl-click: toggle the hovered item out of the selection. If it
        // wasn't in the selection, ctrl-click adds it (matches Figma/macOS).
        const sel = this.selection;
        const inSelection = !!sel && ((this.hoveredNodeId && sel.getSelectedNodeIds().has(this.hoveredNodeId)) || (this.hoveredEdgeId && sel.getSelectedEdgeIds().has(this.hoveredEdgeId)));
        if (inSelection) {
          this.removeFromSelection(nodeIds, edgeIds);
        } else {
          this.addToSelection(nodeIds, edgeIds);
        }
      } else if (event.shiftKey) {
        // Shift-click: add to the existing selection.
        this.addToSelection(nodeIds, edgeIds);
      } else {
        // Plain click: replace the selection.
        this.setSelection(nodeIds, edgeIds);
      }

      // Pop the context menu next to the cursor's world position when the
      // click leaves at least one item selected. Skip when the selection is
      // now empty (e.g. ctrl-click that toggled off the only item), and skip on
      // the edit-mode-entry click so the menu doesn't intercept the user's
      // follow-up handle grab.
      const skeleton = this.deps.getSceneWallSketch();
      const sel = skeleton?.getSelection();
      if (!enteringEditMode && skeleton && sel && (sel.getSelectedNodeIds().size > 0 || sel.getSelectedEdgeIds().size > 0)) {
        const worldAnchor = projectMouseToFloor(event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
        if (worldAnchor) this.showContextMenuForSelection(worldAnchor);
      } else {
        this.contextMenu?.hide();
      }
    } finally {
      // Click ends the mousedown→mouseup window; the snapshot is no longer
      // useful for any subsequent interaction.
      this.clearClickTarget();
    }
  }

  /**
   * Select the OPENING_SKETCH whose glyph was clicked. The wall sketch draws its
   * door/window glyphs from OPENING_SKETCH annotations (keyed by their id in the
   * glyph hit-test map), so a glyph hit selects the corresponding opening sketch.
   * Clears any skeleton node/edge selection first (different selection domains)
   * and dispatches `measurement_select` so the sidebar / global selection picks
   * it up.
   */
  private selectOpeningAnnotation(openingSketchId: string, isCtrl: boolean): void {
    // Drop skeleton edit selection so the two domains don't show as co-selected.
    this.setSelection(null, null);
    this.contextMenu?.hide();

    const openingSketch = this.deps.viewer.scene.openingSketches.find((o) => o.identifier === openingSketchId);
    if (openingSketch) {
      this.deps.viewer.scene.dispatchEvent({
        type: 'measurement_select',
        value: { measure: openingSketch, isCtrl },
      });
    }
  }

  /**
   * Right-click handler — destructive shortcut. Hovering a node deletes that
   * node (`deleteNode` preserves straight pass-throughs). Hovering an edge
   * deletes the edge, then dissolves each endpoint only if it no longer
   * contributes (orphan or collinear pass-through) — leaves, corners, and
   * junctions are preserved because they still carry structural meaning.
   * Single undo snapshot for the whole gesture.
   */
  onRightClick(event: MouseEvent): void {
    // Consume a one-shot suppression set when this right-click just cancelled a
    // drag — don't also run the delete gesture.
    if (this.suppressNextRightClick) {
      this.suppressNextRightClick = false;
      return;
    }
    if (!this.isInteractiveViewActive()) return;
    this.updateHover(event);
    if (!this.hoveredNodeId && !this.hoveredEdgeId) return;

    if (!this.editing) {
      this.enterEditMode();
      if (!this.editing) return;
    }

    this.pushWallSketchUndoSnapshot();

    if (this.hoveredNodeId) {
      this.deleteNode(this.hoveredNodeId, { snapshot: false });
      return;
    }
    if (this.hoveredEdgeId) {
      const skeleton = this.deps.getSceneWallSketch();
      const edge = skeleton?.getEdge(this.hoveredEdgeId);
      const startId = edge?.startNodeId ?? null;
      const endId = edge?.endNodeId ?? null;
      this.deleteEdge(this.hoveredEdgeId, { snapshot: false });
      if (startId) this.dissolveNode(startId);
      if (endId && endId !== startId) this.dissolveNode(endId);
    }
  }

  private enterEditMode(referenceFallback?: THREE.Vector2): void {
    const data = this.captureWallSketchData();
    if (!data) return;
    this.originalData = data;
    this.editing = true;
    // Registered so the React property panel can read/write the WORKING
    // (uncommitted) skeleton state through this session. Cleared on exit.
    this.deps.viewer.activeWallSketchEditSession = this;
    this.sessionPushCount = 0;
    // Pin a reference point so all snap math runs near (0, 0). First node's
    // position matches the trick PointoramaWallSketch uses for its line geometry.
    // An EMPTY sketch (first wall being drawn) has no node — pin on the
    // caller-supplied fallback (the draw press point) instead.
    const first = data.nodes[0];
    this.referencePoint = first ? new THREE.Vector2(first.position.x, first.position.y) : (referenceFallback?.clone() ?? new THREE.Vector2(0, 0));
    this.ensureHelpersGroup();
    // 2D skinning is always on now (transparent at opacity 0.4) — no need
    // to flip a toggle on entry / restore on exit.
    const skeleton = this.deps.getSceneWallSketch();
    this.applyEditVisuals();
    // Hook into the skeleton's selection: while editing, deselect should
    // restore nodes to the orange edit tint, not to the default line color.
    // With selection off nothing can ever enter the selection, so the
    // subscription (context-menu close, `wall_sketch_element_selected` emits,
    // handle locking) has nothing to react to — skip it entirely.
    if (skeleton && this.caps.allowSelect) {
      const sel = skeleton.getSelection();
      sel.setDeselectedNodeColor(() => EDIT_MODE_COLOR);
      this.unsubscribeSelection = skeleton.subscribeSelectionChanged(() => {
        this.closeContextMenuIfEmpty();
        this.emitSelectedElement();
        // Lock handles onto the (possibly multiple) selected edges immediately,
        // without waiting for the next mousemove. Include the currently-hovered
        // wall too so handles don't blink off under the cursor on deselect.
        if (this.editing && !this.drag) {
          const shown = new Set(this.selectedEdgeIds());
          if (this.hoveredEdgeId) shown.add(this.hoveredEdgeId);
          this.updateWallHandles(shown, this.hoveredEdgeId);
        }
      });
    }
    this.deps.dispatchPhase('editing');
  }

  /**
   * Surface the selected skeleton nodes/edges to the React layer so the right
   * panel can show (and bulk-edit) their height constraints + thicknesses.
   * Fires `wall_sketch_element_selected` with `{ annotationIdentifier, kind, ids }`;
   * an empty `ids` clears the panel.
   */
  private emitSelectedElement(): void {
    const skeleton = this.deps.getSceneWallSketch();
    const sel = skeleton?.getSelection();
    let kind: 'node' | 'edge' | null = null;
    let ids: string[] = [];
    if (skeleton && sel) {
      const nodeIds = Array.from(sel.getSelectedNodeIds());
      const edgeIds = Array.from(sel.getSelectedEdgeIds());
      // Edges win when any are selected (their endpoint nodes are
      // auto-selected alongside them). Otherwise the selected nodes.
      if (edgeIds.length > 0) {
        kind = 'edge';
        ids = edgeIds;
      } else if (nodeIds.length > 0) {
        kind = 'node';
        ids = nodeIds;
      }
    }
    this.deps.viewer.scene.dispatchEvent({
      type: 'wall_sketch_element_selected',
      value: {
        annotationIdentifier: skeleton?.identifier ?? null,
        kind,
        ids,
      },
    });
  }

  /**
   * Tear down hover-only helper visuals (the create tool's preview marker can
   * exist without an edit session ever starting). No-op while editing — the
   * edit-mode exit path owns the teardown there. Hosting tools call this on
   * disable / floor change.
   */
  releaseHoverHelpers(): void {
    // Safety: never leave the empty-press orbit block behind when the host
    // tool tears down mid-gesture (tool switch between mousedown and click).
    this.releaseEmptyPressOrbitBlock();
    if (this.editing) return;
    this.disposeHelpers();
    this.referencePoint = null;
  }

  cancel(): void {
    if (!this.editing) return;
    this.cancelDrag();
    const skeleton = this.deps.getSceneWallSketch();
    if (skeleton && this.originalData) {
      skeleton.setData(this.originalData.nodes, this.originalData.edges, null);
      this.deps.onWallSketchRestored?.();
    }
    this.dropSessionUndoEntries();
    this.exitEditMode();
  }

  commit(): void {
    if (!this.editing) return;
    this.cancelDrag();
    const skeleton = this.deps.getSceneWallSketch();
    if (skeleton) {
      // Persist the skin's pass-through thickness weld: connected near-equal
      // walls render as one width — on commit (never mid-drag) the properties
      // match the skinning. Same weld the renderer applies, so data and
      // visuals agree after every edit.
      const nodes = skeleton.getNodes();
      const weldedEdges = applyWeldedEdgeSizes(nodes, skeleton.getAllEdges());
      skeleton.setData(nodes, weldedEdges, skeleton.getMetadata());
      this.deps.viewer.scene.dispatchEvent({
        type: 'edit_skeleton',
        value: {
          annotationIdentifier: skeleton.identifier,
          nodes,
          edges: weldedEdges,
        },
      });
    }
    this.exitEditMode();
  }

  private exitEditMode(): void {
    // Unhook from the skeleton's selection: clear visuals + the orange-tint
    // override + the subscription, in that order. Selection state itself
    // stays where the skeleton owns it; we just stop driving its visuals.
    const sel = this.selection;
    sel?.clearVisuals();
    sel?.setDeselectedNodeColor(undefined);
    if (this.unsubscribeSelection) {
      this.unsubscribeSelection();
      this.unsubscribeSelection = null;
    }
    // Tell the React layer no skeleton element is selected anymore so the
    // right panel reverts from the per-element view.
    this.deps.viewer.scene.dispatchEvent({
      type: 'wall_sketch_element_selected',
      value: { annotationIdentifier: null, kind: null, ids: [] },
    });
    this.removeEditVisuals();
    this.disposeHelpers();
    // 2D skinning is always on (transparent) now — no save/restore needed.
    this.editing = false;
    if (this.deps.viewer.activeWallSketchEditSession === this) {
      this.deps.viewer.activeWallSketchEditSession = null;
    }
    this.originalData = null;
    this.referencePoint = null;
    this.selection?.clearSilent();
    this.deps.dispatchPhase('idle');
  }

  // ─── Selection + delete-key removal ─────────────────────────────────

  /**
   * Delete or Backspace key: remove every selected node and edge in one
   * batched operation. Pushes a single undo snapshot regardless of how many
   * items the selection contains, so a multi-select delete undoes as one
   * step.
   *
   * Selecting an edge implicitly selects its endpoint nodes (see the
   * selection wrappers) — those endpoints must NOT be hard-deleted here:
   * deleting an edge should only DISSOLVE its endpoints (drop orphans,
   * merge collinear pass-throughs) so leaves, corners, and junctions the
   * user never explicitly targeted survive with their other walls intact.
   * Same semantics as the right-click delete gesture. Only nodes selected
   * in their own right (not merely as endpoints of a selected edge) go
   * through the full collinear-aware deleteNode.
   *
   * Order: explicit nodes first (their incident edges are handled by the
   * collinear-aware deleteNode), then edges (no-op for edges a node delete
   * already removed), then endpoint dissolution. Returns true if anything
   * was deleted.
   */
  deleteSelection(): boolean {
    if (!this.editing) return false;
    const sel = this.selection;
    if (!sel) return false;
    const nodeIds = Array.from(sel.getSelectedNodeIds());
    const edgeIds = Array.from(sel.getSelectedEdgeIds());
    if (nodeIds.length === 0 && edgeIds.length === 0) return false;

    // Resolve endpoints BEFORE deleting the edges — getEdge needs them live.
    const endpointIds = new Set(this.endpointNodeIds(edgeIds));

    this.pushWallSketchUndoSnapshot();
    for (const id of nodeIds) {
      if (!endpointIds.has(id)) this.deleteNode(id, { snapshot: false });
    }
    for (const id of edgeIds) this.deleteEdge(id, { snapshot: false });
    for (const id of endpointIds) this.dissolveNode(id);
    return true;
  }

  /**
   * Delete a node, but **preserve any straight pass-throughs**. For each
   * collinear pair of incident edges (one going north, one going south, etc.)
   * we replace the pair with a single edge between the far endpoints, so a
   * deleted junction on a long wall doesn't shred the wall into two unconnected
   * stubs. Non-collinear incident edges are removed normally.
   *
   * Behavior:
   *   - Pure degree-1 (leaf) → remove node + the one edge.
   *   - Pure degree-2 collinear (180°) → dissolve into one straight edge.
   *   - Pure degree-2 non-collinear (corner) → remove node + both edges.
   *   - Higher degree with one collinear pair → dissolve the pair, drop the rest.
   *   - Higher degree with multiple non-overlapping collinear pairs → dissolve
   *     each pair (each edge can participate in at most one pair), drop the rest.
   */
  private deleteNode(nodeId: string, opts: { snapshot?: boolean } = {}): void {
    const skeleton = this.deps.getSceneWallSketch();
    const node = skeleton?.getNode(nodeId);
    if (!skeleton || !node) return;

    if (opts.snapshot !== false) this.pushWallSketchUndoSnapshot();

    const collinearPairs = this.findCollinearPairs(skeleton, nodeId);

    // Edges that are part of a collinear pair will be replaced by a merged
    // edge; tracking them lets us know which incident edges to *replace* vs
    // simply *remove*.
    const pairedEdgeIds = new Set<string>();
    for (const [eA, eB] of collinearPairs) {
      pairedEdgeIds.add(eA.id);
      pairedEdgeIds.add(eB.id);
    }

    // Build the merged "pass-through" edges for each collinear pair, while
    // honouring the existing-connection check (don't create a duplicate edge).
    const mergedEdges: SkeletonEdgeData[] = [];
    const edgeIdToFarEndpoint: Map<string, string> = new Map(); // for patching node.edgeIds
    for (const [eA, eB] of collinearPairs) {
      const farA = eA.startNodeId === nodeId ? eA.endNodeId : eA.startNodeId;
      const farB = eB.startNodeId === nodeId ? eB.endNodeId : eB.startNodeId;
      const alreadyConnected = skeleton.getAllEdges().some((e) => e.id !== eA.id && e.id !== eB.id && ((e.startNodeId === farA && e.endNodeId === farB) || (e.startNodeId === farB && e.endNodeId === farA)));
      if (alreadyConnected) continue;
      const mergedId = this.generateId();
      // Union roomIds + metadata from both halves so an edge fused from
      // (roomA|roomB) and (roomB|roomC) ends up touching all three —
      // critical for room-membership lookups on the survivor. Sizes /
      // wallType stay with `eA`: those describe wall geometry that
      // physically belongs to the surviving line, not metadata.
      const { roomIds, metadata } = mergeEdgeMetadata(eA, eB);
      const merged: SkeletonEdgeData = {
        ...eA,
        id: mergedId,
        name: this.generateName('edge'),
        startNodeId: farA,
        endNodeId: farB,
        roomIds,
        metadata,
      };
      mergedEdges.push(merged);
      edgeIdToFarEndpoint.set(eA.id, mergedId);
      edgeIdToFarEndpoint.set(eB.id, mergedId);
    }

    // Original incident edges to drop (any incident edge, paired or otherwise —
    // the merged edges replace the paired ones, the unpaired ones just disappear).
    const incidentEdgeIds = new Set(
      skeleton
        .getAllEdges()
        .filter((e) => e.startNodeId === nodeId || e.endNodeId === nodeId)
        .map((e) => e.id),
    );

    const survivingEdges = skeleton
      .getAllEdges()
      .filter((e) => !incidentEdgeIds.has(e.id))
      .concat(mergedEdges);

    // Patch each remaining node's edgeIds:
    //   - drop incident edges that were paired-and-merged → replace with merged edge id
    //   - drop incident edges that were paired-but-skipped (already-connected case)
    //   - drop incident edges that were unpaired (just gone)
    // The deleted node itself is filtered out entirely.
    const cleanedNodes = skeleton
      .getNodes()
      .filter((n) => n.id !== nodeId)
      .map((n) => {
        const newEdgeIds: string[] = [];
        const seenMerged = new Set<string>();
        for (const eid of n.edgeIds) {
          if (incidentEdgeIds.has(eid)) {
            const mergedId = edgeIdToFarEndpoint.get(eid);
            if (mergedId && !seenMerged.has(mergedId)) {
              newEdgeIds.push(mergedId);
              seenMerged.add(mergedId);
            }
            // else: edge was unpaired or paired-but-skipped → drop it
          } else {
            newEdgeIds.push(eid);
          }
        }
        return { ...n, edgeIds: newEdgeIds };
      });

    skeleton.setData(cleanedNodes, survivingEdges, null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.clearStaleHoverAfterDelete(nodeId, null);
    this.clearStaleSelection(nodeId, null);
  }

  /**
   * Find non-overlapping pairs of incident edges that are nearly collinear
   * through `nodeId` (their outgoing directions point opposite — dot < -0.985,
   * roughly 10° tolerance). Greedy by best dot product so each edge ends up in
   * at most one pair, with the straightest pairs taking priority.
   */
  private findCollinearPairs(skeleton: PointoramaWallSketch, nodeId: string): Array<[SkeletonEdgeData, SkeletonEdgeData]> {
    const node = skeleton.getNode(nodeId);
    if (!node) return [];
    const incident = skeleton.getAllEdges().filter((e) => e.startNodeId === nodeId || e.endNodeId === nodeId);
    if (incident.length < 2) return [];

    interface Outgoing {
      edge: SkeletonEdgeData;
      dx: number;
      dy: number;
    }
    const outgoings: Outgoing[] = [];
    for (const e of incident) {
      // Curved edges never join a collinear pair — merging an arc with
      // anything by spreading one edge's fields would produce a wrong bulge.
      // (Follow-up: merge two arcs sharing the same circle.)
      if (edgeIsCurved(e)) continue;
      const farId = e.startNodeId === nodeId ? e.endNodeId : e.startNodeId;
      const far = skeleton.getNode(farId);
      if (!far) continue;
      const dx = far.position.x - node.position.x;
      const dy = far.position.y - node.position.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-6) continue;
      outgoings.push({ edge: e, dx: dx / len, dy: dy / len });
    }

    // Score every (i, j) pair, sort by best (most negative) dot product, then
    // greedily take pairs whose edges haven't been used yet.
    interface Candidate {
      a: Outgoing;
      b: Outgoing;
      dot: number;
    }
    const candidates: Candidate[] = [];
    for (let i = 0; i < outgoings.length; i++) {
      for (let j = i + 1; j < outgoings.length; j++) {
        const a = outgoings[i];
        const b = outgoings[j];
        const dot = a.dx * b.dx + a.dy * b.dy;
        if (dot < -0.985) candidates.push({ a, b, dot });
      }
    }
    candidates.sort((p, q) => p.dot - q.dot); // most negative first

    const used = new Set<string>();
    const pairs: Array<[SkeletonEdgeData, SkeletonEdgeData]> = [];
    for (const c of candidates) {
      if (used.has(c.a.edge.id) || used.has(c.b.edge.id)) continue;
      used.add(c.a.edge.id);
      used.add(c.b.edge.id);
      pairs.push([c.a.edge, c.b.edge]);
    }
    return pairs;
  }

  /**
   * Remove a node only if it no longer contributes to the structure:
   *   - degree 0 (orphan, no incident edges) → drop it
   *   - degree 2 with both incident edges collinear → dissolve into a single
   *     pass-through edge (delegates to `deleteNode`, which already handles
   *     the collinear-merge case)
   * Any other case (leaf, corner, junction) is preserved — those nodes
   * carry information the rest of the structure depends on. Returns true if
   * the node was removed. Call sites are responsible for the undo snapshot.
   */
  private dissolveNode(nodeId: string): boolean {
    const skeleton = this.deps.getSceneWallSketch();
    const node = skeleton?.getNode(nodeId);
    if (!skeleton || !node) return false;

    const degree = this.degreeOf(skeleton, nodeId);
    if (degree === 0) {
      // Orphan: just strip the node. No edges to touch.
      const remainingNodes = skeleton.getNodes().filter((n) => n.id !== nodeId);
      skeleton.setData(remainingNodes, skeleton.getAllEdges(), null);
      this.deps.onWallSketchRestored?.();
      this.applyEditVisuals();
      this.clearStaleHoverAfterDelete(nodeId, null);
      this.clearStaleSelection(nodeId, null);
      return true;
    }
    if (degree === 2 && this.findCollinearPairs(skeleton, nodeId).length === 1) {
      this.deleteNode(nodeId, { snapshot: false });
      return true;
    }
    return false;
  }

  private deleteEdge(edgeId: string, opts: { snapshot?: boolean } = {}): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton || !skeleton.getEdge(edgeId)) return;

    if (opts.snapshot !== false) this.pushWallSketchUndoSnapshot();

    const edges = skeleton.getAllEdges().filter((e) => e.id !== edgeId);
    const cleanedNodes = skeleton.getNodes().map((n) => ({
      ...n,
      edgeIds: n.edgeIds.filter((eid) => eid !== edgeId),
    }));

    skeleton.setData(cleanedNodes, edges, null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.clearStaleHoverAfterDelete(null, edgeId);
    this.clearStaleSelection(null, edgeId);
  }

  /** How many edges are incident to a given node. */
  private degreeOf(skeleton: PointoramaWallSketch, nodeId: string): number {
    let n = 0;
    for (const edge of skeleton.getAllEdges()) {
      if (edge.startNodeId === nodeId || edge.endNodeId === nodeId) n++;
    }
    return n;
  }

  /**
   * Find a node coincident (within a tiny epsilon) with `nodeId`, excluding the
   * node itself and any ids in `exclude`. Used at drag-end to detect "dropped on
   * top of" merges robustly even when the snap re-resolve returned `free`
   * because the cursor wiggled past the node radius between the last frame and
   * release. Epsilon is loose vs float roundoff but far tighter than anything
   * visible, so it never welds two genuinely-separate walls.
   */
  private findCoincidentNode(skeleton: PointoramaWallSketch, nodeId: string, exclude?: Set<string>): string | null {
    const COINCIDENT_NODE_EPS_M = 1e-4; // 0.1mm
    const node = skeleton.getNode(nodeId);
    if (!node) return null;
    for (const n of skeleton.getNodes()) {
      if (n.id === nodeId || exclude?.has(n.id)) continue;
      if (Math.hypot(n.position.x - node.position.x, n.position.y - node.position.y) < COINCIDENT_NODE_EPS_M) {
        return n.id;
      }
    }
    return null;
  }

  /**
   * After a multi-node drag (group move-node or move-edge), weld every moving
   * node that came to rest on top of a STATIONARY node. Each moving node that
   * landed coincident with a non-moving node is merged into it. Iterates against
   * a live `moving` set so a node already consumed by an earlier merge (or one
   * moving node landing on another moving node) is skipped — only moving→
   * stationary welds happen, matching the single-node drop behaviour.
   */
  private mergeMovingNodesOntoCoincident(skeleton: PointoramaWallSketch, movingIds: Set<string>, opts: { snapshot?: boolean } = {}): void {
    // Snapshot the ids up front; mergeNodeInto mutates the skeleton each call.
    for (const movedId of [...movingIds]) {
      if (!skeleton.getNode(movedId)) continue; // already consumed by a prior merge
      // Exclude the still-moving set as targets: a moving node should weld onto
      // stationary geometry, not onto its travelling companions.
      const target = this.findCoincidentNode(skeleton, movedId, movingIds);
      if (target) {
        // Outline and wall geometry never weld into each other — a kind
        // mismatch leaves the nodes coincident but disconnected.
        const movedKind = this.connectKindOfNode(skeleton, movedId);
        if (movedKind !== null && !this.canConnectToNode(skeleton, target, movedKind)) continue;
        this.mergeNodeInto(skeleton, movedId, target, opts);
        movingIds.delete(movedId);
      }
    }
  }

  /**
   * After a perpendicular wall move, weld the moved edge's endpoints onto any
   * directly-connected NEIGHBOUR node they slid exactly onto. The perp move is
   * constraint-respecting — endpoints only travel along their neighbour lines —
   * so a coincidence can only ever be with a neighbour (never an arbitrary new
   * node), which is exactly the snap the user asked for. Welding collapses the
   * resulting zero-length neighbour edge into a clean junction.
   *
   * Re-reads the edge's endpoints fresh each pass because `applyMoveEdgePerp`
   * may have split a junction into new node ids, and `mergeNodeInto` mutates the
   * topology. The neighbour set is the endpoint's adjacent nodes via its
   * incident edges (excluding the moved edge itself).
   */
  private weldPerpEndpointsOntoNeighbours(skeleton: PointoramaWallSketch, edgeId: string, opts: { snapshot?: boolean } = {}): void {
    // Two endpoints to consider; resolve their ids per-pass since a merge can
    // rename/re-point them.
    for (let pass = 0; pass < 2; pass++) {
      const edge = skeleton.getEdge(edgeId);
      if (!edge) return;
      const endpointId = pass === 0 ? edge.startNodeId : edge.endNodeId;
      const endpoint = skeleton.getNode(endpointId);
      if (!endpoint) continue;
      // Neighbour nodes connected to this endpoint by a non-moved incident edge.
      const neighbourIds = new Set<string>();
      for (const eid of endpoint.edgeIds) {
        if (eid === edgeId) continue;
        const e = skeleton.getEdge(eid);
        if (!e) continue;
        neighbourIds.add(e.startNodeId === endpointId ? e.endNodeId : e.startNodeId);
      }
      const COINCIDENT_NODE_EPS_M = 1e-4; // 0.1mm — matches findCoincidentNode
      for (const nbId of neighbourIds) {
        const nb = skeleton.getNode(nbId);
        if (!nb) continue;
        if (Math.hypot(nb.position.x - endpoint.position.x, nb.position.y - endpoint.position.y) < COINCIDENT_NODE_EPS_M) {
          // Merge the moved endpoint INTO the stationary neighbour so the
          // neighbour (and the walls anchored to it) stay put.
          this.mergeNodeInto(skeleton, endpointId, nbId, opts);
          break;
        }
      }
    }
  }

  /**
   * After a perpendicular wall move, collapse the moved wall itself when the
   * slide shrank it to (near) nothing. Its endpoints slid toward each other
   * along their neighbour walls' lines, so the vanished wall's two neighbours
   * must now meet in ONE junction — and that junction belongs at the
   * INTERSECTION of the two neighbour centerlines. Merging at either
   * endpoint's current spot instead would kink the neighbour that doesn't
   * pass through that point, so the surviving node is repositioned onto the
   * crossing before the merge. Falls back to the tiny wall's midpoint when
   * the neighbour lines don't cross (parallel) or the crossing is implausibly
   * far away (near-parallel lines).
   *
   * `movedDir` is the moved wall's ORIGINAL unit direction (the wall itself
   * is degenerate by now) — used to skip near-parallel neighbours, mirroring
   * the slideable classification in `applyMoveEdgePerp`.
   */
  private collapseMovedEdgeIfTooSmall(skeleton: PointoramaWallSketch, edgeId: string, movedDir: THREE.Vector2, opts: { snapshot?: boolean } = {}): void {
    const edge = skeleton.getEdge(edgeId);
    if (!edge || edgeIsCurved(edge)) return;
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b || a.id === b.id) return;
    const len = Math.hypot(b.position.x - a.position.x, b.position.y - a.position.y);
    const mid = new THREE.Vector2((a.position.x + b.position.x) / 2, (a.position.y + b.position.y) / 2);
    // "Too small" = under the node snap radius, matching the zero-length snap
    // engage distance in applyMoveEdgePerp: releasing while the vanish
    // preview is engaged commits the vanish, releasing outside keeps the wall.
    const minLen = this.snapRadiiForModifier('default', new THREE.Vector3(mid.x, mid.y, skeleton.getFloorHeight())).node;
    if (len >= minLen) return;

    // One straight, slideable neighbour line per endpoint — the line the
    // endpoint travelled along during the slide (any such incident edge
    // passes through the endpoint, so the first qualifying one defines it).
    const lineAt = (endpoint: SkeletonNodeData): { point: THREE.Vector2; dir: THREE.Vector2 } | null => {
      for (const eid of endpoint.edgeIds) {
        if (eid === edgeId) continue;
        const e = skeleton.getEdge(eid);
        if (!e || edgeIsCurved(e)) continue;
        const otherId = e.startNodeId === endpoint.id ? e.endNodeId : e.startNodeId;
        const other = skeleton.getNode(otherId);
        if (!other) continue;
        const d = new THREE.Vector2(other.position.x - endpoint.position.x, other.position.y - endpoint.position.y);
        if (d.lengthSq() < 1e-9) continue;
        d.normalize();
        if (Math.abs(d.dot(movedDir)) >= PERP_MOVE_PARALLEL_COS) continue;
        return { point: new THREE.Vector2(endpoint.position.x, endpoint.position.y), dir: d };
      }
      return null;
    };

    const lineA = lineAt(a);
    const lineB = lineAt(b);
    let junction = mid;
    if (lineA && lineB) {
      const cross = infiniteLineIntersection(lineA.point, lineA.dir, lineB.point, lineB.dir);
      if (cross && Math.hypot(cross.x - mid.x, cross.y - mid.y) <= Math.max(4 * minLen, len)) {
        junction = new THREE.Vector2(cross.x, cross.y);
      }
    }

    // Reposition the surviving endpoint onto the junction, then merge the
    // other endpoint into it — the moved edge becomes a self-loop that
    // mergeNodeInto drops, and both endpoints' walls + room memberships land
    // on the single junction node.
    const nodes = skeleton.getNodes().map((n) => (n.id === a.id ? { ...n, position: { x: junction.x, y: junction.y, z: a.position.z } } : n));
    skeleton.setData(nodes, skeleton.getAllEdges(), null);
    this.mergeNodeInto(skeleton, b.id, a.id, opts);
  }

  /**
   * Merge `sourceId` into `targetId`: re-point every edge that touched source
   * so it touches target instead, drop self-loops that result, and dedupe edges
   * that would now connect target to the same neighbour twice. The source node
   * is then removed.
   *
   * Used when the user drags a node and drops it on top of another node — the
   * two become one.
   *
   * `opts.snapshot === false` skips the undo snapshot — used mid-drag, where the
   * drag already pushed ONE snapshot of the pre-drag state on first move, so the
   * whole move+merge collapses into a single undo step (undo reverts the entire
   * gesture, not the merge then the move separately).
   */
  private mergeNodeInto(skeleton: PointoramaWallSketch, sourceId: string, targetId: string, opts: { snapshot?: boolean } = {}): void {
    if (sourceId === targetId) return;
    const source = skeleton.getNode(sourceId);
    const target = skeleton.getNode(targetId);
    if (!source || !target) return;

    if (opts.snapshot !== false) this.pushWallSketchUndoSnapshot();

    // Re-point edges
    let edges: SkeletonEdgeData[] = skeleton.getAllEdges().map((e) => {
      const start = e.startNodeId === sourceId ? targetId : e.startNodeId;
      const end = e.endNodeId === sourceId ? targetId : e.endNodeId;
      return start === e.startNodeId && end === e.endNodeId ? e : { ...e, startNodeId: start, endNodeId: end };
    });

    // Drop self-loops
    edges = edges.filter((e) => e.startNodeId !== e.endNodeId);

    // Dedupe — between any (a,b) pair (treating undirected), the first
    // edge wins on topology + sizes, but roomIds + metadata are unioned
    // from every duplicate so a duplicate that bordered a room the
    // survivor didn't still carries that membership forward.
    const dedupKey = (e: SkeletonEdgeData): string => {
      const lo = e.startNodeId < e.endNodeId ? e.startNodeId : e.endNodeId;
      const hi = e.startNodeId < e.endNodeId ? e.endNodeId : e.startNodeId;
      return `${lo}|${hi}`;
    };
    const byKey = new Map<string, SkeletonEdgeData>();
    for (const e of edges) {
      const key = dedupKey(e);
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, e);
        continue;
      }
      const merged = mergeEdgeMetadata(existing, e);
      byKey.set(key, { ...existing, roomIds: merged.roomIds, metadata: merged.metadata });
    }
    edges = Array.from(byKey.values());

    // Rebuild each node's edgeIds from the surviving edges (cheaper + safer
    // than incremental patching given the re-point + dedupe above).
    const incident: Map<string, string[]> = new Map();
    const pushIncident = (nodeId: string, edgeId: string): void => {
      let list = incident.get(nodeId);
      if (!list) {
        list = [];
        incident.set(nodeId, list);
      }
      list.push(edgeId);
    };
    for (const e of edges) {
      pushIncident(e.startNodeId, e.id);
      pushIncident(e.endNodeId, e.id);
    }
    // Union the source node's roomIds + metadata into the target. Other
    // nodes pass through unchanged except for the recomputed edgeIds.
    const { roomIds: targetRoomIds, metadata: targetMetadata } = mergeNodeMetadata(target, source);
    const nodes = skeleton
      .getNodes()
      .filter((n) => n.id !== sourceId)
      .map((n) => {
        const edgeIds = incident.get(n.id) ?? [];
        if (n.id === targetId) {
          return { ...n, edgeIds, roomIds: targetRoomIds, metadata: targetMetadata };
        }
        return { ...n, edgeIds };
      });

    skeleton.setData(nodes, edges, null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.clearStaleHoverAfterDelete(sourceId, null);
    this.clearStaleSelection(sourceId, null);
    // Select the surviving target so the user can see what their drop produced.
    this.setSelection(targetId, null);
  }

  /**
   * Dissolve a node with exactly two incident edges *that lie on a near-straight line through it*.
   * Replaces `node + edgeA + edgeB` with a single direct edge between the two neighbours.
   *
   * No-op if:
   *  - degree ≠ 2
   *  - the two neighbours are the same node (self-loop pair)
   *  - the two neighbours are already directly connected (would duplicate)
   *  - the two edges form a real corner (angle deflection >~10° from straight) —
   *    those are intentional geometry, not redundant pass-through joints.
   */
  private dissolveDegree2Node(skeleton: PointoramaWallSketch, nodeId: string, opts: { snapshot?: boolean } = {}): void {
    const node = skeleton.getNode(nodeId);
    if (!node) return;
    const incident = skeleton.getAllEdges().filter((e) => e.startNodeId === nodeId || e.endNodeId === nodeId);
    if (incident.length !== 2) return;
    const [eA, eB] = incident;
    // Never dissolve through a curved edge — the merged edge would inherit a
    // wrong bulge. (Follow-up: merge two arcs sharing the same circle.)
    if (edgeIsCurved(eA) || edgeIsCurved(eB)) return;
    const neighAId = eA.startNodeId === nodeId ? eA.endNodeId : eA.startNodeId;
    const neighBId = eB.startNodeId === nodeId ? eB.endNodeId : eB.startNodeId;
    if (neighAId === neighBId) return;

    const neighA = skeleton.getNode(neighAId);
    const neighB = skeleton.getNode(neighBId);
    if (!neighA || !neighB) return;

    // Outgoing directions from `node` toward each neighbour. If the edges are
    // collinear through `node` these vectors point in opposite directions, so
    // dot ≈ -1. If the edges form a corner (90° → dot ≈ 0, 45° → dot ≈ 0.7) we
    // bail. -0.985 corresponds to ~10° deviation from a perfectly straight line.
    const ax = neighA.position.x - node.position.x;
    const ay = neighA.position.y - node.position.y;
    const bx = neighB.position.x - node.position.x;
    const by = neighB.position.y - node.position.y;
    const aLen = Math.hypot(ax, ay);
    const bLen = Math.hypot(bx, by);
    if (aLen < 1e-6 || bLen < 1e-6) return;
    const dot = (ax * bx + ay * by) / (aLen * bLen);
    if (dot > -0.985) return; // not collinear — preserve the corner

    // Equal-thickness gate (mirrors removeCollinearNodesPass): only dissolve when
    // the two collinear walls have ~equal thickness. A change in wall thickness
    // across the node is a real feature — keep it as a vertex rather than smearing
    // two distinct walls into one. Check PER SIDE and try BOTH orientations, since
    // the two edges' left/right may be stored swapped relative to each other.
    const THICKNESS_TOL = 0.03; // ±3% relative
    const close = (p: number, q: number): boolean => {
      const denom = Math.max(p, q);
      return denom <= 1e-9 || Math.abs(p - q) / denom <= THICKNESS_TOL;
    };
    const aL = eA.leftSize ?? 0.1;
    const aR = eA.rightSize ?? 0.1;
    const bL = eB.leftSize ?? 0.1;
    const bR = eB.rightSize ?? 0.1;
    const direct = close(aL, bL) && close(aR, bR);
    const inverse = close(aL, bR) && close(aR, bL);
    if (!direct && !inverse) return; // thickness step — preserve the node

    // Already-connected check: skip if neighA-neighB edge exists (apart from
    // the two we're about to remove).
    const alreadyConnected = skeleton.getAllEdges().some((e) => e.id !== eA.id && e.id !== eB.id && ((e.startNodeId === neighAId && e.endNodeId === neighBId) || (e.startNodeId === neighBId && e.endNodeId === neighAId)));
    if (alreadyConnected) return;

    if (opts.snapshot !== false) this.pushWallSketchUndoSnapshot();

    const newEdgeId = this.generateId();
    // Sizes / wallType inherit from edge A (arbitrary but matches the
    // user's mental model that the "main run" continues through the
    // dissolved node). roomIds + metadata, however, are *unioned* — the
    // fused edge keeps touching every room that either half touched.
    const { roomIds, metadata } = mergeEdgeMetadata(eA, eB);
    const merged: SkeletonEdgeData = {
      ...eA,
      id: newEdgeId,
      name: this.generateName('edge'),
      startNodeId: neighAId,
      endNodeId: neighBId,
      roomIds,
      metadata,
    };
    let edges = skeleton.getAllEdges().filter((e) => e.id !== eA.id && e.id !== eB.id);
    edges = [...edges, merged];

    const nodes = skeleton
      .getNodes()
      .filter((n) => n.id !== nodeId)
      .map((n) => {
        if (n.id === neighAId || n.id === neighBId) {
          return {
            ...n,
            edgeIds: n.edgeIds.filter((eid) => eid !== eA.id && eid !== eB.id).concat(newEdgeId),
          };
        }
        return n;
      });

    skeleton.setData(nodes, edges, null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.clearStaleHoverAfterDelete(nodeId, null);
    this.clearStaleSelection(nodeId, null);
  }

  /**
   * Split `edgeId` at `pointRel` (session-relative coords), returning the new
   * junction node's id. Inserts two half-edges in place of the original and
   * patches the endpoints' edgeIds. No-op (returns null) if the edge or its
   * endpoints have already disappeared. Pushes its own undo snapshot.
   */
  /**
   * Whether `edgeId` is a REAL skeleton edge (splittable) rather than a
   * synthetic snap-only ring segment (room outline / wall shell / wall cavity).
   * The synthetic segments are pushed into the snap set with prefixed ids
   * (`room-edge:` / `wall-shell:` / `wall-cavity:`) and don't exist in the
   * skeleton, so `getEdge` returns undefined for them.
   */
  private isRealSkeletonEdge(skeleton: PointoramaWallSketch, edgeId: string): boolean {
    return skeleton.getEdge(edgeId) != null;
  }

  private splitEdgeAt(skeleton: PointoramaWallSketch, edgeId: string, pointRel: THREE.Vector2, opts: { snapshot?: boolean } = {}): string | null {
    const edge = skeleton.getEdge(edgeId);
    if (!edge) return null;
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return null;

    // Callers that already pushed a single undo snapshot for the whole gesture
    // (e.g. extrude commit) pass `snapshot: false` so the split doesn't add a
    // second undo entry — otherwise undoing a new line takes two presses.
    if (opts.snapshot !== false) this.pushWallSketchUndoSnapshot();

    // Curved edges: the node must sit ON the arc, and each half keeps the
    // original circle by splitting the included angle at the projection's
    // arc-length fraction. Straight edges keep the point as-is.
    const aRel = this.toRelative(a.position);
    const bRel = this.toRelative(b.position);
    let halfBulges: [number | undefined, number | undefined] = [edge.bulge, edge.bulge];
    if (edgeIsCurved(edge)) {
      const projected = closestPointOnEdge(pointRel, aRel, bRel, edge.bulge);
      const total = arcEdgeLength(aRel, bRel, edge.bulge);
      const t = total > 1e-9 ? projected.s / total : 0.5;
      halfBulges = splitBulge(edge.bulge ?? 0, t);
      pointRel = new THREE.Vector2(projected.point.x, projected.point.y);
    }

    const z = skeleton.getFloorHeight();
    const abs = this.toAbsolute(pointRel, z);
    const splitNodeId = this.generateId();
    const halfAId = this.generateId();
    const halfBId = this.generateId();
    const splitNode: SkeletonNodeData = {
      id: splitNodeId,
      name: this.generateName('node'),
      position: { x: abs.x, y: abs.y, z: abs.z },
      // Inherit room membership from the parent edge so downstream consumers
      // (e.g. the wall-skinning renderer) treat the split point as part of
      // the same wall slab.
      roomIds: [...edge.roomIds],
      edgeIds: [halfAId, halfBId],
      metadata: '',
    };
    const halfA: SkeletonEdgeData = { ...edge, id: halfAId, name: this.generateName('edge'), startNodeId: a.id, endNodeId: splitNodeId, bulge: halfBulges[0] };
    const halfB: SkeletonEdgeData = { ...edge, id: halfBId, name: this.generateName('edge'), startNodeId: splitNodeId, endNodeId: b.id, bulge: halfBulges[1] };
    const nodes = skeleton
      .getNodes()
      .map((n) => {
        if (n.id === a.id) return { ...n, edgeIds: n.edgeIds.filter((eid) => eid !== edgeId).concat(halfAId) };
        if (n.id === b.id) return { ...n, edgeIds: n.edgeIds.filter((eid) => eid !== edgeId).concat(halfBId) };
        return n;
      })
      .concat(splitNode);
    const newEdges = skeleton
      .getAllEdges()
      .filter((e) => e.id !== edgeId)
      .concat(halfA, halfB);
    skeleton.setData(nodes, newEdges, null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    return splitNodeId;
  }

  /**
   * After a new edge is created, split it (and every existing edge it crosses)
   * at each interior intersection, inserting a shared node so the new wall is
   * topologically connected wherever it cuts across another. Only proper
   * crossings count — shared endpoints and T-touches (an endpoint lying on the
   * other segment) are ignored, since those are already handled by node/edge
   * snapping at draw time.
   *
   * Rebuilds the skeleton once with the full set of splits. The caller is
   * responsible for the undo snapshot (this adds no snapshot of its own).
   * Returns true when at least one crossing was split (and the skeleton +
   * visuals were rebuilt), false when there was nothing to do.
   */
  private splitNewEdgeAtCrossingsIfAny(skeleton: PointoramaWallSketch, newEdgeId: string): boolean {
    const EPS = 1e-6;
    const nodes = new Map(skeleton.getNodes().map((n) => [n.id, { ...n, edgeIds: [...n.edgeIds] }]));
    const edges = new Map(skeleton.getAllEdges().map((e) => [e.id, { ...e }]));
    const newEdge = edges.get(newEdgeId);
    if (!newEdge) return false;
    const z = skeleton.getFloorHeight();

    const pos = (id: string): { x: number; y: number } | null => {
      const n = nodes.get(id);
      return n ? { x: n.position.x, y: n.position.y } : null;
    };
    const ns = pos(newEdge.startNodeId);
    const ne = pos(newEdge.endNodeId);
    if (!ns || !ne) return false;

    // Collect proper crossings of the new edge against every other current edge.
    // `t` is the parameter along the NEW edge (used to order chain splits);
    // `u` the parameter along the crossed edge (chord fraction for straight
    // edges, arc-length fraction for curved ones). A straight edge can cross
    // an arc twice, so curved edges may contribute two crossings.
    const crossings: { t: number; x: number; y: number; otherEdgeId: string; u: number; nodeId?: string }[] = [];
    const newLen = Math.hypot(ne.x - ns.x, ne.y - ns.y);
    const newIsOutline = newEdge.role === 'OUTLINE';
    for (const e of edges.values()) {
      if (e.id === newEdgeId) continue;
      // Outline and wall geometry never connect: a wall crossing the outline
      // (or an outline crossing a wall) passes over it without creating a
      // junction — only same-kind crossings split.
      if ((e.role === 'OUTLINE') !== newIsOutline) continue;
      // Skip edges sharing an endpoint with the new edge — not a crossing.
      if (e.startNodeId === newEdge.startNodeId || e.startNodeId === newEdge.endNodeId || e.endNodeId === newEdge.startNodeId || e.endNodeId === newEdge.endNodeId) continue;
      const es = pos(e.startNodeId);
      const ee = pos(e.endNodeId);
      if (!es || !ee) continue;
      if (edgeIsCurved(e)) {
        const total = arcEdgeLength(es, ee, e.bulge);
        if (total < 1e-9 || newLen < 1e-9) continue;
        for (const hit of segmentArcIntersections(ns, ne, es, ee, e.bulge)) {
          const t = Math.hypot(hit.point.x - ns.x, hit.point.y - ns.y) / newLen;
          const u = hit.s / total;
          // Interior crossings only — endpoint touches are handled by snapping.
          if (t <= EPS || t >= 1 - EPS || u <= EPS || u >= 1 - EPS) continue;
          crossings.push({ t, x: hit.point.x, y: hit.point.y, otherEdgeId: e.id, u });
        }
      } else {
        const hit = segmentSegmentIntersection(ns, ne, es, ee, EPS);
        if (!hit) continue;
        crossings.push({ t: hit.t, x: hit.point.x, y: hit.point.y, otherEdgeId: e.id, u: hit.u });
      }
    }
    if (crossings.length === 0) return false;

    // Order crossings along the new edge so the chain of split sub-edges runs
    // start → … → end.
    crossings.sort((a, b) => a.t - b.t);

    // Split each crossed edge at its intersection, creating one shared node per
    // crossing. Reuse that node when chaining the new edge through it.
    const removeEdgeFromNode = (nodeId: string, edgeId: string) => {
      const n = nodes.get(nodeId);
      if (n) n.edgeIds = n.edgeIds.filter((id) => id !== edgeId);
    };
    const addEdgeToNode = (nodeId: string, edgeId: string) => {
      const n = nodes.get(nodeId);
      if (n && !n.edgeIds.includes(edgeId)) n.edgeIds.push(edgeId);
    };

    const chainNodeIds: string[] = [];
    for (const c of crossings) {
      const crossNodeId = this.generateId();
      nodes.set(crossNodeId, {
        id: crossNodeId,
        name: this.generateName('node'),
        position: { x: c.x, y: c.y, z },
        roomIds: [],
        edgeIds: [],
        metadata: '',
      });
      chainNodeIds.push(crossNodeId);
      c.nodeId = crossNodeId;
    }

    // Split each crossed edge at ALL its crossing nodes in one pass — an arc
    // can be crossed twice by one straight edge. Sub-edge bulges partition the
    // original included angle by the crossings' arc-length fractions, so every
    // piece stays on the original circle.
    const byOtherEdge = new Map<string, typeof crossings>();
    for (const c of crossings) {
      const list = byOtherEdge.get(c.otherEdgeId) ?? [];
      list.push(c);
      byOtherEdge.set(c.otherEdgeId, list);
    }
    for (const [otherEdgeId, edgeCrossings] of byOtherEdge) {
      const other = edges.get(otherEdgeId);
      if (!other) continue;
      edgeCrossings.sort((a, b) => a.u - b.u);
      const stops: { nodeId: string; u: number }[] = [{ nodeId: other.startNodeId, u: 0 }, ...edgeCrossings.map((c) => ({ nodeId: c.nodeId as string, u: c.u })), { nodeId: other.endNodeId, u: 1 }];
      const quarterAngle = Math.atan(other.bulge ?? 0); // θ/4, signed; 0 for straight
      edges.delete(otherEdgeId);
      removeEdgeFromNode(other.startNodeId, otherEdgeId);
      removeEdgeFromNode(other.endNodeId, otherEdgeId);
      for (let i = 0; i < stops.length - 1; i++) {
        const subBulge = edgeIsCurved(other) ? Math.tan((stops[i + 1].u - stops[i].u) * quarterAngle) : other.bulge;
        const sub: SkeletonEdgeData = { ...other, id: this.generateId(), name: this.generateName('edge'), startNodeId: stops[i].nodeId, endNodeId: stops[i + 1].nodeId, bulge: subBulge };
        edges.set(sub.id, sub);
        addEdgeToNode(stops[i].nodeId, sub.id);
        addEdgeToNode(stops[i + 1].nodeId, sub.id);
      }
    }

    // Replace the new edge with a chain: start → cross₀ → … → crossₙ → end.
    const startId = newEdge.startNodeId;
    const endId = newEdge.endNodeId;
    edges.delete(newEdgeId);
    removeEdgeFromNode(startId, newEdgeId);
    removeEdgeFromNode(endId, newEdgeId);
    const chain = [startId, ...chainNodeIds, endId];
    for (let i = 0; i < chain.length - 1; i++) {
      const seg: SkeletonEdgeData = { ...newEdge, id: this.generateId(), name: this.generateName('edge'), startNodeId: chain[i], endNodeId: chain[i + 1] };
      edges.set(seg.id, seg);
      addEdgeToNode(chain[i], seg.id);
      addEdgeToNode(chain[i + 1], seg.id);
    }

    skeleton.setData([...nodes.values()], [...edges.values()], null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    return true;
  }

  // ─── Coordinate-space helpers (relative ↔ absolute) ─────────────────

  /** World point (x,y on floor) → session-relative 2D. */
  private toRelative(world: { x: number; y: number }): THREE.Vector2 {
    if (!this.referencePoint) return new THREE.Vector2(world.x, world.y);
    return new THREE.Vector2(world.x - this.referencePoint.x, world.y - this.referencePoint.y);
  }

  /** Session-relative 2D → absolute world point on the floor plane. */
  private toAbsolute(relative: THREE.Vector2, z: number): { x: number; y: number; z: number } {
    if (!this.referencePoint) return { x: relative.x, y: relative.y, z };
    return { x: relative.x + this.referencePoint.x, y: relative.y + this.referencePoint.y, z };
  }

  /** Build relative-coord SnapNode list — the engine snaps to existing nodes (tight radius). */
  private buildRelativeSnapNodes(skeleton: PointoramaWallSketch): SnapNode[] {
    const refX = this.referencePoint?.x ?? 0;
    const refY = this.referencePoint?.y ?? 0;
    return skeleton.getNodes().map((n) => ({
      id: n.id,
      position: { x: n.position.x - refX, y: n.position.y - refY },
    }));
  }

  /**
   * Relative-coord SnapEdge list — used for the engine's edge-cross snap kind
   * (where a construction line crosses an existing edge). Skips dangling edges
   * (missing endpoints) so the engine never sees inconsistent geometry.
   */
  private buildRelativeSnapEdges(skeleton: PointoramaWallSketch): SnapEdge[] {
    const refX = this.referencePoint?.x ?? 0;
    const refY = this.referencePoint?.y ?? 0;
    const out: SnapEdge[] = [];
    for (const edge of skeleton.getAllEdges()) {
      const a = skeleton.getNode(edge.startNodeId);
      const b = skeleton.getNode(edge.endNodeId);
      if (!a || !b) continue;
      out.push({
        id: edge.id,
        start: { x: a.position.x - refX, y: a.position.y - refY },
        end: { x: b.position.x - refX, y: b.position.y - refY },
        // Bulge is translation-invariant, so it carries into relative coords.
        bulge: edge.bulge,
      });
    }
    return out;
  }

  /**
   * Extra snap targets that only the manual width tool wants: every room
   * polygon edge plus every wall-region ring segment (outer walls + cavities).
   * These let the user snap their width measurement to actual wall surfaces,
   * not just the skeleton centerline geometry.
   *
   * Ids are prefixed (`room-edge:` / `wall-ring:`) so they can't collide
   * with persisted skeleton edge ids — the engine produces an `edge-cross`
   * snap with these ids, but the set-width flow only reads `snap.point`
   * and ignores the id, so the prefix is purely diagnostic.
   */
  private buildSnapEdgesFromRoomsAndWallRegions(skeleton: PointoramaWallSketch): SnapEdge[] {
    const refX = this.referencePoint?.x ?? 0;
    const refY = this.referencePoint?.y ?? 0;
    const out: SnapEdge[] = [];

    // Room polygons. We only add rooms on the current floor — getCurrentFloorWallSketch
    // already implicitly scopes to this floor, but the rooms list is global,
    // so filter by floor height proximity (matches FloorplanToolBase logic).
    const floorH = skeleton.getFloorHeight();
    for (const room of this.deps.viewer.scene.rooms) {
      const polygon = room.getPolygon();
      if (polygon.length < 2) continue;
      // Cheap floor check: every room polygon vertex sits at the room's
      // floor height; skip rooms outside this floor (~0.5m tolerance, same
      // value the tool uses for "rooms on this floor").
      if (Math.abs(polygon[0].z - floorH) > 0.5) continue;
      for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        out.push({
          id: `room-edge:${room.identifier}:${i}`,
          start: { x: a.x - refX, y: a.y - refY },
          end: { x: b.x - refX, y: b.y - refY },
        });
      }
    }

    // Wall-region rings — outer rings (the building shell) + holes (cavities).
    const regions = skeleton.getWallRegions();
    for (let r = 0; r < regions.length; r++) {
      const region = regions[r];
      pushRingAsSegments(out, region.outerRing, `wall-shell:${r}`, refX, refY);
      for (let h = 0; h < region.holes.length; h++) {
        pushRingAsSegments(out, region.holes[h], `wall-cavity:${r}.${h}`, refX, refY);
      }
    }

    return out;
  }

  /**
   * Build construction lines based on the current drag context.
   * All math in session-relative space.
   *
   * The strategy depends on what's being moved/created:
   *  - extrude-from-node N → lines through N parallel + perpendicular to each
   *    of N's adjacent edges. (Anchor = N, reference = N's neighbours.)
   *  - extrude-from-edge   → lines through the split point parallel +
   *    perpendicular to the source edge AND to the edges incident at its endpoints.
   *  - move-node N         → lines through each *neighbour* M of N, parallel +
   *    perpendicular to M's *other* edges. (Encourages "move N so the angle at
   *    M becomes regular.") If N has no neighbours, falls back to world axes
   *    through the drag origin.
   *  - move-edge E         → union of move-node lines for both endpoints.
   *
   * If no construction lines can be inferred (e.g. orphan node), falls back
   * to world axes through the drag origin so the user still gets X/Y snaps.
   */
  private buildConstructionLines(skeleton: PointoramaWallSketch): ConstructionLine[] {
    if (!this.drag) return [];
    const drag = this.drag;
    const lines: ConstructionLine[] = [];
    const refX = this.referencePoint?.x ?? 0;
    const refY = this.referencePoint?.y ?? 0;

    /**
     * Append parallel + perpendicular lines through `origin` for the given
     * source direction. `source` identifies which existing geometry the
     * inference draws from so the visual layer can highlight it / draw a
     * connector. Pass an axis-style source ({} or omitted) for fallback
     * world-axis lines that don't tie to any specific element.
     */
    const addParPerp = (origin: { x: number; y: number }, dir: { x: number; y: number }, source: { sourceNodeId?: string; sourceEdgeId?: string; sourceMidpoint?: { x: number; y: number }; axis?: boolean } = {}): void => {
      const len = Math.hypot(dir.x, dir.y);
      if (len < 1e-6) return;
      const ux = dir.x / len;
      const uy = dir.y / len;
      const meta = source.axis ? { kind: 'axis' as const } : { sourceNodeId: source.sourceNodeId, sourceEdgeId: source.sourceEdgeId, sourceMidpoint: source.sourceMidpoint };
      lines.push({ origin, direction: { x: ux, y: uy }, kind: source.axis ? 'axis' : 'parallel', ...meta });
      lines.push({ origin, direction: { x: -uy, y: ux }, kind: source.axis ? 'axis' : 'perpendicular', ...meta });
    };

    /**
     * Edges adjacent to a node, with the unit direction pointing AWAY from
     * the node and the edge's midpoint (in relative coords). Midpoint is
     * used as the construction-line origin so each line is anchored at the
     * geometric center of its source — making per-line origins distinct
     * even when the lines themselves overlap geometrically, and giving the
     * visualization a sensible "anchor" point to draw connectors to.
     */
    const adjacentDirections = (nodeId: string): { neighbourId: string; direction: { x: number; y: number }; edgeId: string; midpoint: { x: number; y: number } }[] => {
      const out: { neighbourId: string; direction: { x: number; y: number }; edgeId: string; midpoint: { x: number; y: number } }[] = [];
      for (const edge of skeleton.getAllEdges()) {
        let from: typeof edge.startNodeId | null = null;
        let to: typeof edge.endNodeId | null = null;
        if (edge.startNodeId === nodeId) {
          from = edge.startNodeId;
          to = edge.endNodeId;
        } else if (edge.endNodeId === nodeId) {
          from = edge.endNodeId;
          to = edge.startNodeId;
        }
        if (!from || !to) continue;
        const fromNode = skeleton.getNode(from);
        const toNode = skeleton.getNode(to);
        if (!fromNode || !toNode) continue;
        // Curved edges contribute their endpoint TANGENT (the direction the
        // wall actually leaves the node) instead of the chord, and anchor at
        // the arc apex. Straight edges are unchanged.
        let direction = { x: toNode.position.x - fromNode.position.x, y: toNode.position.y - fromNode.position.y };
        let midpoint = {
          x: (fromNode.position.x + toNode.position.x) / 2 - refX,
          y: (fromNode.position.y + toNode.position.y) / 2 - refY,
        };
        if (edgeIsCurved(edge)) {
          const startNode = skeleton.getNode(edge.startNodeId);
          const endNode = skeleton.getNode(edge.endNodeId);
          if (startNode && endNode) {
            const sXY = { x: startNode.position.x, y: startNode.position.y };
            const eXY = { x: endNode.position.x, y: endNode.position.y };
            const total = arcEdgeLength(sXY, eXY, edge.bulge);
            const atStart = from === edge.startNodeId;
            const { tangent } = pointAtArcLength(sXY, eXY, edge.bulge, atStart ? 0 : total);
            direction = atStart ? tangent : { x: -tangent.x, y: -tangent.y };
            const apex = arcMidpoint(sXY, eXY, edge.bulge);
            midpoint = { x: apex.x - refX, y: apex.y - refY };
          }
        }
        out.push({
          neighbourId: to,
          edgeId: edge.id,
          direction,
          midpoint,
        });
      }
      return out;
    };

    if (this.drag.kind === 'extrude-from-node' && this.drag.nodeId) {
      const node = skeleton.getNode(this.drag.nodeId);
      if (!node) return lines;
      const adjs = adjacentDirections(this.drag.nodeId);
      // Origin = the dragged node's position (where the new wall starts).
      // sourceMidpoint = each source edge's midpoint, so the bucket rep
      // tie-break can disambiguate between coincident lines anchored at
      // the same origin.
      const origin = { x: node.position.x - refX, y: node.position.y - refY };
      for (const a of adjs) {
        addParPerp(origin, a.direction, { sourceEdgeId: a.edgeId, sourceMidpoint: a.midpoint });
      }
      if (adjs.length === 0) {
        // Orphan — fall back to world axes through the source node.
        addParPerp(origin, { x: 1, y: 0 }, { axis: true });
      }
      return lines;
    }

    if (this.drag.kind === 'extrude-from-edge' && this.drag.edgeId && this.drag.splitPoint) {
      const edge = skeleton.getEdge(this.drag.edgeId);
      if (!edge) return lines;
      const a = skeleton.getNode(edge.startNodeId);
      const b = skeleton.getNode(edge.endNodeId);
      if (!a || !b) return lines;
      // Origin = the split point on the source edge (where the new wall
      // starts). sourceMidpoint = each source edge's midpoint for bucket
      // rep tie-breaking — without it, every line would share `origin =
      // splitPoint` and the rep tie-break would degenerate to source-id
      // sort.
      const origin = { x: this.drag.splitPoint.x, y: this.drag.splitPoint.y };
      const sourceMid = { x: (a.position.x + b.position.x) / 2 - refX, y: (a.position.y + b.position.y) / 2 - refY };
      // Curved source edge: par/perp to the LOCAL tangent at the split point
      // (extruding "perpendicular off a curved wall" means radially).
      let sourceDir = { x: b.position.x - a.position.x, y: b.position.y - a.position.y };
      if (edgeIsCurved(edge)) {
        const aRel = this.toRelative(a.position);
        const bRel = this.toRelative(b.position);
        const foot = closestPointOnEdge({ x: origin.x, y: origin.y }, aRel, bRel, edge.bulge);
        sourceDir = pointAtArcLength(aRel, bRel, edge.bulge, foot.s).tangent;
      }
      addParPerp(origin, sourceDir, { sourceEdgeId: edge.id, sourceMidpoint: sourceMid });
      for (const adj of adjacentDirections(a.id)) {
        if (adj.edgeId === edge.id) continue;
        addParPerp(origin, adj.direction, { sourceEdgeId: adj.edgeId, sourceMidpoint: adj.midpoint });
      }
      for (const adj of adjacentDirections(b.id)) {
        if (adj.edgeId === edge.id) continue;
        addParPerp(origin, adj.direction, { sourceEdgeId: adj.edgeId, sourceMidpoint: adj.midpoint });
      }
      return lines;
    }

    if (this.drag.kind === 'move-node' && this.drag.nodeId) {
      const adjs = adjacentDirections(this.drag.nodeId);
      // 1) Local lines: par/perp along each of the dragged node's own
      //    incident edges, anchored at the source edge's midpoint. Each
      //    line geometrically passes through the dragged node (the node is
      //    one endpoint of the source edge), so they all serve as "stay on
      //    this wall" / "leave perpendicular" snaps relative to the
      //    inception point.
      for (const a of adjs) {
        addParPerp(a.midpoint, a.direction, { sourceEdgeId: a.edgeId, sourceMidpoint: a.midpoint });
      }
      // 2) Neighbour inferences: for each neighbour M, par/perp through M
      //    based on M's other edges. Anchored at M's other edges' midpoints.
      for (const a of adjs) {
        const MAdjs = adjacentDirections(a.neighbourId).filter((x) => x.neighbourId !== drag.nodeId);
        for (const ma of MAdjs) {
          addParPerp(ma.midpoint, ma.direction, { sourceNodeId: a.neighbourId, sourceEdgeId: ma.edgeId, sourceMidpoint: ma.midpoint });
        }
      }
      if (lines.length === 0) {
        // No geometry to infer from — world axes through drag origin.
        addParPerp({ x: this.drag.dragOrigin.x, y: this.drag.dragOrigin.y }, { x: 1, y: 0 }, { axis: true });
      }
      return lines;
    }

    if (this.drag.kind === 'move-edge' && this.drag.edgeId) {
      const edge = skeleton.getEdge(this.drag.edgeId);
      if (!edge) return lines;
      // Treat each endpoint like a moving node: par/perp through neighbours' other edges.
      const endpointIds = [edge.startNodeId, edge.endNodeId];
      for (const endpointId of endpointIds) {
        const adjs = adjacentDirections(endpointId).filter((a) => a.edgeId !== drag.edgeId);
        for (const a of adjs) {
          const MAdjs = adjacentDirections(a.neighbourId).filter((x) => x.neighbourId !== endpointId);
          for (const ma of MAdjs) {
            addParPerp(ma.midpoint, ma.direction, { sourceNodeId: a.neighbourId, sourceEdgeId: ma.edgeId, sourceMidpoint: ma.midpoint });
          }
        }
      }
      if (lines.length === 0) {
        addParPerp({ x: this.drag.dragOrigin.x, y: this.drag.dragOrigin.y }, { x: 1, y: 0 }, { axis: true });
      }
      return lines;
    }

    if (this.drag.kind === 'draw-wall') {
      // Start anchored on existing geometry (the snapped press point) →
      // par/perp inferred from it, mirroring the extrude gestures. A free
      // start still gets world axes through the press point.
      const origin = { x: this.drag.dragOrigin.x, y: this.drag.dragOrigin.y };
      const startSnap = this.drag.startSnap;
      if (startSnap?.kind === 'node' && startSnap.nodeId) {
        for (const a of adjacentDirections(startSnap.nodeId)) {
          addParPerp(origin, a.direction, { sourceEdgeId: a.edgeId, sourceMidpoint: a.midpoint });
        }
      } else if ((startSnap?.kind === 'edge-cross' || startSnap?.kind === 'on-edge') && startSnap.edgeId) {
        const edge = skeleton.getEdge(startSnap.edgeId);
        const a = edge ? skeleton.getNode(edge.startNodeId) : null;
        const b = edge ? skeleton.getNode(edge.endNodeId) : null;
        if (edge && a && b) {
          const sourceMid = { x: (a.position.x + b.position.x) / 2 - refX, y: (a.position.y + b.position.y) / 2 - refY };
          addParPerp(origin, { x: b.position.x - a.position.x, y: b.position.y - a.position.y }, { sourceEdgeId: edge.id, sourceMidpoint: sourceMid });
        }
      }
      if (lines.length === 0) {
        addParPerp(origin, { x: 1, y: 0 }, { axis: true });
      }
      return lines;
    }

    return lines;
  }

  /**
   * Begin a move drag over the current selection, anchored at `anchorNodeId`
   * (the node/endpoint the user grabbed). Every selected node travels by the
   * cursor delta; the grabbed node is the nominal anchor used for the snap's
   * reference origin and the construction-line context. Used by both the
   * selected-node drag and the selected-edge drag so the two share one path.
   */
  private startMoveSelection(skeleton: PointoramaWallSketch, anchorNodeId: string, floorRel: THREE.Vector2): void {
    const anchor = skeleton.getNode(anchorNodeId);
    if (!anchor) return;
    const dragOrigin = this.toRelative(anchor.position);

    // The moving set is the whole selected node set (an edge selection auto-
    // includes its endpoints), falling back to just the anchor if somehow empty.
    const sel = this.selection;
    const selectedNodeIds = sel ? Array.from(sel.getSelectedNodeIds()) : [];
    const groupNodeIds = selectedNodeIds.length > 0 ? selectedNodeIds : [anchorNodeId];
    const originalPositions = new Map<string, { x: number; y: number }>();
    for (const id of groupNodeIds) {
      const n = skeleton.getNode(id);
      if (!n) continue;
      const rel = this.toRelative(n.position);
      originalPositions.set(id, { x: rel.x, y: rel.y });
    }

    this.drag = {
      kind: 'move-node',
      nodeId: anchorNodeId,
      dragOrigin,
      cursorOffset: floorRel.clone().sub(dragOrigin),
      originalPositions,
      snapshotted: false,
      lastSnap: null,
    };
    const startAbs = this.toAbsolute(dragOrigin, skeleton.getFloorHeight());
    this.buildAndCacheDragConstructionLines(new THREE.Vector3(startAbs.x, startAbs.y, startAbs.z));
    this.deps.viewer.inputHandler?.blockOrbitControls?.();
  }

  // ─── Drag handlers (forwarded from the tool) ────────────────────────

  onDragStart(data: DragEventData): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return;

    // Read from the click-target snapshot, not the live hover. The live hover
    // state has been overwritten by every mousemove between mousedown and now
    // (BaseTool requires 5+px of movement before firing onDragStart) and may
    // already say "no hover" because the cursor drifted off the node sphere.
    // The snapshot was taken at the actual click pixel in onMouseDown.
    const targetNodeId = this.clickTargetNodeId;
    const targetEdgeId = this.clickTargetEdgeId;
    const targetEdgePoint = this.clickTargetEdgePoint;

    const isLeft = data.button === 0;
    const isRight = data.button === 2;

    // Edit-mode entry rules:
    //  - Right / middle button → never enter edit mode (those are camera /
    //    when-editing-only extrude, not edit triggers).
    //  - Left button on a node or edge → enter edit mode and proceed with
    //    the move/select branch below.
    //  - Left button in empty space → enter edit mode AND start a box-select
    //    drag. Box-select needs edit mode (selection is an editing concept),
    //    and starting from an empty area is the natural way to multi-select.
    if (!this.editing) {
      if (!isLeft) return;
      // No sketch is being edited yet: a left-drag from EMPTY floor DRAWS a
      // wall (press = start, release = end) — the "just start drawing" entry.
      // It enters edit mode itself (works on a brand-new empty sketch), so
      // it must run before the generic enterEditMode below, whose reference
      // pinning needs at least one node. Drags that start ON a node/edge
      // keep their usual move/extrude meaning via the branches below.
      if (!targetNodeId && !targetEdgeId && !this.clickTargetHandle && !this.clickTargetBowEdgeId && !this.clickTargetMoveEdgeId) {
        if (this.caps.allowCreate) {
          this.startDrawWall(data);
          return;
        }
        // Creation off: an empty-space drag can still box-select below.
      }
      this.enterEditMode();
      if (!this.editing) return;
    }

    // Empty-space left-drag while editing: box-select — or, when selection is
    // off (create tool), another draw-wall (every empty drag draws there).
    // "Empty" must also exclude the bow/move gizmos: they park OUTSIDE the
    // wall band (no node/edge under the cursor), so without these checks a
    // drag on them would box-select instead of reaching their branches below.
    // Restricted to the 2D ortho view — see isInteractiveViewActive.
    if (isLeft && !targetNodeId && !targetEdgeId && !this.clickTargetHandle && !this.clickTargetBowEdgeId && !this.clickTargetMoveEdgeId) {
      if (!this.caps.allowSelect) {
        this.startDrawWall(data);
        return;
      }
      if (!this.isInteractiveViewActive()) return;
      this.startBoxSelect(data.event);
      return;
    }

    // Wall-side resize takes priority over the edge/node drags below — when
    // a handle was under the cursor at mousedown, dragging adjusts that
    // side's `leftSize` / `rightSize`. Left-button only. The handle branches
    // are selection-gated: without selection the handles never render, so the
    // click targets stay null — the guards are defensive.
    if (isLeft && this.caps.allowSelect && this.clickTargetHandle) {
      this.startResizeWallSide(this.clickTargetHandle, data.event);
      return;
    }

    // BOW handle: left-drag bows the wall into a circular arc.
    if (isLeft && this.caps.allowSelect && this.clickTargetBowEdgeId) {
      this.startBowEdge(this.clickTargetBowEdgeId, data.event);
      return;
    }

    // Edge MOVE handle: left-drag slides the whole wall perpendicular to itself.
    if (isLeft && this.caps.allowSelect && this.clickTargetMoveEdgeId) {
      this.startMoveEdgePerp(this.clickTargetMoveEdgeId, data.event);
      return;
    }

    const floor = projectMouseToFloor(data.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
    if (!floor) return;

    const floorRel = this.toRelative(floor);

    // Selection-driven left-drag gesture (consistent for nodes AND edges):
    //   - target is ALREADY selected → MOVE the whole selection.
    //   - target is NOT selected     → EXTRUDE a new wall from it (node-extrude
    //     for a node, edge-split-extrude for an edge).
    // This mirrors the right-drag node extrude and the previous edge left-drag,
    // but gates "move" behind selection so you can't accidentally drag-move
    // something you only meant to grab — an unselected grab creates instead.
    //
    // Capability variants:
    //  - selection off (create tool): the selection read is hard-nulled — the
    //    shared selection state can carry stale entries from another tool, and
    //    every grab must create, never move.
    //  - creation off (edit tool): an unselected grab selects the target and
    //    moves it (grab-to-move) instead of extruding.
    const sel = this.caps.allowSelect ? this.selection : null;
    if (isLeft && targetNodeId) {
      const nodeIsSelected = sel?.getSelectedNodeIds().has(targetNodeId) ?? false;
      if (nodeIsSelected) {
        this.startMoveSelection(skeleton, targetNodeId, floorRel);
        return;
      }
      if (!this.caps.allowCreate) {
        this.setSelection(targetNodeId, null);
        this.startMoveSelection(skeleton, targetNodeId, floorRel);
        return;
      }
      // Unselected node → extrude a new wall from it (was right-drag only).
      const node = skeleton.getNode(targetNodeId);
      if (!node) return;
      this.drag = {
        kind: 'extrude-from-node',
        nodeId: targetNodeId,
        dragOrigin: this.toRelative(node.position),
        cursorOffset: new THREE.Vector2(),
        originalPositions: new Map(),
        snapshotted: false,
        lastSnap: null,
        startButton: data.button,
      };
      this.buildAndCacheDragConstructionLines(new THREE.Vector3(node.position.x, node.position.y, skeleton.getFloorHeight()));
      this.deps.viewer.inputHandler?.blockOrbitControls?.();
      return;
    }

    if (isLeft && targetEdgeId && targetEdgePoint) {
      const edgeIsSelected = sel?.getSelectedEdgeIds().has(targetEdgeId) ?? false;
      if (edgeIsSelected || (!this.caps.allowCreate && this.caps.allowSelect)) {
        // Selected edge → move the selection (its endpoints are auto-selected
        // alongside it, so this translates the wall and any co-selected
        // geometry). Anchor the move at one endpoint; the cursor delta drives
        // the translation, so which endpoint is the anchor doesn't shift it.
        // With creation off, an unselected edge grab selects it first
        // (grab-to-move) — extruding is not available there.
        if (!edgeIsSelected) this.setSelection(null, targetEdgeId);
        const endpointAnchor = skeleton.getEdge(targetEdgeId)?.startNodeId ?? null;
        if (endpointAnchor) {
          this.startMoveSelection(skeleton, endpointAnchor, floorRel);
          return;
        }
      }
      // Unselected edge → split + extrude (create a new wall branching off the
      // edge at the click point).
      if (!this.caps.allowCreate) return;
      const splitRel = this.toRelative(targetEdgePoint);
      this.drag = {
        kind: 'extrude-from-edge',
        edgeId: targetEdgeId,
        splitPoint: splitRel.clone(),
        dragOrigin: splitRel.clone(),
        cursorOffset: new THREE.Vector2(),
        originalPositions: new Map(),
        snapshotted: false,
        lastSnap: null,
        startButton: data.button,
      };
      const startAbs = this.toAbsolute(splitRel, skeleton.getFloorHeight());
      this.buildAndCacheDragConstructionLines(new THREE.Vector3(startAbs.x, startAbs.y, startAbs.z));
      this.deps.viewer.inputHandler?.blockOrbitControls?.();
      return;
    }

    if (isRight && targetNodeId && this.caps.allowCreate) {
      const node = skeleton.getNode(targetNodeId);
      if (!node) return;
      this.drag = {
        kind: 'extrude-from-node',
        nodeId: targetNodeId,
        dragOrigin: this.toRelative(node.position),
        cursorOffset: new THREE.Vector2(),
        originalPositions: new Map(),
        snapshotted: false,
        lastSnap: null,
        startButton: data.button,
      };
      this.buildAndCacheDragConstructionLines(new THREE.Vector3(node.position.x, node.position.y, skeleton.getFloorHeight()));
      this.deps.viewer.inputHandler?.blockOrbitControls?.();
      return;
    }
  }

  /**
   * Start a draw-wall drag: draws a brand-new wall between two free points —
   * the (snapped) press point and, on release, the (snapped) cursor. Mirrors
   * the extrude gesture's snapping + live preview, but neither endpoint needs
   * to exist yet; either may connect onto existing nodes/edges via its snap.
   * Enters edit mode itself, including on a completely EMPTY sketch (the
   * press point pins the session reference). Public so the editor tool can
   * start it right after creating a fresh sketch mid-drag.
   */
  startDrawWall(data: DragEventData): void {
    if (this.drag) return;
    if (!this.caps.allowCreate) return; // public — guard direct tool calls too
    if (!this.isInteractiveViewActive()) return;
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return;
    const floor = projectMouseToFloor(data.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
    if (!floor) return;
    if (!this.editing) this.enterEditMode(new THREE.Vector2(floor.x, floor.y));
    if (!this.editing) return;

    const startRel = this.toRelative(floor);
    this.drag = {
      kind: 'draw-wall',
      splitPoint: startRel.clone(),
      dragOrigin: startRel.clone(),
      cursorOffset: new THREE.Vector2(),
      originalPositions: new Map(),
      snapshotted: false,
      lastSnap: null,
      startButton: data.button,
    };
    // Snap the START too, so pressing near existing geometry connects the new
    // wall there instead of dropping a duplicate node beside it.
    const startSnap = this.resolveSnapForDrag(skeleton, startRel, this.modifierFromEvent(data.event));
    this.drag.startSnap = startSnap;
    this.drag.dragOrigin = startSnap.point.clone();
    this.drag.splitPoint = startSnap.point.clone();
    const startAbs = this.toAbsolute(startSnap.point, skeleton.getFloorHeight());
    this.buildAndCacheDragConstructionLines(new THREE.Vector3(startAbs.x, startAbs.y, startAbs.z));
    this.deps.viewer.inputHandler?.blockOrbitControls?.();
  }

  onDrag(data: DragEventData): void {
    if (!this.drag) return;
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return;

    // Box-select: just track the rectangle, no snap math, no skeleton mutation.
    if (this.drag.kind === 'box-select') {
      this.updateBoxSelect(data.event);
      return;
    }

    const floor = projectMouseToFloor(data.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
    if (!floor) return;

    const floorRel = this.toRelative(floor);
    const candidate = floorRel.sub(this.drag.cursorOffset);
    const modifier = this.modifierFromEvent(data.event);

    // Bow drag bypasses the generic snap machinery — its only snap is
    // "back to straight", handled inside applyBowEdge.
    if (this.drag.kind === 'bow-edge' && this.drag.edgeId) {
      if (!this.drag.snapshotted) {
        this.pushWallSketchUndoSnapshot();
        this.drag.snapshotted = true;
      }
      this.applyBowEdge(skeleton, this.drag.edgeId, candidate, modifier, data.event);
      return;
    }

    // Drop the live extrude preview BEFORE snapping so the snap engine never
    // sees the temporary preview node/edge — otherwise the cursor snaps to its
    // own preview target (felt like grid-stepping / forced wall connection).
    // `updateExtrudePreview` re-applies the preview after the snap resolves.
    this.restoreExtrudePreviewBase(skeleton);
    const snap = this.resolveSnapForDrag(skeleton, candidate, modifier);
    this.drag.lastSnap = snap;

    // Push undo snapshot once per drag, the first time the user actually moves.
    if (!this.drag.snapshotted) {
      this.pushWallSketchUndoSnapshot();
      this.drag.snapshotted = true;
    }

    if (this.drag.kind === 'move-node' && this.drag.nodeId) {
      // Group moves carry an explicit shared translation (any node may have
      // grabbed the snap); single moves place the grabbed node at snap.point.
      const groupTranslation = this.drag.originalPositions.size > 1 ? snap.translation : undefined;
      this.applyMoveNode(skeleton, this.drag.nodeId, snap.point, groupTranslation);
    } else if (this.drag.kind === 'move-edge' && this.drag.edgeId) {
      // resolveSnapForDrag always supplies a translation for move-edge.
      const translation = snap.translation ?? new THREE.Vector2();
      this.applyMoveEdge(skeleton, this.drag.edgeId, translation);
    } else if (this.drag.kind === 'move-edge-perp' && this.drag.edgeId) {
      this.applyMoveEdgePerp(skeleton, this.drag.edgeId, snap.point);
    } else if (this.drag.kind === 'extrude-from-node' || this.drag.kind === 'extrude-from-edge' || this.drag.kind === 'draw-wall') {
      this.updateExtrudePreview(snap.point);
    } else if (this.drag.kind === 'resize-wall-side' && this.drag.edgeId && this.drag.side) {
      this.applyResizeWallSide(skeleton, this.drag.edgeId, this.drag.side, snap, data.event);
    }

    this.updateSnapMarker(snap);
    this.updateSnapVisuals(skeleton, snap);
  }

  /**
   * Resolve the snap for the current drag, applying mode-specific logic so
   * the snap visual lands where the user expects it to land.
   *
   * For move-edge specifically, the snap is evaluated at each ENDPOINT (not
   * the click handle). This way when the user grabs an edge mid-segment and
   * drags it near a construction line, the snap aligns an endpoint to the
   * line, which is what they expect, and the click handle lands wherever
   * preserves the edge's shape. Snap marker shows at the snapped endpoint.
   *
   * Returns the snap result PLUS an explicit translation vector that the
   * caller applies to whatever geometry it owns. For move-node the
   * translation is implicit (snap.point == new node center) so caller can
   * ignore the translation field; for move-edge it's required.
   */
  private resolveSnapForDrag(skeleton: PointoramaWallSketch, candidate: THREE.Vector2, modifier: SnapModifier): SnapResult & { translation?: THREE.Vector2 } {
    if (!this.drag) return { point: candidate.clone(), kind: 'free' };

    // Sample world point used to translate screen-pixel snap targets into
    // world meters at the current zoom. The candidate is in session-relative
    // space; lift it to absolute for camera-distance math.
    const z = skeleton.getFloorHeight();
    const candidateAbs = this.toAbsolute(candidate, z);
    const sampleWorld = new THREE.Vector3(candidateAbs.x, candidateAbs.y, candidateAbs.z);
    const radii = this.snapRadiiForModifier(modifier, sampleWorld);
    const nodes = this.buildRelativeSnapNodes(skeleton);
    // Resize-wall-side broadens the snap edge set the same way the menu
    // set-width path does (room polygons + wall-region rings) so the user
    // can drop the side onto an adjacent wall, and turns on direct edge-
    // proximity snapping. The source edge is ignored so the cursor doesn't
    // snap back to the centerline mid-drag.
    const isResize = this.drag.kind === 'resize-wall-side';
    // Extrude drags also want to land directly on a target wall regardless of
    // angle — without direct edge snap they only attach to a wall via a
    // construction line crossing it (i.e. only when it lines up perpendicular
    // / parallel).
    const isExtrude = this.drag.kind === 'extrude-from-node' || this.drag.kind === 'extrude-from-edge' || this.drag.kind === 'draw-wall';
    const isMoveNode = this.drag.kind === 'move-node';
    // Room / outline / cavity edges are valid snap targets for resize, extrude,
    // AND plain node moves — a dragged node should be able to land on a real wall
    // surface, not just on other skeleton geometry. They compose with the normal
    // node + skeleton-edge + construction-line snapping below.
    const includeWallEdges = isResize || isExtrude || isMoveNode;
    const edges = includeWallEdges ? [...this.buildRelativeSnapEdges(skeleton), ...this.buildSnapEdgesFromRoomsAndWallRegions(skeleton)] : this.buildRelativeSnapEdges(skeleton);
    // Construction lines are pre-bucketed at drag start and reused — see
    // `buildAndCacheDragConstructionLines` / `startResizeWallSide`. Falls
    // back to a fresh build if the cache somehow wasn't populated.
    const constructionLines = this.cachedDragConstructionLines ?? bucketConstructionLines(this.buildConstructionLines(skeleton), radii.line);
    const ignoreNodeIds = this.dragIgnoreNodeIds();
    const ignoreEdgeIds = isResize && this.drag.edgeId ? new Set([this.drag.edgeId]) : this.dragIgnoreEdgeIds();

    const drag = this.drag;
    // Multi-point snap whenever more than one node moves together — every
    // move-edge (two endpoints) and any group move-node (whole selection). Each
    // moving node is a snap candidate; the one that lands closest to a target
    // wins and its translation is applied to the whole set, so ANY node in the
    // selection can grab a snap and merge. A lone moving node keeps the simple
    // single-point snap (snap.point is its own landing).
    const isMultiPoint = drag.kind === 'move-edge' || (drag.kind === 'move-node' && drag.originalPositions.size > 1);
    if (!isMultiPoint) {
      return this.snapEngine.resolve({
        nodes,
        edges,
        constructionLines,
        candidate,
        modifier,
        ignoreNodeIds,
        ignoreEdgeIds,
        nodeRadius: radii.node,
        lineTolerance: radii.line,
        // Direct edge-proximity snap so a moved node can land ON a wall edge,
        // not only where a construction line crosses one.
        allowDirectEdgeSnap: isResize || isExtrude || isMoveNode,
      });
    }

    // Snap each moving node at its prospective position and pick whichever lands
    // closest to a target. Translation is in relative space (the cursor delta
    // off the drag origin), shared by every moving node.
    const dx = candidate.x - drag.dragOrigin.x;
    const dy = candidate.y - drag.dragOrigin.y;

    let bestSnap: SnapResult | null = null;
    let bestResidual = Infinity;
    let bestEndpointId: string | null = null;

    for (const [endpointId, orig] of drag.originalPositions.entries()) {
      const prospective = new THREE.Vector2(orig.x + dx, orig.y + dy);
      const result = this.snapEngine.resolve({
        nodes,
        edges,
        constructionLines,
        candidate: prospective,
        modifier,
        ignoreNodeIds,
        ignoreEdgeIds,
        nodeRadius: radii.node,
        lineTolerance: radii.line,
        // Match the single-point path: a moving node can land directly on a wall.
        allowDirectEdgeSnap: isMoveNode,
      });
      if (result.kind === 'free') continue;
      const residual = result.point.distanceTo(prospective);
      if (residual < bestResidual) {
        bestResidual = residual;
        bestSnap = result;
        bestEndpointId = endpointId;
      }
    }

    if (!bestSnap || !bestEndpointId) {
      // Nothing snapped — free translation, marker at the grabbed-node landing.
      return {
        point: new THREE.Vector2(drag.dragOrigin.x + dx, drag.dragOrigin.y + dy),
        kind: 'free',
        translation: new THREE.Vector2(dx, dy),
      };
    }

    // The chosen node lands exactly at bestSnap.point; derive the translation
    // that achieves that. The snap marker/visual shows at the snapped node's
    // landing (bestSnap.point); the shared translation moves the whole set.
    const orig = drag.originalPositions.get(bestEndpointId);
    if (!orig) return bestSnap;
    return {
      point: bestSnap.point,
      kind: bestSnap.kind,
      nodeId: bestSnap.nodeId,
      edgeId: bestSnap.edgeId,
      lines: bestSnap.lines,
      translation: new THREE.Vector2(bestSnap.point.x - orig.x, bestSnap.point.y - orig.y),
    };
  }

  onDragEnd(data: DragEventData): void {
    if (!this.drag) return;
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) {
      this.cancelDrag();
      return;
    }

    // Releasing with the OTHER mouse button cancels the drag (e.g. right-click
    // to abort drawing a new edge). `data.event.button` is the button just
    // released; `this.drag.startButton` is the one that began the drag. When
    // they differ the user bailed — cancel instead of committing, which would
    // otherwise leave the drag's starting node behind. (`data.button` can't be
    // used: BaseTool overwrites its drag-button bookkeeping on the second
    // mousedown, so it no longer reflects the start button here.)
    const startButton = this.drag.startButton ?? 0;
    if (data.event && typeof data.event.button === 'number' && data.event.button !== startButton) {
      this.cancelDrag();
      // A right-button cancel's release also fires onRightClick (the delete
      // gesture). Swallow that one so cancelling can't also delete whatever is
      // under the cursor.
      if (data.event.button === 2) this.suppressNextRightClick = true;
      return;
    }

    // Box-select commits separately — no snap or skeleton mutation, just
    // compute the new selection from the visible rectangle. The cursor's
    // floor-projected position becomes the menu's world anchor when the
    // resulting selection is non-empty.
    if (this.drag.kind === 'box-select') {
      const menuAnchor = projectMouseToFloor(data.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
      this.finishBoxSelect(menuAnchor);
      this.endDrag();
      return;
    }

    const floor = projectMouseToFloor(data.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
    if (!floor) {
      this.cancelDrag();
      return;
    }

    const floorRel = this.toRelative(floor);
    const candidate = floorRel.sub(this.drag.cursorOffset);
    const modifier = this.modifierFromEvent(data.event);

    // Bow drag: apply the final curvature (mirrors the live preview) and
    // normalize a near-zero bulge back to a clean straight edge.
    if (this.drag.kind === 'bow-edge' && this.drag.edgeId) {
      this.applyBowEdge(skeleton, this.drag.edgeId, candidate, modifier, data.event);
      if (this.setWidthLabel) this.setWidthLabel.style.display = 'none';
      this.endDrag();
      return;
    }

    const z = skeleton.getFloorHeight();
    const candidateAbs = this.toAbsolute(candidate, z);
    const radii = this.snapRadiiForModifier(modifier, new THREE.Vector3(candidateAbs.x, candidateAbs.y, candidateAbs.z));
    // Drop the live extrude preview BEFORE resolving the release snap so the
    // snap engine never sees the temporary preview node/edge (otherwise the
    // release snaps to its own preview target, which then vanishes — leaving a
    // degenerate/garbage target that aborts the commit, i.e. the "it reverts
    // when I let go" bug).
    this.restoreExtrudePreviewBase(skeleton);
    const nodes = this.buildRelativeSnapNodes(skeleton);
    // Mirror resolveSnapForDrag's resize-wall-side adjustments so the final
    // snap on release matches the live preview the user saw mid-drag.
    const isResize = this.drag.kind === 'resize-wall-side';
    const isExtrude = this.drag.kind === 'extrude-from-node' || this.drag.kind === 'extrude-from-edge' || this.drag.kind === 'draw-wall';
    const isMoveNode = this.drag.kind === 'move-node';
    // Mirror resolveSnapForDrag: room/outline/cavity edges are snap targets for
    // resize, extrude, and node moves so the release matches the live preview.
    const edges = isResize || isExtrude || isMoveNode ? [...this.buildRelativeSnapEdges(skeleton), ...this.buildSnapEdgesFromRoomsAndWallRegions(skeleton)] : this.buildRelativeSnapEdges(skeleton);
    const constructionLines = this.cachedDragConstructionLines ?? bucketConstructionLines(this.buildConstructionLines(skeleton), radii.line);
    const snap = this.snapEngine.resolve({
      nodes,
      edges,
      constructionLines,
      candidate,
      modifier,
      ignoreNodeIds: this.dragIgnoreNodeIds(),
      ignoreEdgeIds: isResize && this.drag.edgeId ? new Set([this.drag.edgeId]) : this.dragIgnoreEdgeIds(),
      nodeRadius: radii.node,
      lineTolerance: radii.line,
      allowDirectEdgeSnap: isResize || isExtrude || isMoveNode,
    });

    if (this.drag.kind === 'move-node' && this.drag.nodeId) {
      // Live position is already applied during onDrag. On release, finalize.
      // Single-node move:
      //   - if the moved node landed on another node, merge them
      //   - else if landed on an existing edge (edge-cross), split that edge
      //     and merge the moved node into the new split node
      //   - else if the moved node has exactly 2 incident collinear edges, dissolve it
      // Group move (>1 node): weld every moving node that landed on a stationary
      // node. Edge-split / dissolve are single-node finalizers, so they're
      // skipped for groups — but each node can still snap onto another node.
      const movedId = this.drag.nodeId;
      const isGroupMove = this.drag.originalPositions.size > 1;
      // The drag already pushed ONE undo snapshot of the pre-drag state on first
      // move, so the finalizers below must NOT push their own — otherwise the
      // merge/split/dissolve becomes a separate undo step and the user has to
      // undo twice (once for the merge, once for the move). Suppress unless the
      // drag somehow never snapshotted, in which case push one so there's still
      // an undo point. See pushWallSketchUndoSnapshot in onDrag.
      const finalizeOpts = { snapshot: !this.drag.snapshotted };
      if (!isGroupMove) {
        // Coincidence check first: during the drag the moved node may have
        // already been snapped onto another node's position (so they share
        // coords exactly), but the drag-end re-resolve can return `free`
        // if the cursor wiggled past `nodeRadius` between the last onDrag
        // and release. Fall back to a direct position-equality test so an
        // "I dropped it on top" intent reliably merges. Epsilon picked to
        // be loose vs float-roundoff but tight enough to never merge two
        // walls separated by anything visible.
        const movedNode = skeleton.getNode(movedId);
        const coincidentTarget = movedNode ? this.findCoincidentNode(skeleton, movedId) : null;
        // Kind gate for welds: outline and wall geometry never connect — a
        // wall node dropped onto the outline (or vice versa) stays a separate
        // node at the snapped position. An orphan moved node is kindless.
        const movedKind = this.connectKindOfNode(skeleton, movedId);
        const canWeldNode = (targetId: string): boolean => movedKind === null || this.canConnectToNode(skeleton, targetId, movedKind);

        if (coincidentTarget && canWeldNode(coincidentTarget)) {
          this.mergeNodeInto(skeleton, movedId, coincidentTarget, finalizeOpts);
        } else if (snap.kind === 'node' && snap.nodeId && snap.nodeId !== movedId && canWeldNode(snap.nodeId)) {
          this.mergeNodeInto(skeleton, movedId, snap.nodeId, finalizeOpts);
        } else if (snap.kind === 'edge-cross' && snap.edgeId && this.isRealSkeletonEdge(skeleton, snap.edgeId) && (movedKind === null || this.canConnectToEdge(skeleton, snap.edgeId, movedKind))) {
          // Synthetic room/wall ring segments aren't splittable — only split &
          // merge into a REAL skeleton edge. Dropping onto a room edge just
          // leaves the node at the snapped position (handled by onDrag already).
          const splitNodeId = this.splitEdgeAt(skeleton, snap.edgeId, snap.point, finalizeOpts);
          if (splitNodeId) {
            this.mergeNodeInto(skeleton, movedId, splitNodeId, { snapshot: false });
          }
        } else if (this.degreeOf(skeleton, movedId) === 2) {
          this.dissolveDegree2Node(skeleton, movedId, finalizeOpts);
        }
      } else {
        // Group move: every selected node was translated together. Merge each
        // moving node that landed on top of a stationary node (it carried a
        // snap target there, or the user dropped it coincident). No edge-split
        // or dissolve — those are single-node finalizers that don't generalize.
        this.mergeMovingNodesOntoCoincident(skeleton, new Set(this.drag.originalPositions.keys()), finalizeOpts);
      }
    } else if (this.drag.kind === 'move-edge' && this.drag.edgeId) {
      // Already applied during onDrag.
    } else if (this.drag.kind === 'move-edge-perp' && this.drag.edgeId) {
      // Final apply at the snapped cursor — live-mutated each frame; this keeps
      // the committed state identical to the last frame the user saw.
      this.applyMoveEdgePerp(skeleton, this.drag.edgeId, snap.point);
      // Weld any endpoint that slid exactly onto one of its neighbour nodes. The
      // perp move keeps endpoints on their neighbour lines (constraint-
      // respecting), so the only nodes they can ever reach are those neighbours
      // — never an arbitrary new node. Merging there collapses the zero-length
      // neighbour edge into a clean junction. Suppress the snapshot so the slide
      // + weld undo as one step (the drag already snapshotted on first move).
      this.weldPerpEndpointsOntoNeighbours(skeleton, this.drag.edgeId, { snapshot: !this.drag.snapshotted });
      // If the slide shrank the moved wall itself to (near) nothing — its two
      // endpoints slid into each other along the neighbour walls — the wall
      // vanishes: replace it with a single junction at the crossing of those
      // neighbour walls' centerlines.
      if (this.drag.moveDir) {
        this.collapseMovedEdgeIfTooSmall(skeleton, this.drag.edgeId, new THREE.Vector2(this.drag.moveDir.x, this.drag.moveDir.y), { snapshot: !this.drag.snapshotted });
      }
    } else if (this.drag.kind === 'extrude-from-node' && this.drag.nodeId) {
      // Preview overlay already dropped above (before the snap resolve), so
      // commit + undo snapshot operate on the real (pre-preview) topology.
      if (!this.drag.snapshotted) {
        this.pushWallSketchUndoSnapshot();
        this.drag.snapshotted = true;
      }
      this.commitExtrudeFromNode(skeleton, this.drag.nodeId, snap);
    } else if (this.drag.kind === 'extrude-from-edge' && this.drag.edgeId && this.drag.splitPoint) {
      if (!this.drag.snapshotted) {
        this.pushWallSketchUndoSnapshot();
        this.drag.snapshotted = true;
      }
      this.commitExtrudeFromEdge(skeleton, this.drag.edgeId, this.drag.splitPoint, snap);
    } else if (this.drag.kind === 'draw-wall' && this.drag.startSnap) {
      if (!this.drag.snapshotted) {
        this.pushWallSketchUndoSnapshot();
        this.drag.snapshotted = true;
      }
      this.commitDrawWall(skeleton, this.drag.startSnap, snap);
    } else if (this.drag.kind === 'resize-wall-side' && this.drag.edgeId && this.drag.side) {
      // Final apply at the snapped cursor — the live preview already mutated
      // the skeleton each frame, but the release snap can differ slightly
      // from the last onDrag frame (different snap state under the cursor).
      // Re-applying with the final `snap.point` keeps the committed value
      // identical to what the user sees on release.
      if (!this.drag.snapshotted) {
        this.pushWallSketchUndoSnapshot();
        this.drag.snapshotted = true;
      }
      this.applyResizeWallSide(skeleton, this.drag.edgeId, this.drag.side, snap, data.event);
      // Hide the dashed visual + label now that the drag is over.
      if (this.setWidthVisual) this.setWidthVisual.visible = false;
      if (this.setWidthLabel) this.setWidthLabel.style.display = 'none';
    }

    // Match the wall properties to the skinning after every completed
    // MUTATING gesture (thickness gizmo, node/edge moves, drawing/extruding
    // new walls, bows) — never mid-drag. Box-select returned above; every
    // kind reaching this point mutated geometry.
    this.weldSketchThicknesses(skeleton);

    this.endDrag();
  }

  /**
   * Apply the skin's pass-through thickness weld to the sketch DATA in place
   * (same weld the renderer uses, so properties and visuals agree). Runs at
   * gesture ends and width-input confirms; persistence still happens on
   * commit. No-op when nothing welds.
   */
  private weldSketchThicknesses(skeleton: PointoramaWallSketch): void {
    const nodes = skeleton.getNodes();
    const edges = skeleton.getAllEdges();
    const welded = applyWeldedEdgeSizes(nodes, edges);
    if (welded === edges) return;
    skeleton.setData(nodes, welded, skeleton.getMetadata());
    this.deps.onWallSketchRestored?.();
    if (this.editing) this.applyEditVisuals();
    this.selection?.reapplyVisuals();
  }

  /** Aborts current drag, restoring the original positions if needed. */
  private cancelDrag(): void {
    if (!this.drag) return;
    const skeleton = this.deps.getSceneWallSketch();
    if (skeleton && this.drag.snapshotted && (this.drag.kind === 'move-node' || this.drag.kind === 'move-edge' || this.drag.kind === 'move-edge-perp' || this.drag.kind === 'resize-wall-side' || this.drag.kind === 'bow-edge')) {
      // Roll back via the snapshot we already pushed. Pop it to keep undo clean.
      const mgr = this.deps.getUndoRedo();
      if (mgr?.canUndo) {
        mgr.undo();
        // The snapshot now sits on the redo stack; clear it so cancelled work
        // doesn't appear in redo history either.
        this.sessionPushCount = Math.max(0, this.sessionPushCount - 1);
      }
    }
    // Extrude drags mutate only the in-memory preview overlay (not the real
    // skeleton), but they DO push one undo snapshot on first move. Drop the
    // preview to get back to the clean state, then discard that now-orphaned
    // snapshot (popState, not undo — the skeleton is already clean) so a
    // cancelled new line leaves no stray undo entry.
    if (skeleton && (this.drag.kind === 'extrude-from-node' || this.drag.kind === 'extrude-from-edge' || this.drag.kind === 'draw-wall')) {
      this.restoreExtrudePreviewBase(skeleton);
      if (this.drag.snapshotted) {
        this.deps.getUndoRedo()?.popState();
        this.sessionPushCount = Math.max(0, this.sessionPushCount - 1);
      }
    }
    this.endDrag();
  }

  private endDrag(): void {
    // Close the thickness-drag peek at the room/outline lines (paired with
    // startResizeWallSide) — endDrag is the single funnel for commit + cancel.
    if (this.drag?.kind === 'resize-wall-side') {
      this.deps.viewer.floorplanRepresentation?.endOriginalPeek();
    }
    this.emptyPressOrbitBlock = false;
    this.deps.viewer.inputHandler?.unblockOrbitControls?.();
    this.hideSnapMarker();
    this.hideExtrudeLine();
    // Restore source-node tints + clear glyphs/highlights. We pass the current
    // skeleton so any tinted SphereMarkers can have their original color put
    // back; if the skeleton was rebuilt (setData) the markers are already gone
    // and forgetTints() will have cleared our bookkeeping.
    this.snapVisuals?.hide(this.deps.getSceneWallSketch());
    // Box-select rectangle gets hidden by finishBoxSelect on commit; this
    // catches the cancelled-drag case too.
    this.boxSelect?.cancel();
    this.drag = null;
    this.cachedDragConstructionLines = null;
    // Handles get repositioned on the next mousemove via updateHover; hide
    // them now so a stale-pos sphere doesn't linger between drag end and
    // the next hover sample.
    this.updateWallHandles(null);
    this.hoveredHandle = null;
    this.updateBowHandles(null);
    this.hoveredBowEdgeId = null;
    this.updateEdgeMoveHandles(null);
    this.hoveredMoveEdgeId = null;
    this.clearClickTarget();
  }

  private clearClickTarget(): void {
    this.clickTargetNodeId = null;
    this.clickTargetEdgeId = null;
    this.clickTargetEdgePoint = null;
    this.clickTargetHandle = null;
    this.clickTargetBowEdgeId = null;
    this.clickTargetMoveEdgeId = null;
  }

  // ─── Drag helpers ────────────────────────────────────────────────────

  private modifierFromEvent(event: MouseEvent): SnapModifier {
    if (event.ctrlKey || event.metaKey) return 'none';
    if (event.shiftKey) return 'strict';
    return 'default';
  }

  /**
   * Compute world-meter snap radii from screen-pixel targets, given the
   * current camera and a sample world point. The pixel→meter ratio depends on
   * camera distance for perspective and on the projection matrix for ortho;
   * `Utils.projectedRadius` handles both. Called once per drag frame.
   *
   * Strict mode (Shift) tightens the node radius so Shift means "exact landing
   * only." Line tolerance is irrelevant in strict mode (the engine skips line
   * snaps entirely under strict) but we keep it consistent for callers.
   *
   * @param sampleWorldPoint  A point in absolute world coords near the action,
   *                          typically the candidate's world position. Used as
   *                          the depth reference for perspective cameras.
   */
  /**
   * Build and cache the bucketed construction lines for the active drag.
   * Called once when a drag starts (skipping box-select, which has no snap).
   * The `sampleWorldPoint` is just a stable position to compute a
   * representative `lineTolerance` from for bucketing — typically the drag
   * origin in world space. Bucketing tolerance doesn't need to track zoom
   * frame-by-frame; stability is more valuable than per-zoom-level precision.
   */
  private buildAndCacheDragConstructionLines(sampleWorldPoint: THREE.Vector3): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) {
      this.cachedDragConstructionLines = null;
      return;
    }
    const lines = this.buildConstructionLines(skeleton);
    const radii = this.snapRadiiForModifier('default', sampleWorldPoint);
    // Inception candidate: the drag origin in the same relative coords the
    // construction lines live in. Picking the bucket rep closest to this
    // point keeps the line the user was nearest to at drag start as the
    // surviving snap target — e.g. a node move that started on a wall
    // keeps the wall's parallel, an edge-split extrude keeps the
    // perpendicular through the split point.
    const inception = this.drag?.dragOrigin ? { x: this.drag.dragOrigin.x, y: this.drag.dragOrigin.y } : undefined;
    this.cachedDragConstructionLines = bucketConstructionLines(lines, radii.line, inception);
  }

  private snapRadiiForModifier(modifier: SnapModifier, sampleWorldPoint: THREE.Vector3): { node: number; line: number } {
    const camera = this.deps.viewer.scene.getActiveCamera();
    const size = this.deps.viewer.renderer.getSize(new THREE.Vector2());
    const w = size.width || 1000;
    const h = size.height || 1000;
    const distance = camera.position.distanceTo(sampleWorldPoint);
    // pixelsPerMeter = pixels-on-screen for a 1m world segment at this depth.
    // 0 / NaN guards: clamp to a safe positive ratio.
    const pixelsPerMeter = Math.max(1e-6, Utils.projectedRadius(1, camera, distance, w, h));
    const pxToMeters = (px: number): number => clampNumber(px / pixelsPerMeter, MIN_SNAP_RADIUS_M, MAX_SNAP_RADIUS_M);
    const tighten = modifier === 'strict' ? STRICT_TIGHTEN : 1;
    return {
      node: pxToMeters(SNAP_NODE_RADIUS_PX * tighten),
      line: pxToMeters(SNAP_LINE_TOLERANCE_PX),
    };
  }

  /**
   * Nodes the snap engine should ignore — every node that is moving in the
   * current drag. For a single-node move that's just the grabbed node; for a
   * group move it's the whole selection; for a move-edge it's both endpoints.
   * All moving nodes share the same translation, so they must never snap onto
   * each other (only onto stationary geometry).
   */
  private dragIgnoreNodeIds(): Set<string> {
    const set = new Set<string>();
    if (!this.drag) return set;
    if (this.drag.kind === 'move-node' || this.drag.kind === 'move-edge') {
      for (const id of this.drag.originalPositions.keys()) set.add(id);
    }
    return set;
  }

  /**
   * Edges the snap engine should skip for edge-cross snaps:
   *   - move-node N: edges incident to N (snapping onto your own incident edge
   *     would split it at a weird place and drag the moving node toward it).
   *   - extrude-from-edge E: the source edge itself.
   *   - extrude-from-node N: edges incident to N.
   */
  private dragIgnoreEdgeIds(): Set<string> {
    const set = new Set<string>();
    if (!this.drag) return set;
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return set;
    const addIncident = (nodeId: string): void => {
      const node = skeleton.getNode(nodeId);
      if (!node) return;
      for (const eid of node.edgeIds) set.add(eid);
    };
    if (this.drag.kind === 'move-node') {
      // Every moving node's incident edges — snapping a moving node onto an
      // edge that moves with it would split it at a nonsense place.
      for (const id of this.drag.originalPositions.keys()) addIncident(id);
    }
    if (this.drag.kind === 'extrude-from-node' && this.drag.nodeId) {
      addIncident(this.drag.nodeId);
    }
    if (this.drag.kind === 'extrude-from-edge' && this.drag.edgeId) {
      set.add(this.drag.edgeId);
    }
    if (this.drag.kind === 'move-edge' && this.drag.edgeId) {
      set.add(this.drag.edgeId);
    }
    return set;
  }

  // ─── Geometry mutations ──────────────────────────────────────────────

  /**
   * Move a node (or a group of nodes if the drag was started on one of a
   * multi-selection). `targetRel` is where the *grabbed* node's center should
   * land; every other node in `drag.originalPositions` is translated by the
   * same delta so the group preserves its shape.
   */
  private applyMoveNode(skeleton: PointoramaWallSketch, nodeId: string, targetRel: THREE.Vector2, translation?: THREE.Vector2): void {
    const z = skeleton.getFloorHeight();
    const drag = this.drag;
    const grabbedOrig = drag?.originalPositions.get(nodeId);
    const isGroupMove = !!drag && drag.originalPositions.size > 1;
    // Translation in relative space. For a group move the snap is resolved
    // across ALL moving nodes (any of them may grab a snap target), so the
    // caller passes the shared translation directly. For a single-node move the
    // delta is just where the grabbed node should land minus its origin.
    const dx = translation ? translation.x : grabbedOrig ? targetRel.x - grabbedOrig.x : 0;
    const dy = translation ? translation.y : grabbedOrig ? targetRel.y - grabbedOrig.y : 0;

    // Capture `drag` into a local so the closure narrows it for the group path
    // (eslint forbids non-null assertion inside callbacks).
    const groupDrag = isGroupMove ? drag : null;
    const nodes = skeleton.getNodes().map((n) => {
      if (groupDrag) {
        const orig = groupDrag.originalPositions.get(n.id);
        if (!orig) return n;
        const newRel = new THREE.Vector2(orig.x + dx, orig.y + dy);
        const abs = this.toAbsolute(newRel, z);
        return { ...n, position: { x: abs.x, y: abs.y, z: abs.z } };
      }
      // Single-node drag — just place the grabbed node at target.
      if (n.id === nodeId) {
        const abs = this.toAbsolute(targetRel, z);
        return { ...n, position: { x: abs.x, y: abs.y, z: abs.z } };
      }
      return n;
    });

    skeleton.setData(nodes, skeleton.getAllEdges(), null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    // Selection visual was wiped by setData rebuilding spheres; reapply.
    this.selection?.reapplyVisuals();
  }

  /**
   * Translate both endpoints of an edge by `translation` (relative-space delta).
   * Caller is responsible for picking a translation — typically derived from a
   * snap on one of the endpoints.
   */
  private applyMoveEdge(skeleton: PointoramaWallSketch, edgeId: string, translation: THREE.Vector2): void {
    const edge = skeleton.getEdge(edgeId);
    if (!edge || !this.drag) return;
    const drag = this.drag;
    const z = skeleton.getFloorHeight();
    const updated = skeleton.getNodes().map((n) => {
      const orig = drag.originalPositions.get(n.id);
      if (!orig) return n;
      // Translate in relative space, convert back to absolute for storage.
      const newRel = new THREE.Vector2(orig.x + translation.x, orig.y + translation.y);
      const abs = this.toAbsolute(newRel, z);
      return { ...n, position: { x: abs.x, y: abs.y, z: abs.z } };
    });
    skeleton.setData(updated, skeleton.getAllEdges(), null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.selection?.reapplyVisuals();
  }

  // ─── Edge perpendicular move (slide neighbours / extrude on constraint) ──

  /**
   * Begin a perpendicular wall-move drag from the edge's move handle. Captures
   * the edge's unit direction + normal and a clean base snapshot so each frame
   * can recompute the slide/extrude result from scratch.
   */
  private startMoveEdgePerp(edgeId: string, event: MouseEvent): void {
    const skeleton = this.deps.getSceneWallSketch();
    const edge = skeleton?.getEdge(edgeId);
    if (!skeleton || !edge) return;
    if (!this.editing) {
      this.enterEditMode();
      if (!this.editing) return;
    }
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return;

    const z = skeleton.getFloorHeight();
    const aRel = this.toRelative(a.position);
    const bRel = this.toRelative(b.position);
    const dir = new THREE.Vector2(bRel.x - aRel.x, bRel.y - aRel.y);
    if (dir.lengthSq() < 1e-9) return;
    dir.normalize();
    const normal = new THREE.Vector2(-dir.y, dir.x); // left-perpendicular

    const floor = projectMouseToFloor(event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), z);
    if (!floor) return;
    const floorRel = this.toRelative(floor);
    const midRel = new THREE.Vector2((aRel.x + bRel.x) / 2, (aRel.y + bRel.y) / 2);

    const originalPositions = new Map<string, { x: number; y: number }>();
    originalPositions.set(edge.startNodeId, { x: aRel.x, y: aRel.y });
    originalPositions.set(edge.endNodeId, { x: bRel.x, y: bRel.y });

    this.drag = {
      kind: 'move-edge-perp',
      edgeId,
      dragOrigin: midRel.clone(),
      cursorOffset: floorRel.clone().sub(midRel),
      originalPositions,
      snapshotted: false,
      lastSnap: null,
      moveDir: { x: dir.x, y: dir.y },
      moveNormal: { x: normal.x, y: normal.y },
      // Clean base snapshot for the per-frame slide/extrude rebuilds.
      previewBaseNodes: skeleton.getNodes().map((n) => ({ ...n, edgeIds: [...n.edgeIds], roomIds: [...n.roomIds] })),
      previewBaseEdges: skeleton.getAllEdges().map((e) => ({ ...e, roomIds: [...e.roomIds] })),
    };
    this.deps.viewer.inputHandler?.blockOrbitControls?.();
  }

  /**
   * Per-frame perpendicular wall move. `snapPoint` is the snapped cursor in
   * relative coords; only its component along the edge normal is used (the move
   * is strictly perpendicular). For each endpoint: if a neighbour edge can
   * absorb the slide (it is NOT near-parallel to the moved wall), the node
   * slides along that neighbour's line; otherwise the old node stays put and a
   * new edge is extruded from it to the moved endpoint.
   */
  private applyMoveEdgePerp(skeleton: PointoramaWallSketch, edgeId: string, snapPoint: THREE.Vector2): void {
    const drag = this.drag;
    if (!drag || !drag.previewBaseNodes || !drag.previewBaseEdges || !drag.moveDir || !drag.moveNormal) return;
    const z = skeleton.getFloorHeight();
    const dir = new THREE.Vector2(drag.moveDir.x, drag.moveDir.y);
    const normal = new THREE.Vector2(drag.moveNormal.x, drag.moveNormal.y);

    // Perpendicular signed offset: project (snap − origin) onto the normal.
    const delta = new THREE.Vector2(snapPoint.x - drag.dragOrigin.x, snapPoint.y - drag.dragOrigin.y);
    let offset = delta.dot(normal);

    // Snap the offset so an endpoint can land exactly ON a neighbour node when
    // the slide brings it close. The endpoint reaches neighbour N precisely when
    // the moved centerline passes through N, i.e. at offset = (N − orig)·normal.
    // We pull `offset` to the nearest such value within the node snap radius so
    // the user gets a crisp "lands on the neighbour" engage (which the drag-end
    // weld then collapses). This is the ONLY snap the constrained perp move
    // allows — endpoints travel along their neighbour lines, so neighbour nodes
    // are the only reachable targets.
    {
      const sample = this.toAbsolute(new THREE.Vector2(drag.dragOrigin.x + normal.x * offset, drag.dragOrigin.y + normal.y * offset), z);
      const snapM = this.snapRadiiForModifier('default', new THREE.Vector3(sample.x, sample.y, sample.z)).node;
      const baseEdge = drag.previewBaseEdges.find((e) => e.id === edgeId);
      let bestOffset: number | null = null;
      let bestDist = snapM;
      if (baseEdge) {
        for (const endNodeId of [baseEdge.startNodeId, baseEdge.endNodeId]) {
          const orig = drag.originalPositions.get(endNodeId);
          const baseNode = drag.previewBaseNodes.find((n) => n.id === endNodeId);
          if (!orig || !baseNode) continue;
          for (const eid of baseNode.edgeIds) {
            if (eid === edgeId) continue;
            const nbEdge = drag.previewBaseEdges.find((e) => e.id === eid);
            if (!nbEdge) continue;
            const nbNodeId = nbEdge.startNodeId === endNodeId ? nbEdge.endNodeId : nbEdge.startNodeId;
            const nbNode = drag.previewBaseNodes.find((n) => n.id === nbNodeId);
            if (!nbNode) continue;
            const nbRel = this.toRelative(nbNode.position);
            const offsetForN = (nbRel.x - orig.x) * normal.x + (nbRel.y - orig.y) * normal.y;
            const d = Math.abs(offset - offsetForN);
            if (d < bestDist) {
              bestDist = d;
              bestOffset = offsetForN;
            }
          }
        }
      }
      // Also snap to the offset where the moved wall ITSELF collapses to zero
      // length. Each endpoint travels along a slideable neighbour line, so the
      // two endpoints meet exactly where a neighbour line at one end crosses a
      // neighbour line at the other. Pulling `offset` there previews the wall
      // vanishing, and the drag-end collapse then replaces it with a single
      // junction at that crossing (see collapseMovedEdgeIfTooSmall).
      if (baseEdge && !edgeIsCurved(baseEdge)) {
        const linesPerEnd: { point: THREE.Vector2; dir: THREE.Vector2 }[][] = [];
        for (const endNodeId of [baseEdge.startNodeId, baseEdge.endNodeId]) {
          const lines: { point: THREE.Vector2; dir: THREE.Vector2 }[] = [];
          linesPerEnd.push(lines);
          const orig = drag.originalPositions.get(endNodeId);
          const baseNode = drag.previewBaseNodes.find((n) => n.id === endNodeId);
          if (!orig || !baseNode) continue;
          for (const eid of baseNode.edgeIds) {
            if (eid === edgeId) continue;
            const nbEdge = drag.previewBaseEdges.find((e) => e.id === eid);
            if (!nbEdge) continue;
            const nbNodeId = nbEdge.startNodeId === endNodeId ? nbEdge.endNodeId : nbEdge.startNodeId;
            const nbNode = drag.previewBaseNodes.find((n) => n.id === nbNodeId);
            if (!nbNode) continue;
            const nbRel = this.toRelative(nbNode.position);
            const nbDir = new THREE.Vector2(nbRel.x - orig.x, nbRel.y - orig.y);
            if (nbDir.lengthSq() < 1e-9) continue;
            nbDir.normalize();
            if (Math.abs(nbDir.dot(dir)) >= PERP_MOVE_PARALLEL_COS) continue;
            lines.push({ point: new THREE.Vector2(orig.x, orig.y), dir: nbDir });
          }
        }
        const origStart = drag.originalPositions.get(baseEdge.startNodeId);
        if (origStart) {
          for (const la of linesPerEnd[0]) {
            for (const lb of linesPerEnd[1]) {
              const cross = infiniteLineIntersection(la.point, la.dir, lb.point, lb.dir);
              if (!cross) continue;
              // The moved centerline passes through the crossing at exactly
              // this offset (origStart lies on the original centerline).
              const offsetForCross = (cross.x - origStart.x) * normal.x + (cross.y - origStart.y) * normal.y;
              const d = Math.abs(offset - offsetForCross);
              if (d < bestDist) {
                bestDist = d;
                bestOffset = offsetForCross;
              }
            }
          }
        }
      }
      if (bestOffset !== null) offset = bestOffset;
    }

    // Rebuild from the clean base each frame.
    const nodes = drag.previewBaseNodes.map((n) => ({ ...n, edgeIds: [...n.edgeIds], roomIds: [...n.roomIds] }));
    const edges = drag.previewBaseEdges.map((e) => ({ ...e, roomIds: [...e.roomIds] }));
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const edge = edges.find((e) => e.id === edgeId);
    if (!edge) return;

    for (const endNodeId of [edge.startNodeId, edge.endNodeId]) {
      const orig = drag.originalPositions.get(endNodeId);
      const node = nodeById.get(endNodeId);
      if (!orig || !node) continue;
      // The moved edge stays PARALLEL to itself: its new centerline passes
      // through the perpendicularly-translated endpoint with the same direction.
      const movedRel = new THREE.Vector2(orig.x + normal.x * offset, orig.y + normal.y * offset);

      // Neighbour edges at this endpoint (excluding the moved edge), classified
      // by whether they can SLIDE (their line crosses the translated moved-edge
      // line) or are CONSTRAINED (near-parallel to the moved wall — no crossing).
      const neighbours = node.edgeIds
        .filter((eid) => eid !== edgeId)
        .map((eid) => edges.find((e) => e.id === eid))
        .filter((e): e is SkeletonEdgeData => !!e);
      // The direction the wall is moving toward (signed normal). Used at a
      // junction split to decide which slideable walls follow the wall (migrate
      // to the new node) vs. stay behind on the old node.
      const moveSide = normal.clone().multiplyScalar(Math.sign(offset) || 1);
      type Slideable = { nb: SkeletonEdgeData; otherRel: THREE.Vector2; nbDir: THREE.Vector2; towardMove: boolean };
      const slideable: Slideable[] = []; // line crosses the moved-edge line (degenerate / near-parallel ones are left out — they stay put)
      for (const nb of neighbours) {
        const otherId = nb.startNodeId === endNodeId ? nb.endNodeId : nb.startNodeId;
        const other = nodeById.get(otherId);
        if (!other) continue;
        const otherRel = this.toRelative(other.position);
        const nbDir = new THREE.Vector2(otherRel.x - orig.x, otherRel.y - orig.y);
        if (nbDir.lengthSq() < 1e-9 || Math.abs(nbDir.clone().normalize().dot(dir)) >= PERP_MOVE_PARALLEL_COS) continue;
        slideable.push({ nb, otherRel, nbDir: nbDir.normalize(), towardMove: nbDir.dot(moveSide) > 0 });
      }

      // A neighbour is CONSTRAINED (blocking) when it runs near-parallel to the
      // moved wall, so its line never crosses the translated edge — it can't
      // absorb the slide. Those are the only walls that force a split.
      const hasBlocker = slideable.length < neighbours.length;
      // Walls that follow the moved wall to the new node. When NOTHING blocks
      // (every neighbour can slide), the whole junction just slides as one — all
      // neighbours follow regardless of which way they point, so no node is left
      // behind and no split happens. Only once a blocking neighbour pins the old
      // node do we fall back to migrating just the slideables heading TOWARD the
      // move side and leaving the rest (and the blocker) behind.
      const followers = hasBlocker ? slideable.filter((s) => s.towardMove) : slideable;
      const stays = neighbours.length - followers.length; // walls remaining on the old node
      // A crossing target keeps the lead follower straight; else the bare
      // perpendicular point.
      const leadCross = followers.length > 0 ? infiniteLineIntersection(movedRel, dir, followers[0].otherRel, followers[0].nbDir) : null;
      const target = leadCross ?? movedRel;
      const targetAbs = this.toAbsolute(new THREE.Vector2(target.x, target.y), z);

      if (neighbours.length === 0) {
        // Free end — just translate it perpendicular.
        const abs = this.toAbsolute(movedRel, z);
        node.position = { x: abs.x, y: abs.y, z: abs.z };
      } else if (stays === 0) {
        // Everything at this node follows the wall (simple corner, or all walls
        // head the move way): move the shared node itself to the target. No
        // split needed — nothing is left behind.
        node.position = { x: targetAbs.x, y: targetAbs.y, z: targetAbs.z };
      } else {
        // SPLIT the node: the moved wall + followers migrate to a NEW node at the
        // target (joined to the old node by a stub edge); the walls that stay
        // keep the old, anchored node. This turns one junction into two lower-
        // degree nodes with a connecting edge — and nothing is duplicated because
        // each wall lives on exactly one of the two nodes.
        const newNodeId = this.generateId();
        const stubId = this.generateId();
        const migrated = [edgeId, ...followers.map((s) => s.nb.id)];
        const newNode: SkeletonNodeData = {
          id: newNodeId,
          name: this.generateName('node'),
          position: { x: targetAbs.x, y: targetAbs.y, z: targetAbs.z },
          roomIds: [...edge.roomIds],
          edgeIds: [...migrated, stubId],
          metadata: '',
        };
        const stub: SkeletonEdgeData = { ...edge, id: stubId, name: this.generateName('edge'), startNodeId: endNodeId, endNodeId: newNodeId };
        // Old node keeps the stay-behind walls + the stub; drops the migrated set.
        node.edgeIds = node.edgeIds.filter((eid) => !migrated.includes(eid)).concat(stubId);
        // Reconnect every migrated edge's shared end to the new node.
        for (const e of edges) {
          if (!migrated.includes(e.id)) continue;
          if (e.startNodeId === endNodeId) e.startNodeId = newNodeId;
          if (e.endNodeId === endNodeId) e.endNodeId = newNodeId;
        }
        nodes.push(newNode);
        edges.push(stub);
        nodeById.set(newNodeId, newNode);
      }
    }

    skeleton.setData(nodes, edges, null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.selection?.reapplyVisuals();
  }

  /**
   * Begin a wall-side resize drag. Uses the same construction-line context
   * as `startSetWidthMode` (parallel + perpendicular through the source
   * edge) and the same set-width visual + label, but the commit lives in
   * `applyResizeWallSide` and runs every frame instead of on a click.
   */
  private startResizeWallSide(handle: { edgeId: string; side: WallSide }, _event: MouseEvent): void {
    const skeleton = this.deps.getSceneWallSketch();
    const edge = skeleton?.getEdge(handle.edgeId);
    if (!skeleton || !edge) return;
    if (!this.editing) {
      this.enterEditMode();
      if (!this.editing) return;
    }
    this.ensureSetWidthVisual();
    this.ensureSetWidthLabel();
    // The skinned floorplan view hides the room/outline lines; a thickness
    // drag needs them as reference, so peek at them for the drag's duration
    // (ended in endDrag).
    this.deps.viewer.floorplanRepresentation?.beginOriginalPeek();
    this.drag = {
      kind: 'resize-wall-side',
      edgeId: handle.edgeId,
      side: handle.side,
      // dragOrigin / cursorOffset aren't meaningful for resize (the snap
      // candidate is just the cursor on the floor), but the DragState shape
      // is shared; supply harmless dummies.
      dragOrigin: new THREE.Vector2(),
      cursorOffset: new THREE.Vector2(),
      originalPositions: new Map(),
      snapshotted: false,
      lastSnap: null,
    };

    // Bucket the construction lines once for this drag. Inception candidate
    // = the handle position in relative coords, so the bucket rep nearest
    // to where the user grabbed wins (parallel-along-the-wall handles will
    // start closest to the perpendicular snap, etc.).
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (a && b) {
      const mid = new THREE.Vector3((a.position.x + b.position.x) / 2, (a.position.y + b.position.y) / 2, skeleton.getFloorHeight());
      const radii = this.snapRadiiForModifier('default', mid);
      const handlePosWorld = this.wallHandlePosition(skeleton, edge, handle.side);
      const inception = handlePosWorld ? { x: handlePosWorld.x - (this.referencePoint?.x ?? 0), y: handlePosWorld.y - (this.referencePoint?.y ?? 0) } : undefined;
      this.cachedDragConstructionLines = bucketConstructionLines(this.buildSetWidthConstructionLines(skeleton, edge), radii.line, inception);
    }

    this.deps.viewer.inputHandler?.blockOrbitControls?.();
  }

  /**
   * Per-frame side-resize: take the snapped cursor, project to the source
   * edge's infinite line, write the perpendicular distance into the
   * appropriate `leftSize` / `rightSize`, and update the dashed visual +
   * label. Mirrors `updateSetWidthVisual` but commits live to the skeleton.
   */
  private applyResizeWallSide(skeleton: PointoramaWallSketch, edgeId: string, side: WallSide, snap: SnapResult, event: MouseEvent): void {
    const edge = skeleton.getEdge(edgeId);
    if (!edge) return;
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return;

    const z = skeleton.getFloorHeight();
    const snappedAbs = this.toAbsolute(snap.point, z);
    const a2 = new THREE.Vector2(a.position.x, a.position.y);
    const b2 = new THREE.Vector2(b.position.x, b.position.y);
    const c2 = new THREE.Vector2(snappedAbs.x, snappedAbs.y);
    let closestOnEdge2: THREE.Vector2;
    let distance: number;
    if (edgeIsCurved(edge)) {
      // Radial distance to the arc centerline — the arc equivalent of the
      // perpendicular distance below.
      const proj = closestPointOnEdge({ x: c2.x, y: c2.y }, { x: a2.x, y: a2.y }, { x: b2.x, y: b2.y }, edge.bulge);
      closestOnEdge2 = new THREE.Vector2(proj.point.x, proj.point.y);
      distance = proj.distance;
    } else {
      closestOnEdge2 = closestPointOnInfiniteLine(c2, a2, b2);
      distance = Math.hypot(c2.x - closestOnEdge2.x, c2.y - closestOnEdge2.y);
    }

    // Honour the side the user grabbed regardless of which side the cursor
    // is on. The set-width tool inferred side from a cross product; here
    // the handle owns its side, so dragging across the wall just clamps
    // the size to the perpendicular distance — the user can't accidentally
    // flip from leftSize to rightSize mid-drag.
    const newEdges = skeleton.getAllEdges().map((e) => {
      if (e.id !== edge.id) return e;
      return side === 'left' ? { ...e, leftSize: distance } : { ...e, rightSize: distance };
    });
    skeleton.setData(skeleton.getNodes(), newEdges, null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.selection?.reapplyVisuals();

    // Visuals: dashed line from the closest point on the edge to the snapped
    // cursor, plus the running width label. Same visual as the menu set-
    // width path so the two tools read identically.
    if (this.helpersGroup && this.setWidthVisual) {
      const start = this.worldToHelpersLocal({ x: closestOnEdge2.x, y: closestOnEdge2.y, z: z + 0.03 });
      const end = this.worldToHelpersLocal({ x: c2.x, y: c2.y, z: z + 0.03 });
      const positions = this.setWidthVisual.geometry.attributes.position as THREE.BufferAttribute;
      positions.setXYZ(0, start.x, start.y, start.z);
      positions.setXYZ(1, end.x, end.y, end.z);
      positions.needsUpdate = true;
      this.setWidthVisual.computeLineDistances();
      this.setWidthVisual.visible = true;
    }
    if (this.setWidthLabel) {
      this.setWidthLabel.textContent = `${distance.toFixed(3)} m`;
      this.setWidthLabel.style.display = 'block';
      const rect = this.deps.viewer.renderer.domElement.getBoundingClientRect();
      this.setWidthLabel.style.left = `${event.clientX - rect.left + 12}px`;
      this.setWidthLabel.style.top = `${event.clientY - rect.top + 12}px`;
    }
  }

  /** Live preview for extrude — draws a temp line from the source to the cursor. */
  private updateExtrudePreview(targetRel: THREE.Vector2): void {
    if (!this.drag) return;
    const group = this.ensureHelpersGroup();
    if (!group) return;
    const skeleton = this.deps.getSceneWallSketch();
    const z = (skeleton?.getFloorHeight() ?? 0) + 0.03;
    // Convert to coords relative to the helpers group so vertex buffers stay
    // small. dragOrigin is already in session-relative space; adding the
    // helpers-group origin offset (which equals the session reference point)
    // gives us the same value when the reference is the same — but we route
    // through the worldToHelpersLocal helper to be explicit.
    const startAbs = this.toAbsolute(this.drag.dragOrigin, z);
    const targetAbs = this.toAbsolute(targetRel, z);
    const startLocal = this.worldToHelpersLocal(startAbs);
    const targetLocal = this.worldToHelpersLocal(targetAbs);
    if (!this.extrudeLine) {
      const geom = new THREE.BufferGeometry();
      geom.setFromPoints([startLocal, targetLocal]);
      const mat = new THREE.LineDashedMaterial({ color: EXTRUDE_PREVIEW_COLOR, dashSize: 0.1, gapSize: 0.05, depthTest: false, transparent: true, opacity: 0.9 });
      this.extrudeLine = new THREE.Line(geom, mat);
      this.extrudeLine.computeLineDistances();
      this.extrudeLine.renderOrder = 999;
      group.add(this.extrudeLine);
    } else {
      const positions = this.extrudeLine.geometry.attributes.position as THREE.BufferAttribute;
      positions.setXYZ(0, startLocal.x, startLocal.y, startLocal.z);
      positions.setXYZ(1, targetLocal.x, targetLocal.y, targetLocal.z);
      positions.needsUpdate = true;
      this.extrudeLine.computeLineDistances();
    }

    // Live skeleton preview: rebuild the wall skinning with a temporary new
    // edge so the user sees the actual wall (not just a guide line) while
    // dragging. Restored before commit / on cancel.
    if (skeleton) this.applyExtrudeWallSketchPreview(skeleton, targetRel);
  }

  /**
   * Overlay a temporary node + edge onto the skeleton (the line being drawn)
   * and rebuild so the wall skinning previews live. Snapshots the original
   * node/edge data the first time so {@link restoreExtrudePreviewBase} can roll
   * it back before commit / on cancel. Cheap enough per-frame — node-move and
   * resize drags already rebuild skinning the same way.
   */
  private applyExtrudeWallSketchPreview(skeleton: PointoramaWallSketch, targetRel: THREE.Vector2): void {
    const drag = this.drag;
    if (!drag) return;
    const z = skeleton.getFloorHeight();

    // Snapshot the clean base once, before any preview edge is applied.
    if (!drag.previewBaseNodes || !drag.previewBaseEdges) {
      drag.previewBaseNodes = skeleton.getNodes().map((n) => ({ ...n, edgeIds: [...n.edgeIds], roomIds: [...n.roomIds] }));
      drag.previewBaseEdges = skeleton.getAllEdges().map((e) => ({ ...e, roomIds: [...e.roomIds] }));
    }

    // Resolve the source node id: the extruded node, or a temp node at the
    // edge split point for extrude-from-edge.
    const baseNodes = drag.previewBaseNodes.map((n) => ({ ...n, edgeIds: [...n.edgeIds] }));
    const baseEdges = drag.previewBaseEdges.map((e) => ({ ...e }));

    let sourceNodeId: string | null = null;
    const previewNodes = [...baseNodes];
    if (drag.kind === 'extrude-from-node' && drag.nodeId) {
      sourceNodeId = drag.nodeId;
    } else if ((drag.kind === 'extrude-from-edge' || drag.kind === 'draw-wall') && drag.splitPoint) {
      const splitAbs = this.toAbsolute(drag.splitPoint, z);
      sourceNodeId = '__preview_split__';
      previewNodes.push({ id: sourceNodeId, name: 'preview', position: { x: splitAbs.x, y: splitAbs.y, z: splitAbs.z }, roomIds: [], edgeIds: ['__preview_edge__'], metadata: '' });
    }
    if (!sourceNodeId) return;

    const targetAbs = this.toAbsolute(targetRel, z);
    const targetNodeId = '__preview_target__';
    previewNodes.push({ id: targetNodeId, name: 'preview', position: { x: targetAbs.x, y: targetAbs.y, z: targetAbs.z }, roomIds: [], edgeIds: ['__preview_edge__'], metadata: '' });

    // Mirror the commit-time role decision so the preview looks right
    // (attachment from the drag's source refs; the commit recomputes).
    const previewOutline = this.isOutlineAttachment(skeleton, {
      nodeId: drag.kind === 'extrude-from-node' ? drag.nodeId : undefined,
      edgeId: drag.kind !== 'extrude-from-node' ? drag.edgeId : undefined,
    });
    const previewEdge: SkeletonEdgeData = {
      id: '__preview_edge__',
      name: 'preview',
      startNodeId: sourceNodeId,
      endNodeId: targetNodeId,
      roomIds: [],
      ...this.newEdgeDefaults(previewOutline),
      metadata: '',
    };
    const previewEdges = [...baseEdges, previewEdge];
    if (drag.kind === 'extrude-from-node') {
      const src = previewNodes.find((n) => n.id === sourceNodeId);
      if (src) src.edgeIds = [...src.edgeIds, previewEdge.id];
    }

    skeleton.setData(previewNodes, previewEdges, null);
    drag.previewApplied = true;
  }

  /**
   * Roll the skeleton back to the snapshot taken before the live extrude
   * preview was applied. Called before commit (so commit operates on the real
   * topology) and on cancel. No-op when no preview is active.
   */
  private restoreExtrudePreviewBase(skeleton: PointoramaWallSketch): void {
    const drag = this.drag;
    if (!drag?.previewApplied || !drag.previewBaseNodes || !drag.previewBaseEdges) return;
    skeleton.setData(drag.previewBaseNodes, drag.previewBaseEdges, null);
    drag.previewApplied = false;
  }

  private hideExtrudeLine(): void {
    if (!this.extrudeLine) return;
    this.helpersGroup?.remove(this.extrudeLine);
    this.extrudeLine.geometry.dispose();
    (this.extrudeLine.material as THREE.Material).dispose();
    this.extrudeLine = null;
  }

  /**
   * Commit a draw-wall drag: resolve/create BOTH endpoints from their snaps
   * (either may land on an existing node or split an existing edge and
   * connect there) and draw the wall between them. A degenerate drag
   * (release ≈ press) draws nothing — and creates no stray start node.
   */
  private commitDrawWall(skeleton: PointoramaWallSketch, startSnap: SnapResult, endSnap: SnapResult): void {
    if (this.isDegenerateExtrudeTarget(endSnap, startSnap.point)) return;
    // Gesture kind from the START side only: pressing on outline geometry
    // continues the outline, anything else draws a wall — and the two kinds
    // never connect (the start resolution below can therefore always connect).
    const kind = this.isOutlineAttachment(skeleton, { snap: startSnap }) ? 'outline' : 'wall';
    const sourceNodeId = this.resolveOrCreateTargetNode(skeleton, startSnap, kind, { snapshot: false });
    if (!sourceNodeId) return;
    this.commitExtrudeFromNode(skeleton, sourceNodeId, endSnap);
  }

  private commitExtrudeFromNode(skeleton: PointoramaWallSketch, sourceNodeId: string, snap: SnapResult): void {
    // Role from the SOURCE side only: extruding from outline geometry
    // continues the outline; the target snap never promotes a wall into an
    // outline (outlines and walls don't connect — see canConnectToNode).
    // Decided BEFORE the target resolution below — a snap onto an edge splits
    // it, replacing the snapped edge id.
    const outlineAttachment = this.isOutlineAttachment(skeleton, { nodeId: sourceNodeId });
    // The gesture already pushed one undo snapshot in onDrag/onDragEnd, so the
    // target-edge split must not push another (undo would otherwise take two).
    const targetNodeId = this.resolveOrCreateTargetNode(skeleton, snap, outlineAttachment ? 'outline' : 'wall', { snapshot: false });
    if (!targetNodeId || targetNodeId === sourceNodeId) return;

    // Avoid duplicate edges between the same pair.
    const existing = skeleton.getAllEdges().find((e) => (e.startNodeId === sourceNodeId && e.endNodeId === targetNodeId) || (e.startNodeId === targetNodeId && e.endNodeId === sourceNodeId));
    if (existing) return;

    const edges = skeleton.getAllEdges();
    const newEdge: SkeletonEdgeData = {
      id: this.generateId(),
      name: this.generateName('edge'),
      startNodeId: sourceNodeId,
      endNodeId: targetNodeId,
      roomIds: [],
      ...this.newEdgeDefaults(outlineAttachment),
      metadata: '',
    };
    const nextEdges = [...edges, newEdge];
    const nextNodes = skeleton.getNodes().map((n) => {
      if (n.id === sourceNodeId || n.id === targetNodeId) {
        return { ...n, edgeIds: [...n.edgeIds, newEdge.id] };
      }
      return n;
    });
    skeleton.setData(nextNodes, nextEdges, null);
    // Connect the new wall wherever it crosses an existing one (rebuilds +
    // refreshes visuals itself; no-op when there are no crossings, in which
    // case we still need the restore/visuals below).
    if (!this.splitNewEdgeAtCrossingsIfAny(skeleton, newEdge.id)) {
      this.deps.onWallSketchRestored?.();
      this.applyEditVisuals();
    }
  }

  /**
   * True when an extrude drag didn't actually draw a wall — the snapped target
   * lands on (or within ~1 cm of) the drag's origin point. Used to abort
   * commit so a click / cancel doesn't leave a stray starting node behind.
   * `snap.kind === 'node'` referencing the origin would also be degenerate, but
   * the origin isn't an existing node here, so a point-distance test suffices.
   */
  private isDegenerateExtrudeTarget(snap: SnapResult, originRel: THREE.Vector2): boolean {
    const DEGENERATE_DIST = 0.01; // metres
    return snap.point.distanceTo(originRel) < DEGENERATE_DIST;
  }

  private commitExtrudeFromEdge(skeleton: PointoramaWallSketch, sourceEdgeId: string, splitPointRel: THREE.Vector2, snap: SnapResult): void {
    // Splitting the source edge at splitPoint creates a new node + replaces
    // the edge with two halves. Then we draw a new edge from that new node to
    // the snap target. Outline continuation is decided up front from the
    // SOURCE edge only (the target never promotes — outlines and walls don't
    // connect) — both the source split and the target resolution replace the
    // snapped edge ids.
    const outlineAttachment = this.isOutlineAttachment(skeleton, { edgeId: sourceEdgeId });
    const edge = skeleton.getEdge(sourceEdgeId);
    if (!edge) return;
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return;

    // Degenerate drag (cursor never left the split point — e.g. a click/cancel
    // rather than a real drag-out) must NOT split the source edge. Splitting
    // unconditionally left a stray "starting node" behind on cancel. Bail
    // before mutating anything when the target lands on the split point.
    if (this.isDegenerateExtrudeTarget(snap, splitPointRel)) return;

    // Curved source edge: pin the split node onto the arc and give each half
    // the bulge that keeps it on the original circle.
    let halfBulges: [number | undefined, number | undefined] = [edge.bulge, edge.bulge];
    if (edgeIsCurved(edge)) {
      const aRel = this.toRelative(a.position);
      const bRel = this.toRelative(b.position);
      const projected = closestPointOnEdge(splitPointRel, aRel, bRel, edge.bulge);
      const total = arcEdgeLength(aRel, bRel, edge.bulge);
      const t = total > 1e-9 ? projected.s / total : 0.5;
      halfBulges = splitBulge(edge.bulge ?? 0, t);
      splitPointRel = new THREE.Vector2(projected.point.x, projected.point.y);
    }

    const z = skeleton.getFloorHeight();
    const splitAbs = this.toAbsolute(splitPointRel, z);
    const splitNodeId = this.generateId();
    const splitNode: SkeletonNodeData = {
      id: splitNodeId,
      name: this.generateName('node'),
      position: { x: splitAbs.x, y: splitAbs.y, z: splitAbs.z },
      roomIds: [...edge.roomIds],
      edgeIds: [],
      metadata: '',
    };
    const halfAId = this.generateId();
    const halfBId = this.generateId();
    const halfA: SkeletonEdgeData = { ...edge, id: halfAId, name: this.generateName('edge'), startNodeId: a.id, endNodeId: splitNodeId, bulge: halfBulges[0] };
    const halfB: SkeletonEdgeData = { ...edge, id: halfBId, name: this.generateName('edge'), startNodeId: splitNodeId, endNodeId: b.id, bulge: halfBulges[1] };

    let nodes = skeleton.getNodes().map((n) => {
      if (n.id === a.id) return { ...n, edgeIds: n.edgeIds.filter((eid) => eid !== sourceEdgeId).concat(halfAId) };
      if (n.id === b.id) return { ...n, edgeIds: n.edgeIds.filter((eid) => eid !== sourceEdgeId).concat(halfBId) };
      return n;
    });
    nodes = [...nodes, { ...splitNode, edgeIds: [halfAId, halfBId] }];
    let edges = skeleton
      .getAllEdges()
      .filter((e) => e.id !== sourceEdgeId)
      .concat(halfA, halfB);

    skeleton.setData(nodes, edges, null);
    // Gesture-level snapshot already pushed — suppress the split's own.
    const targetNodeId = this.resolveOrCreateTargetNode(skeleton, snap, outlineAttachment ? 'outline' : 'wall', { snapshot: false });
    if (!targetNodeId || targetNodeId === splitNodeId) {
      this.deps.onWallSketchRestored?.();
      this.applyEditVisuals();
      return;
    }
    const newEdgeId = this.generateId();
    const newEdge: SkeletonEdgeData = {
      id: newEdgeId,
      name: this.generateName('edge'),
      startNodeId: splitNodeId,
      endNodeId: targetNodeId,
      roomIds: [],
      ...this.newEdgeDefaults(outlineAttachment),
      metadata: '',
    };
    nodes = skeleton.getNodes().map((n) => {
      if (n.id === splitNodeId || n.id === targetNodeId) {
        return { ...n, edgeIds: [...n.edgeIds, newEdgeId] };
      }
      return n;
    });
    edges = [...skeleton.getAllEdges(), newEdge];
    skeleton.setData(nodes, edges, null);
    // Connect the new wall wherever it crosses an existing one.
    if (!this.splitNewEdgeAtCrossingsIfAny(skeleton, newEdgeId)) {
      this.deps.onWallSketchRestored?.();
      this.applyEditVisuals();
    }
  }

  /**
   * Given a snap result, return the node id to attach to. If the snap is to an
   * existing node, return its id. If on an edge, split the edge and return the
   * new node id. If free, create a new isolated node at the snap point.
   * `snap.point` is in session-relative coords; we convert to absolute when
   * writing to node.position.
   */
  /**
   * Kind-compatibility for topological connections: OUTLINE geometry (the
   * measured outer wall) and WALL geometry never connect to each other — they
   * share position snaps only. An orphan node (no incident edges) is kindless
   * and connects to either.
   */
  private canConnectToNode(skeleton: PointoramaWallSketch, nodeId: string, kind: 'outline' | 'wall'): boolean {
    const node = skeleton.getNode(nodeId);
    if (!node) return false;
    if (node.edgeIds.length === 0) return true; // orphan — kindless
    const outlineOnly = node.edgeIds.every((id) => skeleton.getEdge(id)?.role === 'OUTLINE');
    return kind === 'outline' ? outlineOnly : !outlineOnly;
  }

  private canConnectToEdge(skeleton: PointoramaWallSketch, edgeId: string, kind: 'outline' | 'wall'): boolean {
    const isOutline = skeleton.getEdge(edgeId)?.role === 'OUTLINE';
    return kind === 'outline' ? isOutline : !isOutline;
  }

  /** Connection kind of an existing node: null for a kindless orphan (no
   * incident edges), 'outline' when every incident edge is OUTLINE, 'wall'
   * otherwise. */
  private connectKindOfNode(skeleton: PointoramaWallSketch, nodeId: string): 'outline' | 'wall' | null {
    const node = skeleton.getNode(nodeId);
    if (!node || node.edgeIds.length === 0) return null;
    return node.edgeIds.every((id) => skeleton.getEdge(id)?.role === 'OUTLINE') ? 'outline' : 'wall';
  }

  private resolveOrCreateTargetNode(skeleton: PointoramaWallSketch, snap: SnapResult, kind: 'outline' | 'wall', opts: { snapshot?: boolean } = {}): string | null {
    // Existing node — connect directly, unless the kinds are incompatible
    // (wall↔outline): then keep the snapped POSITION but not the connection —
    // a fresh node lands there via the fall-through below.
    if (snap.kind === 'node' && snap.nodeId && this.canConnectToNode(skeleton, snap.nodeId, kind)) return snap.nodeId;

    // Edge-cross / on-edge — the cursor landed on (or a construction line
    // crossed) a snap edge. If that's a REAL skeleton edge of a compatible
    // kind, split it at the landing point and connect to the new junction
    // node. But the snap-edge set also includes SYNTHETIC ring segments — the
    // room outlines and wall shell/cavity rings (ids prefixed `room-edge:` /
    // `wall-shell:` / `wall-cavity:`) — which aren't edges in the skeleton and
    // can't be split. Snapping a new wall to one of those used to call
    // splitEdgeAt with a nonexistent edge id, which returned null and silently
    // BLOCKED creation. For a synthetic ring segment (or a kind-incompatible
    // edge) we just drop a fresh node at the snap point (fall through below):
    // the wall ends on the snapped position, which is what the snap was
    // asking for.
    if ((snap.kind === 'edge-cross' || snap.kind === 'on-edge') && snap.edgeId && this.isRealSkeletonEdge(skeleton, snap.edgeId) && this.canConnectToEdge(skeleton, snap.edgeId, kind)) {
      return this.splitEdgeAt(skeleton, snap.edgeId, snap.point, opts);
    }

    // No-snap, line-snap, or intersection-snap → create a fresh node at the
    // snap point.
    const z = skeleton.getFloorHeight();
    const abs = this.toAbsolute(snap.point, z);
    const newNode: SkeletonNodeData = {
      id: this.generateId(),
      name: this.generateName('node'),
      position: { x: abs.x, y: abs.y, z: abs.z },
      roomIds: [],
      edgeIds: [],
      metadata: '',
    };
    const nodes = [...skeleton.getNodes(), newNode];
    skeleton.setData(nodes, skeleton.getAllEdges(), null);
    return newNode.id;
  }

  /** Generate an opaque unique identifier for a node or edge. Reference fields (`startNodeId`, `endNodeId`, `edgeIds`, `roomIds`) point at these. */
  private generateId(): string {
    return crypto.randomUUID();
  }

  /**
   * Whether a draw gesture CONTINUES the merged outline: it hangs off an
   * outline node/edge, or its target snap lands on one. A node counts as
   * outline only when ALL its incident edges are outline — that way a normal
   * wall can still be attached to an outline junction without turning into a
   * boundary line, while extending an open outline chain from its endpoint
   * keeps drawing outline. Evaluate BEFORE any target-edge split — a split
   * replaces the snapped edge id.
   */
  private isOutlineAttachment(skeleton: PointoramaWallSketch, ref: { nodeId?: string | null; edgeId?: string | null; snap?: SnapResult | null }): boolean {
    const nodeIsOutline = (nodeId: string | null | undefined): boolean => {
      if (!nodeId) return false;
      const node = skeleton.getNode(nodeId);
      if (!node || node.edgeIds.length === 0) return false;
      return node.edgeIds.every((id) => skeleton.getEdge(id)?.role === 'OUTLINE');
    };
    const edgeIsOutline = (edgeId: string | null | undefined): boolean => !!edgeId && skeleton.getEdge(edgeId)?.role === 'OUTLINE';

    if (nodeIsOutline(ref.nodeId) || edgeIsOutline(ref.edgeId)) return true;
    const snap = ref.snap;
    if (snap) {
      if (snap.kind === 'node' && nodeIsOutline(snap.nodeId)) return true;
      if ((snap.kind === 'edge-cross' || snap.kind === 'on-edge') && edgeIsOutline(snap.edgeId)) return true;
    }
    return false;
  }

  /**
   * Type/thickness defaults for a freshly drawn edge. Merged-outline boundary
   * edges (`role:'OUTLINE'`, zero thickness, never skinned) are created only
   * when the gesture attaches to the outline (`outlineAttachment`, see
   * {@link isOutlineAttachment}) — or as the SEED case: the very first edge
   * drawn on an empty Measured floor is the start of its outer wall
   * (otherwise a measured outline could never be drawn from scratch).
   * Everything else is a standard inner wall.
   */
  private newEdgeDefaults(outlineAttachment: boolean): Pick<SkeletonEdgeData, 'leftSize' | 'rightSize' | 'wallType' | 'role'> {
    const sketch = this.deps.getSceneWallSketch();
    const measuredSeed = !!sketch && sketch.getAllEdges().filter((e) => e.id !== '__preview_edge__').length === 0 && (this.deps.viewer.floorplanRepresentation?.isOriginalActive(sketch.groupIdentifier) ?? false);
    return outlineAttachment || measuredSeed ? { leftSize: 0, rightSize: 0, wallType: 'OUTER', role: 'OUTLINE' } : { leftSize: 0.1, rightSize: 0.1, wallType: 'INNER' };
  }

  /** Generate a human/display label. Not used for identity. */
  private generateName(prefix: 'node' | 'edge'): string {
    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
  }

  // ─── Selection wrappers ─────────────────────────────────────────────
  // Thin forwarders to the WallSketchSelection module so call sites in this
  // file read at the same level of detail as before the extraction.
  //
  // Selecting an edge implicitly selects its endpoint nodes too — picking an
  // edge in this tool means "I want to operate on this segment," and every
  // operation (move, delete, extrude) needs the endpoints anyway. Done here
  // so both the click handler and box-select get the behavior for free.
  // Removal is left alone: the user may have selected the endpoint
  // separately and shouldn't lose it just because they removed the edge.

  private endpointNodeIds(edgeIds: Iterable<string> | string | null): string[] {
    if (edgeIds == null) return [];
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return [];
    const ids = typeof edgeIds === 'string' ? [edgeIds] : edgeIds;
    const out: string[] = [];
    for (const edgeId of ids) {
      const edge = skeleton.getEdge(edgeId);
      if (!edge) continue;
      out.push(edge.startNodeId, edge.endNodeId);
    }
    return out;
  }

  /**
   * True when the sketch's floor shows the Measured representation AND no
   * edit session is active — the skeleton graph is hidden then and only
   * OUTLINE-role geometry remains visible (via the skin boundary) and
   * therefore selectable. While EDITING, the graph (incl. the red error
   * segments) shows as an intermediate state and everything is selectable.
   */
  private measuredOutlineOnlyActive(): boolean {
    if (this.editing) return false;
    const sketch = this.deps.getSceneWallSketch();
    return !!sketch && (this.deps.viewer.floorplanRepresentation?.isOriginalActive(sketch.groupIdentifier) ?? false);
  }

  /** Normalize + filter edge ids to the editable set (outline-only on measured floors). */
  private selectableEdgeIds(edgeIds: Iterable<string> | string | null): string[] {
    const list = edgeIds == null ? [] : typeof edgeIds === 'string' ? [edgeIds] : Array.from(edgeIds);
    if (!this.measuredOutlineOnlyActive()) return list;
    const sketch = this.deps.getSceneWallSketch();
    return list.filter((id) => sketch?.getEdge(id)?.role === 'OUTLINE');
  }

  /** Normalize + filter node ids to the editable set (outline-attached only on measured floors). */
  private selectableNodeIds(nodeIds: Iterable<string> | string | null): string[] {
    const list = nodeIds == null ? [] : typeof nodeIds === 'string' ? [nodeIds] : Array.from(nodeIds);
    if (!this.measuredOutlineOnlyActive()) return list;
    const sketch = this.deps.getSceneWallSketch();
    return list.filter((id) => {
      const node = sketch?.getNode(id);
      return !!node && node.edgeIds.some((eid) => sketch?.getEdge(eid)?.role === 'OUTLINE');
    });
  }

  private setSelection(nodeIds: Iterable<string> | string | null, edgeIds: Iterable<string> | string | null): void {
    // Chokepoint for the no-selection capability: internal callers (commit
    // paths, merge-drop, box-select) may try to select their result — with
    // selection off, nothing may ever enter the shared selection state.
    if (!this.caps.allowSelect) return;
    // Measured floors: only outline geometry is visible/editable. Every
    // selection entry (click, drag targets, box-select, internal commits)
    // funnels through here, so the hidden skeleton can't be selected —
    // and thus never grows edit handles — no matter the input path.
    const edges = this.selectableEdgeIds(edgeIds);
    const baseNodes = this.selectableNodeIds(nodeIds);
    const merged = new Set<string>([...baseNodes, ...this.endpointNodeIds(edges)]);
    this.selection?.setSelection(merged, edges);
  }

  private addToSelection(nodeIds: Iterable<string>, edgeIds: Iterable<string>): void {
    if (!this.caps.allowSelect) return;
    const edges = this.selectableEdgeIds(edgeIds);
    const merged = new Set<string>([...this.selectableNodeIds(nodeIds), ...this.endpointNodeIds(edges)]);
    this.selection?.addToSelection(merged, edges);
  }

  private removeFromSelection(nodeIds: Iterable<string>, edgeIds: Iterable<string>): void {
    this.selection?.removeFromSelection(nodeIds, edgeIds);
  }

  /** Close the context menu when the selection becomes empty. Wired into the WallSketchSelection's onSelectionChanged. */
  private closeContextMenuIfEmpty(): void {
    // No selection ⇒ effectively empty — close the menu.
    if (!this.selection || this.selection.isEmpty()) {
      this.contextMenu?.hide();
    }
  }

  /**
   * Pop the context menu next to `worldAnchor` (in world coords) with items
   * built from the current selection. Called from "terminal" selection
   * actions: a plain click that lands on geometry, a Shift/Ctrl click that
   * left at least one item selected, or a box-drag end with hits. Items:
   *   - Delete                 (always, when something is selected)
   *   - Recalculate width      (when at least one edge is selected)
   *   - Set width manually     (when exactly one edge is selected)
   */
  private showContextMenuForSelection(worldAnchor: THREE.Vector3): void {
    const sel = this.selection;
    if (!sel) return;
    if (sel.getSelectedNodeIds().size === 0 && sel.getSelectedEdgeIds().size === 0) return;
    if (!this.contextMenu) {
      this.contextMenu = new RadialMenu(this.deps.viewer);
    }
    const menu = this.contextMenu;
    const items: RadialMenuItem[] = [];

    // Delete — always shown. Closes the menu since the selection is empty
    // afterwards. (closeContextMenuIfEmpty also catches this, but the
    // explicit flag avoids one frame of stale menu while the deletion runs.)
    items.push({
      id: 'delete',
      iconSvg: ICON_TRASH_SVG,
      tooltip: this.t('Delete selection'),
      closeOnClick: true,
      onClick: () => this.deleteSelection(),
    });

    // Width ops only make sense on WALL edges — outline edges are
    // zero-thickness boundary lines (the batch ops below skip them too), and
    // MEASURED floors render centerlines without a band at all, so the width
    // items disappear there entirely (matching the hidden side handles).
    const skeletonForMenu = this.deps.getSceneWallSketch();
    const measuredFloor = skeletonForMenu ? (this.deps.viewer.floorplanRepresentation?.isOriginalActive(skeletonForMenu.groupIdentifier) ?? false) : false;
    const wallEdgeCount = !skeletonForMenu || measuredFloor ? 0 : [...sel.getSelectedEdgeIds()].filter((id) => skeletonForMenu.getEdge(id)?.role !== 'OUTLINE').length;

    if (sel.getSelectedEdgeIds().size > 0) {
      // Auto width — recompute wall thickness from the point cloud.
      if (wallEdgeCount > 0) {
        items.push({
          id: 'auto-width',
          iconSvg: ICON_REFRESH_SVG,
          tooltip: wallEdgeCount === 1 ? this.t('Auto width') : this.t('Auto width ({count} edges)', { args: { count: wallEdgeCount } }),
          closeOnClick: true,
          onClick: () => this.recalculateSelectedEdgeWidths(),
        });

        // Set thickness — type an exact total wall width; both sides grow/shrink
        // equally about the centerline. Applies to every selected wall edge.
        items.push({
          id: 'set-thickness',
          iconSvg: ICON_RULER_SVG,
          tooltip: wallEdgeCount === 1 ? this.t('Set thickness') : this.t('Set thickness ({count} edges)', { args: { count: wallEdgeCount } }),
          onClick: () => this.startSetThicknessInput(worldAnchor),
        });
      }

      // Orthogonalize — snap near-90° corners among the selected edges to exact
      // 90°, moving nodes as little as possible. Needs a corner (≥ 2 edges) to
      // be useful.
      if (sel.getSelectedEdgeIds().size >= 2) {
        items.push({
          id: 'orthogonalize',
          iconSvg: ICON_RIGHT_ANGLE_SVG,
          tooltip: this.t('Square up angles ({count} edges)', { args: { count: sel.getSelectedEdgeIds().size } }),
          closeOnClick: true,
          onClick: () => this.orthogonalizeSelectedEdges(),
        });

        // Make parallel — align the selected walls onto their common dominant
        // direction, same minimal-movement solve as square-up.
        items.push({
          id: 'parallelize',
          iconSvg: ICON_PARALLEL_SVG,
          tooltip: this.t('Make parallel ({count} edges)', { args: { count: sel.getSelectedEdgeIds().size } }),
          closeOnClick: true,
          onClick: () => this.parallelizeSelectedEdges(),
        });
      }
    }

    // Manual width is the drag-handle interaction — see `startResizeWallSide`.

    menu.show({ type: 'items', items }, worldAnchor);
  }

  // ─── Recalculate width (per-edge) ───────────────────────────────────

  /**
   * Re-measure wall thickness for each currently-selected edge using the
   * same algorithm as the pipeline's `Measure Wall Thickness` pass, but
   * scoped to just the selection. Pushes one undo snapshot for the batch.
   *
   * Requires `deps.buildFloorSkeleton` — the per-edge measurement routine
   * needs the pipeline-style FloorSkeleton (with rooms + wall polygons),
   * which only the tool can construct.
   */
  private recalculateSelectedEdgeWidths(): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return;
    const sel = skeleton.getSelection();
    if (sel.getSelectedEdgeIds().size === 0) return;
    if (!this.deps.buildFloorSkeleton) {
      console.warn('[WallSketchEditSession] recalculate-width: deps.buildFloorSkeleton not provided');
      return;
    }

    const floorSk = this.deps.buildFloorSkeleton();
    if (!floorSk) return;

    this.pushWallSketchUndoSnapshot();

    // Map persisted-edge ids ↔ FloorSkeleton edges. The FloorSkeleton built
    // from the scene shares the same edge ids since the tool's
    // updateSceneWallSketch uses the same generation, so direct lookup works.
    const updates = new Map<string, { leftSize: number; rightSize: number }>();
    for (const edgeId of sel.getSelectedEdgeIds()) {
      // Outline edges have no band to measure.
      if (skeleton.getEdge(edgeId)?.role === 'OUTLINE') continue;
      // Curved edges keep their authored sizes — the pipeline measurement
      // assumes a straight centerline. (Follow-up: sample perpendicular at
      // the arc midpoint tangent.)
      if (edgeIsCurved(skeleton.getEdge(edgeId) ?? {})) continue;
      const measured = measureEdgeThickness(floorSk, edgeId);
      if (measured) updates.set(edgeId, measured);
    }
    if (updates.size === 0) return;

    const newEdges = skeleton.getAllEdges().map((e) => {
      const u = updates.get(e.id);
      return u ? { ...e, leftSize: u.leftSize, rightSize: u.rightSize } : e;
    });
    skeleton.setData(skeleton.getNodes(), newEdges, null);
    // Auto-measured widths adopt the skinning's pass-through weld too.
    this.weldSketchThicknesses(skeleton);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.selection?.reapplyVisuals();
  }

  // ─── Align edges (square up 90° corners / make parallel) ─────────────

  /**
   * Snap the selected edges' near-90° corners to exact 90°. The solver aligns
   * each near-axis edge to the selection's dominant grid orientation while
   * keeping edge midpoints stable (minimal node movement), leaving nodes shared
   * with non-selected edges anchored so the rest of the sketch is untouched.
   * Pushes one undo snapshot for the batch.
   */
  private orthogonalizeSelectedEdges(): void {
    this.alignSelectedEdges(orthogonalizeEdges, { anchorForeignNodes: true });
  }

  /** Align the selected walls onto their common dominant direction ("make
   *  parallel") — same minimal-movement solve, single 180° axis. Unlike
   *  square-up, junction nodes shared with NON-selected walls may move (the
   *  neighbours follow their node): the selected walls are usually each
   *  embedded in the sketch, and anchoring every foreign junction would pin
   *  both endpoints of every edge — a guaranteed no-op on non-connected
   *  selections. */
  private parallelizeSelectedEdges(): void {
    this.alignSelectedEdges(parallelizeEdges, { anchorForeignNodes: false });
  }

  /** Shared driver of the square-up / make-parallel actions: collect the
   *  straight selected edges, optionally anchor every node the selection
   *  doesn't own, run `solve`, and apply the moved nodes with one undo
   *  snapshot. */
  private alignSelectedEdges(solve: (input: OrthogonalizeInput) => OrthogonalizeResult, opts: { anchorForeignNodes: boolean }): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return;
    const sel = skeleton.getSelection();
    const selectedEdgeIds = sel.getSelectedEdgeIds();
    if (selectedEdgeIds.size < 2) return;

    const allEdges = skeleton.getAllEdges();
    const selectedEdges: OrthoEdge[] = [];
    for (const e of allEdges) {
      // Curved edges have no chord orientation to align — exclude them
      // from the solver (their endpoints may still move via neighbours; the
      // bulge is preserved, so the arc follows its chord).
      if (selectedEdgeIds.has(e.id) && !edgeIsCurved(e)) selectedEdges.push({ id: e.id, startNodeId: e.startNodeId, endNodeId: e.endNodeId });
    }
    if (selectedEdges.length < 2) return;

    // Square-up anchors any node touched by a non-selected edge so aligning
    // the selection can't drag the surrounding sketch (curved edges count as
    // non-selected: their endpoints stay put). Make-parallel skips this —
    // junction nodes move and their neighbour walls follow.
    const anchoredNodeIds = new Set<string>();
    if (opts.anchorForeignNodes) {
      for (const e of allEdges) {
        if (selectedEdgeIds.has(e.id) && !edgeIsCurved(e)) continue;
        anchoredNodeIds.add(e.startNodeId);
        anchoredNodeIds.add(e.endNodeId);
      }
    }

    const nodePositions = new Map<string, { x: number; y: number }>();
    for (const n of skeleton.getNodes()) nodePositions.set(n.id, { x: n.position.x, y: n.position.y });

    const { movedNodes } = solve({ nodes: nodePositions, selectedEdges, anchoredNodeIds });
    if (movedNodes.size === 0) return;

    this.pushWallSketchUndoSnapshot();

    const newNodes = skeleton.getNodes().map((n) => {
      const moved = movedNodes.get(n.id);
      return moved ? { ...n, position: { x: moved.x, y: moved.y, z: n.position.z } } : n;
    });
    skeleton.setData(newNodes, skeleton.getAllEdges(), null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.selection?.reapplyVisuals();
  }

  // ─── Set thickness (type an exact width) ─────────────────────────────

  /**
   * Morph the context menu into a numeric input seeded with the selection's
   * current total wall width (average across selected edges). On confirm, set
   * every selected edge's total width to the typed value while keeping the
   * PHYSICAL MIDDLE of each wall band (the midpoint between its two faces)
   * fixed — so an asymmetric wall grows/shrinks toward both faces evenly from
   * where it currently sits rather than recentering on the skeleton line. If a
   * side would go negative (wall too thin to stay centered), it clamps to 0 and
   * the remainder spills onto the other side. Cancel restores the item menu.
   * Edits are done in the user's display unit.
   */
  private startSetThicknessInput(worldAnchor: THREE.Vector3): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return;
    const sel = skeleton.getSelection();
    const edgeIds = sel.getSelectedEdgeIds();
    if (edgeIds.size === 0 || !this.contextMenu) return;

    // Outline edges stay zero-thickness — the input applies to walls only.
    const selectedEdges = skeleton.getAllEdges().filter((e) => edgeIds.has(e.id) && e.role !== 'OUTLINE');
    if (selectedEdges.length === 0) return;

    // Seed with the average current total width across the selected edges.
    const avgTotal = selectedEdges.reduce((sum, e) => sum + (e.leftSize ?? 0) + (e.rightSize ?? 0), 0) / selectedEdges.length;

    const unit = this.deps.viewer.lengthUnitDisplay;
    this.contextMenu.setContent({
      type: 'input',
      label: this.t('thickness'),
      unit: unit.code,
      step: String(getSnapStepDisplay(unit.code)),
      initialValue: metersToDisplay(avgTotal, unit).toFixed(2),
      onConfirm: (raw) => {
        const parsed = parseFloat(raw);
        if (!isFinite(parsed) || parsed <= 0) {
          this.contextMenu?.hide();
          return;
        }
        const width = displayToMeters(parsed, unit);
        this.pushWallSketchUndoSnapshot();
        const newEdges = skeleton.getAllEdges().map((e) => {
          if (!edgeIds.has(e.id) || e.role === 'OUTLINE') return e;
          const left = e.leftSize ?? 0;
          const right = e.rightSize ?? 0;
          // Offset of the wall band's physical middle from the skeleton line.
          const mid = (right - left) / 2;
          // Keep that middle fixed: each face sits width/2 from it.
          let newRight = mid + width / 2;
          let newLeft = width / 2 - mid;
          // Too thin to stay centered — clamp the negative side to 0 and put the
          // whole width on the other side.
          if (newLeft < 0) {
            newLeft = 0;
            newRight = width;
          } else if (newRight < 0) {
            newRight = 0;
            newLeft = width;
          }
          return { ...e, leftSize: newLeft, rightSize: newRight };
        });
        skeleton.setData(skeleton.getNodes(), newEdges, null);
        // The typed width adopts the skinning's pass-through weld on confirm.
        this.weldSketchThicknesses(skeleton);
        this.deps.onWallSketchRestored?.();
        this.applyEditVisuals();
        this.selection?.reapplyVisuals();
        this.contextMenu?.hide();
      },
      onCancel: () => this.showContextMenuForSelection(worldAnchor),
    });
  }

  // ─── Set width manually (single-edge) ────────────────────────────────

  /**
   * Enter the set-width interaction mode. While active, the cursor's
   * perpendicular distance to the edge defines the wall thickness on
   * whichever side the cursor is. A live preview line + label follow the
   * cursor; a click commits and exits the mode; ESC cancels.
   */
  /**
   * Construction lines for the set-width snap context: just parallel +
   * perpendicular to the source edge. Matches "drawing a perpendicular off
   * the edge" mental model, and keeps the visual link tied to the edge
   * being measured.
   */
  private buildSetWidthConstructionLines(skeleton: PointoramaWallSketch, edge: SkeletonEdgeData): ConstructionLine[] {
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return [];
    const refX = this.referencePoint?.x ?? 0;
    const refY = this.referencePoint?.y ?? 0;
    const dx = b.position.x - a.position.x;
    const dy = b.position.y - a.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return [];
    const ux = dx / len;
    const uy = dy / len;
    const midX = (a.position.x + b.position.x) / 2 - refX;
    const midY = (a.position.y + b.position.y) / 2 - refY;
    const midpoint = { x: midX, y: midY };
    return [
      { origin: midpoint, direction: { x: ux, y: uy }, kind: 'parallel', sourceEdgeId: edge.id, sourceMidpoint: midpoint },
      { origin: midpoint, direction: { x: -uy, y: ux }, kind: 'perpendicular', sourceEdgeId: edge.id, sourceMidpoint: midpoint },
    ];
  }

  // ─── Wall-edge handles ─────────────────────────────────────────────
  //
  // Two SphereMarkers (one per side) sit at the midpoints of a wall's
  // offset edges and follow the live `leftSize` / `rightSize`. Visible only
  // when the cursor is near a wall (its centerline is hovered). Click and
  // drag = adjust that side's width; same snap engine + visuals as the
  // menu set-width path. Handles are NOT rendered in non-editing mode.

  /**
   * Midpoint of the rendered offset edge for one wall side. Reads the
   * mitered corner points from the skeleton so the handle sits exactly at
   * the visible midpoint of the side rectangle — corner cuts at junctions
   * shift the offset endpoints inward, so a perpendicular projection from
   * the centerline midpoint would land outside the rendered wall and look
   * visually disconnected.
   */
  private wallHandlePosition(skeleton: PointoramaWallSketch, edge: SkeletonEdgeData, side: WallSide): THREE.Vector3 | null {
    return this.wallHandlePlacement(skeleton, edge, side)?.pos ?? null;
  }

  /**
   * Position + outward direction of one side's width handle. The triangle
   * glyph points along `outward` ("pull away from the wall to thicken").
   */
  private wallHandlePlacement(skeleton: PointoramaWallSketch, edge: SkeletonEdgeData, side: WallSide): { pos: THREE.Vector3; outwardX: number; outwardY: number } | null {
    // Place the handle at the CENTERLINE midpoint offset perpendicular by this
    // side's thickness. Using the mitered junction corners' midpoint (as before)
    // pulls the handle far off the wall when a sharp/very-asymmetric junction
    // projects a corner point a long way out — the offset-from-midpoint is
    // always on the wall body, mid-span, regardless of how the ends miter.
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return null;
    const dx = b.position.x - a.position.x;
    const dy = b.position.y - a.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return null;
    // Anchor at the half-arc-length point offset along the LOCAL left
    // perpendicular (matches the skinning offset convention: left boundary =
    // centerline + perp * leftSize). For straight edges this is exactly the
    // chord midpoint ± chord perpendicular.
    const aXY = { x: a.position.x, y: a.position.y };
    const bXY = { x: b.position.x, y: b.position.y };
    const half = arcEdgeLength(aXY, bXY, edge.bulge) / 2;
    const { point, tangent } = pointAtArcLength(aXY, bXY, edge.bulge, half);
    const perpX = -tangent.y;
    const perpY = tangent.x;
    const sideSign = side === 'left' ? 1 : -1;
    const offset = sideSign * (side === 'left' ? (edge.leftSize ?? 0.1) : (edge.rightSize ?? 0.1));
    return {
      pos: new THREE.Vector3(point.x + perpX * offset, point.y + perpY * offset, skeleton.getFloorHeight()),
      outwardX: perpX * sideSign,
      outwardY: perpY * sideSign,
    };
  }

  /**
   * Lazy-init the two handle markers and parent them to the helpers group.
   * Hidden by default — `updateWallHandles` flips visibility per frame.
   */
  /** Lazy-create (and return) the left/right handle marker pair for `edgeId`. */
  private ensureWallHandlePair(edgeId: string): { left: TriangleHandleMarker; right: TriangleHandleMarker } | null {
    const existing = this.wallHandlePairs.get(edgeId);
    if (existing) return existing;
    const group = this.ensureHelpersGroup();
    if (!group) return null;
    const make = (): TriangleHandleMarker => {
      const m = new TriangleHandleMarker(this.deps.viewer);
      m.setColor(WALL_HANDLE_COLOR);
      m.setScaleFactor(WALL_HANDLE_BASE_SCALE);
      m.setRenderOrder(HELPER_RENDER_ORDER);
      m.visible = false;
      group.add(m);
      return m;
    };
    const pair = { left: make(), right: make() };
    this.wallHandlePairs.set(edgeId, pair);
    return pair;
  }

  /**
   * Refresh which walls show drag handles. `edgeIds` is the set of edges that
   * should have handles right now (the hovered wall plus every selected edge —
   * handles lock to selected edges). Pairs for edges no longer in the set are
   * hidden + disposed. The hover box follows `boxEdgeId` (the single
   * hovered/primary wall) for clarity. Pass an empty set + null to hide all.
   */
  private updateWallHandles(edgeIds: Iterable<string> | string | null, boxEdgeId?: string | null): void {
    const skeleton = this.deps.getSceneWallSketch();
    const wanted = new Set<string>(edgeIds == null ? [] : typeof edgeIds === 'string' ? [edgeIds] : edgeIds);

    // MEASURED floors render centerlines only — no wall band exists there, so
    // thickness side-handles and the band hover box are suppressed for EVERY
    // edge (move/bow/insert/delete still work). On Wall-sketch floors only
    // the outline edges are exempt: zero-thickness boundary lines have no
    // band to resize either.
    if (skeleton && (this.deps.viewer.floorplanRepresentation?.isOriginalActive(skeleton.groupIdentifier) ?? false)) {
      wanted.clear();
    } else if (skeleton) {
      for (const eid of [...wanted]) {
        if (skeleton.getEdge(eid)?.role === 'OUTLINE') wanted.delete(eid);
      }
    }

    // Drop pairs whose edge is no longer shown (or vanished from the skeleton).
    for (const [eid, pair] of this.wallHandlePairs) {
      if (!skeleton || !wanted.has(eid) || !skeleton.getEdge(eid)) {
        pair.left.parent?.remove(pair.left);
        pair.right.parent?.remove(pair.right);
        pair.left.dispose();
        pair.right.dispose();
        this.wallHandlePairs.delete(eid);
      }
    }

    if (!skeleton || wanted.size === 0) {
      this.updateHoverBox(null, null);
      return;
    }

    for (const eid of wanted) {
      const edge = skeleton.getEdge(eid);
      if (!edge) continue;
      const pair = this.ensureWallHandlePair(eid);
      if (!pair) continue;
      const leftPlace = this.wallHandlePlacement(skeleton, edge, 'left');
      const rightPlace = this.wallHandlePlacement(skeleton, edge, 'right');
      if (leftPlace) {
        pair.left.position.copy(this.worldToHelpersLocal({ x: leftPlace.pos.x, y: leftPlace.pos.y, z: leftPlace.pos.z + 0.05 }));
        pair.left.setDirection(leftPlace.outwardX, leftPlace.outwardY);
        pair.left.visible = true;
      } else pair.left.visible = false;
      if (rightPlace) {
        pair.right.position.copy(this.worldToHelpersLocal({ x: rightPlace.pos.x, y: rightPlace.pos.y, z: rightPlace.pos.z + 0.05 }));
        pair.right.setDirection(rightPlace.outwardX, rightPlace.outwardY);
        pair.right.visible = true;
      } else pair.right.visible = false;
    }

    // Hover box follows the explicit `boxEdgeId`, else the single shown edge.
    const boxId = boxEdgeId !== undefined ? boxEdgeId : wanted.size === 1 ? [...wanted][0] : null;
    const boxEdge = boxId ? (skeleton.getEdge(boxId) ?? null) : null;
    this.updateHoverBox(skeleton, boxEdge);
  }

  /**
   * Draw (or hide) a translucent box over the hovered wall sized to its full
   * thickness (`leftSize` + `rightSize`) and length, so it's clear which wall
   * is hovered and how thick it is. The quad is the wall's straight offset
   * rectangle — the same basis the handles and hover hit-test use, so it hugs
   * the wall body and ignores junction mitering. Pass a null edge to hide.
   */
  private updateHoverBox(skeleton: PointoramaWallSketch | null, edge: SkeletonEdgeData | null): void {
    if (!skeleton || !edge) {
      if (this.hoverBoxMesh) this.hoverBoxMesh.visible = false;
      return;
    }
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) {
      if (this.hoverBoxMesh) this.hoverBoxMesh.visible = false;
      return;
    }
    const dx = b.position.x - a.position.x;
    const dy = b.position.y - a.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) {
      if (this.hoverBoxMesh) this.hoverBoxMesh.visible = false;
      return;
    }
    const ls = edge.leftSize ?? 0.1;
    const rs = edge.rightSize ?? 0.1;
    const z = skeleton.getFloorHeight();

    // Left/right offset polylines with paired sampling — a quad strip. A
    // straight edge yields the familiar 2×2 rectangle; a curved one the
    // tessellated arc band.
    const { left, right } = tessellateBandPair({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge, ls, rs);

    this.ensureHoverBox();
    if (!this.hoverBoxMesh) return;
    // Two triangles per strip quad, lifted just above the floor. The vertex
    // count varies with tessellation — resize the buffer when needed.
    const lift = z + 0.04;
    const quadCount = left.length - 1;
    const vertexCount = quadCount * 6;
    let positions = this.hoverBoxMesh.geometry.attributes.position as THREE.BufferAttribute | undefined;
    if (!positions || positions.count !== vertexCount) {
      this.hoverBoxMesh.geometry.dispose();
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3));
      this.hoverBoxMesh.geometry = geom;
      positions = geom.attributes.position as THREE.BufferAttribute;
    }
    let v = 0;
    const put = (p: { x: number; y: number }): void => {
      const local = this.worldToHelpersLocal({ x: p.x, y: p.y, z: lift });
      positions.setXYZ(v++, local.x, local.y, local.z);
    };
    for (let i = 0; i < quadCount; i++) {
      // (leftᵢ, leftᵢ₊₁, rightᵢ₊₁) and (leftᵢ, rightᵢ₊₁, rightᵢ)
      put(left[i]);
      put(left[i + 1]);
      put(right[i + 1]);
      put(left[i]);
      put(right[i + 1]);
      put(right[i]);
    }
    positions.needsUpdate = true;
    this.hoverBoxMesh.geometry.computeBoundingSphere();
    this.hoverBoxMesh.visible = true;
  }

  /** Lazy-init the hover-box mesh (strip-sized on first draw) in the helpers group. */
  private ensureHoverBox(): void {
    if (this.hoverBoxMesh) return;
    const group = this.ensureHelpersGroup();
    if (!group) return;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6 * 3), 3));
    this.hoverBoxMat = new THREE.MeshBasicMaterial({
      color: WALL_HANDLE_COLOR,
      transparent: true,
      opacity: 0.22,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.hoverBoxMesh = new THREE.Mesh(geom, this.hoverBoxMat);
    this.hoverBoxMesh.renderOrder = HELPER_RENDER_ORDER - 1; // under the handle balls
    this.hoverBoxMesh.frustumCulled = false;
    this.hoverBoxMesh.visible = false;
    group.add(this.hoverBoxMesh);
  }

  /**
   * Hit-test the cursor against the currently-shown handles. Returns the
   * matched handle (or null) along with which side. Pixel-space test like
   * `updateHover`. Treats a hit as "within GRAB_WALL_HANDLE_RADIUS_PX of the
   * handle's projected center" — a forgiving box so the small side balls are
   * easy to grab; nearest-distance still disambiguates left vs right.
   */
  private pickWallHandle(event: MouseEvent): { edgeId: string; side: WallSide } | null {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton || this.wallHandlePairs.size === 0) return null;
    const mousePx = this.hitTester.mousePx(event);
    // Nearest handle across EVERY shown wall (hovered + all selected) wins, so
    // the grab disambiguates both which edge and which side.
    let best: { edgeId: string; side: WallSide; dist: number } | null = null;
    for (const edgeId of this.wallHandlePairs.keys()) {
      const edge = skeleton.getEdge(edgeId);
      if (!edge) continue;
      for (const side of ['left', 'right'] as WallSide[]) {
        const pos = this.wallHandlePosition(skeleton, edge, side);
        if (!pos) continue;
        const px = this.hitTester.projectWorldToPixel({ x: pos.x, y: pos.y, z: pos.z });
        if (!px) continue;
        const d = px.distanceTo(mousePx);
        if (d < GRAB_WALL_HANDLE_RADIUS_PX && (!best || d < best.dist)) {
          best = { edgeId, side, dist: d };
        }
      }
    }
    return best ? { edgeId: best.edgeId, side: best.side } : null;
  }

  /**
   * Light up exactly the handle the cursor is over (`this.hoveredHandle` for a
   * wall side, `this.hoveredMoveEdgeId` for a move handle) and dim every other
   * shown handle. Brightens the ball (`SphereMarker.highlight`) and bumps its
   * scale so the user can see precisely what a mousedown would grab. Called
   * from `updateHover` after the pick state is resolved; safe to call when both
   * targets are null (it just clears all highlights).
   */
  private refreshHandleHighlights(): void {
    for (const [edgeId, pair] of this.wallHandlePairs) {
      const hot = this.hoveredHandle?.edgeId === edgeId ? this.hoveredHandle.side : null;
      this.applyHandleHover(pair.left, hot === 'left');
      this.applyHandleHover(pair.right, hot === 'right');
    }
    for (const [edgeId, marker] of this.edgeMoveHandles) {
      this.applyHandleHover(marker, this.hoveredMoveEdgeId === edgeId);
    }
    for (const [edgeId, marker] of this.bowHandles) {
      this.applyHandleHover(marker, this.hoveredBowEdgeId === edgeId, BOW_HANDLE_BASE_SCALE, BOW_HANDLE_HOVER_SCALE);
    }
  }

  /** Toggle a single handle's hover look (color brighten + slight grow). */
  private applyHandleHover(marker: EditHandleVisual, hovered: boolean, baseScale = WALL_HANDLE_BASE_SCALE, hoverScale = WALL_HANDLE_HOVER_SCALE): void {
    if (hovered) {
      marker.highlight();
      marker.setScaleFactor(hoverScale);
    } else {
      marker.unHighlight();
      marker.setScaleFactor(baseScale);
    }
  }

  private disposeWallHandles(): void {
    for (const pair of this.wallHandlePairs.values()) {
      pair.left.parent?.remove(pair.left);
      pair.right.parent?.remove(pair.right);
      pair.left.dispose();
      pair.right.dispose();
    }
    this.wallHandlePairs.clear();
    if (this.hoverBoxMesh) {
      this.hoverBoxMesh.parent?.remove(this.hoverBoxMesh);
      this.hoverBoxMesh.geometry.dispose();
      this.hoverBoxMesh = null;
    }
    if (this.hoverBoxMat) {
      this.hoverBoxMat.dispose();
      this.hoverBoxMat = null;
    }
    this.hoveredHandle = null;
    this.clickTargetHandle = null;
  }

  // ─── Edge MOVE handle (perpendicular wall move) ─────────────────────

  /**
   * World position of an edge's move handle: the half-arc-length point of the
   * centerline. For straight edges that's the chord midpoint; for arched
   * walls it rides the curve (the arc apex) instead of hanging at the chord
   * midpoint, which sits off the wall body once the wall bows.
   */
  private edgeMoveHandlePosition(skeleton: PointoramaWallSketch, edge: SkeletonEdgeData): THREE.Vector3 | null {
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return null;
    const aXY = { x: a.position.x, y: a.position.y };
    const bXY = { x: b.position.x, y: b.position.y };
    const half = arcEdgeLength(aXY, bXY, edge.bulge) / 2;
    const { point } = pointAtArcLength(aXY, bXY, edge.bulge, half);
    return new THREE.Vector3(point.x, point.y, skeleton.getFloorHeight());
  }

  /** Refresh the move handles to cover exactly `edgeIds`; pool + hide the rest. */
  private updateEdgeMoveHandles(edgeIds: Iterable<string> | string | null): void {
    const skeleton = this.deps.getSceneWallSketch();
    const wanted = new Set<string>(edgeIds == null ? [] : typeof edgeIds === 'string' ? [edgeIds] : edgeIds);

    for (const [eid, marker] of this.edgeMoveHandles) {
      if (!skeleton || !wanted.has(eid) || !skeleton.getEdge(eid)) {
        marker.parent?.remove(marker);
        marker.dispose();
        this.edgeMoveHandles.delete(eid);
      }
    }
    if (!skeleton || wanted.size === 0) return;

    const group = this.ensureHelpersGroup();
    if (!group) return;
    for (const eid of wanted) {
      const edge = skeleton.getEdge(eid);
      if (!edge) continue;
      let marker = this.edgeMoveHandles.get(eid);
      if (!marker) {
        marker = new SphereMarker(this.deps.viewer);
        marker.setColor(EXTRUDE_PREVIEW_COLOR); // oil-blue, distinct from the green width handles
        marker.setScaleFactor(WALL_HANDLE_BASE_SCALE);
        marker.setRenderOrder(HELPER_RENDER_ORDER);
        group.add(marker);
        this.edgeMoveHandles.set(eid, marker);
      }
      const pos = this.edgeMoveHandlePosition(skeleton, edge);
      if (pos) {
        marker.position.copy(this.worldToHelpersLocal({ x: pos.x, y: pos.y, z: pos.z + 0.05 }));
        marker.visible = true;
      } else marker.visible = false;
    }
  }

  /** Pick the move handle (edge id) nearest the cursor within grab radius. */
  private pickEdgeMoveHandle(event: MouseEvent): string | null {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton || this.edgeMoveHandles.size === 0) return null;
    const mousePx = this.hitTester.mousePx(event);
    let best: { edgeId: string; dist: number } | null = null;
    for (const edgeId of this.edgeMoveHandles.keys()) {
      const edge = skeleton.getEdge(edgeId);
      if (!edge) continue;
      const pos = this.edgeMoveHandlePosition(skeleton, edge);
      if (!pos) continue;
      const px = this.hitTester.projectWorldToPixel({ x: pos.x, y: pos.y, z: pos.z });
      if (!px) continue;
      const d = px.distanceTo(mousePx);
      if (d < GRAB_WALL_HANDLE_RADIUS_PX && (!best || d < best.dist)) best = { edgeId, dist: d };
    }
    return best ? best.edgeId : null;
  }

  private disposeEdgeMoveHandles(): void {
    for (const marker of this.edgeMoveHandles.values()) {
      marker.parent?.remove(marker);
      marker.dispose();
    }
    this.edgeMoveHandles.clear();
    this.hoveredMoveEdgeId = null;
    this.clickTargetMoveEdgeId = null;
  }

  // ─── Bow handle (curve a wall into a circular arc) ───────────────────

  private bowHandlePosition(skeleton: PointoramaWallSketch, edge: SkeletonEdgeData): THREE.Vector3 | null {
    return this.bowHandlePlacement(skeleton, edge)?.pos ?? null;
  }

  /**
   * Position + facing of an edge's bow handle glyph. The move handle owns
   * the wall's mid-arc point, so the bow glyph always PARKS beside it —
   * radially outward past the apex for curved edges, to the LEFT of the
   * chord midpoint for straight ones — offset by that side's wall thickness
   * PLUS a screen-space clearance, so it always sits fully outside the wall
   * body regardless of thickness. The glyph's convex side faces along `dir`
   * — the way the wall bows (or would bow) when dragged further that way.
   */
  private bowHandlePlacement(skeleton: PointoramaWallSketch, edge: SkeletonEdgeData): { pos: THREE.Vector3; dirX: number; dirY: number } | null {
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return null;
    const z = skeleton.getFloorHeight();
    const dx = b.position.x - a.position.x;
    const dy = b.position.y - a.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return null;
    const mid = { x: (a.position.x + b.position.x) / 2, y: (a.position.y + b.position.y) / 2 };

    // Park direction: radially outward (chord midpoint → apex) for curved
    // edges, left perpendicular for straight ones (arbitrary but stable).
    let anchor = mid;
    let dirX = -dy / len;
    let dirY = dx / len;
    if (edgeIsCurved(edge)) {
      const apex = arcMidpoint({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge);
      const ax = apex.x - mid.x;
      const ay = apex.y - mid.y;
      const aLen = Math.hypot(ax, ay);
      if (aLen > 1e-9) {
        anchor = apex;
        dirX = ax / aLen;
        dirY = ay / aLen;
      }
    }

    // px → meters at the handle's depth, same camera math as the snap radii.
    const camera = this.deps.viewer.scene.getActiveCamera();
    const size = this.deps.viewer.renderer.getSize(new THREE.Vector2());
    const anchorV3 = new THREE.Vector3(anchor.x, anchor.y, z);
    const pixelsPerMeter = Math.max(1e-6, Utils.projectedRadius(1, camera, camera.position.distanceTo(anchorV3), size.width || 1000, size.height || 1000));
    // Always park OUTSIDE the wall body: the anchor sits on the centerline
    // (chord midpoint / arc apex), so add the wall's thickness on the park
    // side — leftSize when the park direction points to the edge's left,
    // rightSize otherwise — plus the screen-space clearance.
    const leftDot = dirX * (-dy / len) + dirY * (dx / len);
    const sideThickness = leftDot >= 0 ? (edge.leftSize ?? 0.1) : (edge.rightSize ?? 0.1);
    const offset = sideThickness + BOW_HANDLE_CLEARANCE_PX / pixelsPerMeter;
    return { pos: new THREE.Vector3(anchor.x + dirX * offset, anchor.y + dirY * offset, z), dirX, dirY };
  }

  /** Refresh the bow handles to cover exactly `edgeIds`; pool + drop the rest. */
  private updateBowHandles(edgeIds: Iterable<string> | string | null): void {
    const skeleton = this.deps.getSceneWallSketch();
    const wanted = new Set<string>(edgeIds == null ? [] : typeof edgeIds === 'string' ? [edgeIds] : edgeIds);

    for (const [eid, marker] of this.bowHandles) {
      if (!skeleton || !wanted.has(eid) || !skeleton.getEdge(eid)) {
        marker.parent?.remove(marker);
        marker.dispose();
        this.bowHandles.delete(eid);
      }
    }
    if (!skeleton || wanted.size === 0) return;

    const group = this.ensureHelpersGroup();
    if (!group) return;
    for (const eid of wanted) {
      const edge = skeleton.getEdge(eid);
      if (!edge) continue;
      let marker = this.bowHandles.get(eid);
      if (!marker) {
        marker = new BowHandleMarker(this.deps.viewer);
        marker.setColor(BOW_HANDLE_COLOR);
        marker.setScaleFactor(BOW_HANDLE_BASE_SCALE);
        marker.setRenderOrder(HELPER_RENDER_ORDER);
        group.add(marker);
        this.bowHandles.set(eid, marker);
      }
      const place = this.bowHandlePlacement(skeleton, edge);
      if (place) {
        marker.position.copy(this.worldToHelpersLocal({ x: place.pos.x, y: place.pos.y, z: place.pos.z + 0.05 }));
        marker.setDirection(place.dirX, place.dirY);
        marker.visible = true;
      } else marker.visible = false;
    }
  }

  /** Pick the bow handle (edge id) nearest the cursor within grab radius. */
  private pickBowHandle(event: MouseEvent): string | null {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton || this.bowHandles.size === 0) return null;
    const mousePx = this.hitTester.mousePx(event);
    let best: { edgeId: string; dist: number } | null = null;
    for (const edgeId of this.bowHandles.keys()) {
      const edge = skeleton.getEdge(edgeId);
      if (!edge) continue;
      const pos = this.bowHandlePosition(skeleton, edge);
      if (!pos) continue;
      const px = this.hitTester.projectWorldToPixel({ x: pos.x, y: pos.y, z: pos.z });
      if (!px) continue;
      const d = px.distanceTo(mousePx);
      if (d < BOW_HANDLE_GRAB_RADIUS_PX && (!best || d < best.dist)) best = { edgeId, dist: d };
    }
    return best ? best.edgeId : null;
  }

  private disposeBowHandles(): void {
    for (const marker of this.bowHandles.values()) {
      marker.parent?.remove(marker);
      marker.dispose();
    }
    this.bowHandles.clear();
    this.hoveredBowEdgeId = null;
    this.clickTargetBowEdgeId = null;
  }

  private startBowEdge(edgeId: string, event: MouseEvent): void {
    const skeleton = this.deps.getSceneWallSketch();
    const edge = skeleton?.getEdge(edgeId);
    if (!skeleton || !edge) return;
    if (!this.editing) {
      this.enterEditMode();
      if (!this.editing) return;
    }
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return;

    const aRel = this.toRelative(a.position);
    const bRel = this.toRelative(b.position);
    const chord = new THREE.Vector2(bRel.x - aRel.x, bRel.y - aRel.y);
    const chordLen = chord.length();
    if (chordLen < 1e-9) return;
    const dir = chord.divideScalar(chordLen);
    const normal = new THREE.Vector2(-dir.y, dir.x); // left-perpendicular
    const mid = new THREE.Vector2((aRel.x + bRel.x) / 2, (aRel.y + bRel.y) / 2);

    const floor = projectMouseToFloor(event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
    if (!floor) return;
    const floorRel = this.toRelative(floor);
    // Anchor the cursor offset to the TRUE apex (not the displayed handle,
    // which sits beside the midpoint for straight edges) so the first drag
    // frame doesn't jump the wall to a phantom sagitta.
    const s0 = sagittaFromBulge(chordLen, edge.bulge ?? 0);
    const apex = new THREE.Vector2(mid.x + normal.x * s0, mid.y + normal.y * s0);

    this.drag = {
      kind: 'bow-edge',
      edgeId,
      dragOrigin: mid.clone(),
      cursorOffset: floorRel.clone().sub(apex),
      originalPositions: new Map(),
      snapshotted: false,
      lastSnap: null,
      startButton: 0,
      moveDir: { x: dir.x, y: dir.y },
      moveNormal: { x: normal.x, y: normal.y },
      bowChordLen: chordLen,
    };
    this.deps.viewer.inputHandler?.blockOrbitControls?.();
  }

  /**
   * Per-frame bow apply. `candidateRel` is the offset-corrected cursor in
   * relative coords; only its component along the chord's left normal is used
   * (the sagitta). Default/Shift snap back to a perfectly straight wall near
   * zero sagitta; Ctrl bows freely. Near-full circles are clamped.
   */
  private applyBowEdge(skeleton: PointoramaWallSketch, edgeId: string, candidateRel: THREE.Vector2, modifier: SnapModifier, event: MouseEvent): void {
    const drag = this.drag;
    if (!drag || !drag.moveNormal || !drag.bowChordLen) return;
    const edge = skeleton.getEdge(edgeId);
    if (!edge) return;
    const mid = drag.dragOrigin;
    const normal = drag.moveNormal;
    let sagitta = (candidateRel.x - mid.x) * normal.x + (candidateRel.y - mid.y) * normal.y;

    const z = skeleton.getFloorHeight();
    const midAbs = this.toAbsolute(new THREE.Vector2(mid.x, mid.y), z);

    // Snap back to straight near zero sagitta (default + Shift; Ctrl = free).
    let snappedStraight = false;
    if (modifier !== 'none') {
      const camera = this.deps.viewer.scene.getActiveCamera();
      const size = this.deps.viewer.renderer.getSize(new THREE.Vector2());
      const sample = new THREE.Vector3(midAbs.x, midAbs.y, midAbs.z);
      const pixelsPerMeter = Math.max(1e-6, Utils.projectedRadius(1, camera, camera.position.distanceTo(sample), size.width || 1000, size.height || 1000));
      const threshold = Math.max(0.02, BOW_SNAP_STRAIGHT_PX / pixelsPerMeter);
      if (Math.abs(sagitta) < threshold) {
        sagitta = 0;
        snappedStraight = true;
      }
    }

    let bulge = bulgeFromSagitta(drag.bowChordLen, sagitta);
    const maxBulge = Math.tan(ARC_MAX_INCLUDED_ANGLE / 4);
    bulge = Math.max(-maxBulge, Math.min(maxBulge, bulge));
    // Persist straight walls without the key — keeps the JSON clean and makes
    // "back to straight" bit-identical to a never-curved edge.
    const normalized = Math.abs(bulge) < BULGE_STRAIGHT_EPS ? undefined : bulge;

    const newEdges = skeleton.getAllEdges().map((e) => (e.id === edgeId ? { ...e, bulge: normalized } : e));
    skeleton.setData(skeleton.getNodes(), newEdges, null);
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.selection?.reapplyVisuals();

    // Marker at the chord midpoint signals the "snapped straight" engage.
    this.updateSnapMarker(snappedStraight ? { point: new THREE.Vector2(mid.x, mid.y), kind: 'node' } : { point: candidateRel.clone(), kind: 'free' });

    // Live radius readout, same floating label the width drag uses.
    if (this.setWidthLabel) {
      if (normalized !== undefined) {
        const radius = radiusFromBulge(drag.bowChordLen, normalized);
        this.setWidthLabel.textContent = `R ${radius.toFixed(2)} m`;
        this.setWidthLabel.style.display = 'block';
        const rect = this.deps.viewer.renderer.domElement.getBoundingClientRect();
        this.setWidthLabel.style.left = `${event.clientX - rect.left + 12}px`;
        this.setWidthLabel.style.top = `${event.clientY - rect.top + 12}px`;
      } else {
        this.setWidthLabel.style.display = 'none';
      }
    }
  }

  private ensureSetWidthVisual(): void {
    if (this.setWidthVisual) return;
    const group = this.ensureHelpersGroup();
    if (!group) return;
    const geom = new THREE.BufferGeometry();
    geom.setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    this.setWidthVisualMat = new THREE.LineDashedMaterial({
      color: WALL_HANDLE_COLOR, // brand green — matches the side handles
      dashSize: 0.08,
      gapSize: 0.04,
      depthTest: false,
      transparent: true,
      opacity: 0.95,
    });
    this.setWidthVisual = new THREE.Line(geom, this.setWidthVisualMat);
    this.setWidthVisual.computeLineDistances();
    this.setWidthVisual.renderOrder = 999;
    this.setWidthVisual.visible = false;
    group.add(this.setWidthVisual);
  }

  private ensureSetWidthLabel(): void {
    if (this.setWidthLabel) return;
    this.setWidthLabel = document.createElement('div');
    this.setWidthLabel.style.cssText = `
      position: absolute;
      display: none;
      padding: 3px 6px;
      background: rgba(0, 221, 255, 0.92);
      color: #0a0a0a;
      font-family: monospace;
      font-size: 12px;
      font-weight: 600;
      border-radius: 3px;
      pointer-events: none;
      z-index: 10001;
      white-space: nowrap;
    `;
    this.deps.viewer.renderArea.appendChild(this.setWidthLabel);
  }

  private disposeSetWidthMode(): void {
    if (this.setWidthVisual) {
      this.helpersGroup?.remove(this.setWidthVisual);
      this.setWidthVisual.geometry.dispose();
      this.setWidthVisualMat?.dispose();
      this.setWidthVisual = null;
      this.setWidthVisualMat = null;
    }
    if (this.setWidthLabel) {
      this.setWidthLabel.remove();
      this.setWidthLabel = null;
    }
  }

  /**
   * Drop ids from the selection that no longer exist on the skeleton. Call
   * after a topology mutation that may have removed selected items. The
   * WallSketchSelection module rebuilds the visuals + notifies us via
   * `onSelectionChanged` (which closes empty-selection context menus).
   */
  private clearStaleSelection(deletedNodeId: string | null, deletedEdgeId: string | null): void {
    this.selection?.clearStale(deletedNodeId, deletedEdgeId);
  }

  private clearStaleHoverAfterDelete(deletedNodeId: string | null, deletedEdgeId: string | null): void {
    let changed = false;
    if (deletedNodeId && this.hoveredNodeId === deletedNodeId) {
      this.hoveredNodeId = null;
      changed = true;
    }
    if (deletedEdgeId && this.hoveredEdgeId === deletedEdgeId) {
      this.hoveredEdgeId = null;
      this.hoveredEdgePoint = null;
      this.hidePreviewMarker();
      changed = true;
    }
    if (changed) this.syncHoverToWallSketch();
  }

  // ─── Preview / snap marker helpers ──────────────────────────────────

  private showPreviewMarker(at: THREE.Vector3): void {
    const group = this.ensureHelpersGroup();
    if (!group) return; // not editing — nothing to attach to
    if (!this.previewMarker) {
      this.previewMarker = new SphereMarker(this.deps.viewer);
      this.previewMarker.setColor(PREVIEW_NODE_COLOR);
      // Match the regular skeleton-node size so the preview reads as "if I
      // click here, this node would appear" rather than a tiny dot.
      this.previewMarker.setScaleFactor(1.2);
      this.previewMarker.setRenderOrder(HELPER_RENDER_ORDER);
      group.add(this.previewMarker);
    }
    // Position is relative to the helpers group, which sits at the session
    // reference point. Float math here stays close to (0, 0).
    const local = this.worldToHelpersLocal({ x: at.x, y: at.y, z: at.z + 0.03 });
    this.previewMarker.position.copy(local);
    this.previewMarker.visible = true;
  }

  private hidePreviewMarker(): void {
    if (this.previewMarker) this.previewMarker.visible = false;
  }

  private updateSnapMarker(snap: SnapResult): void {
    if (snap.kind === 'free') {
      this.hideSnapMarker();
      return;
    }
    const group = this.ensureHelpersGroup();
    if (!group) return;
    if (!this.snapMarker) {
      this.snapMarker = new SphereMarker(this.deps.viewer);
      this.snapMarker.setColor(SNAP_NODE_COLOR);
      this.snapMarker.setScaleFactor(0.6);
      this.snapMarker.setRenderOrder(HELPER_RENDER_ORDER);
      group.add(this.snapMarker);
    }
    const z = (this.deps.getSceneWallSketch()?.getFloorHeight() ?? 0) + 0.04;
    const abs = this.toAbsolute(snap.point, z);
    const local = this.worldToHelpersLocal(abs);
    this.snapMarker.position.copy(local);
    this.snapMarker.visible = true;
    this.snapMarker.setColor(this.snapColorForKind(snap.kind));
  }

  private snapColorForKind(kind: SnapKind): THREE.Color {
    // Brand palette, but still distinct per snap kind so the user can tell an
    // exact-node lock from a construction-line / intersection snap.
    switch (kind) {
      case 'node':
        return BRAND_NEON_GREEN; // brand green — exact node lock
      case 'intersection':
        return BRAND_FRENCH_VIOLET; // violet — two construction lines crossing
      case 'line':
        return BRAND_OIL_BLUE; // oil blue — single construction line
      default:
        return SNAP_NODE_COLOR;
    }
  }

  private hideSnapMarker(): void {
    if (this.snapMarker) this.snapMarker.visible = false;
  }

  /**
   * Build the per-line visual descriptors and hand them to the WallSketchSnapVisuals
   * helper. Translates relative-coord engine output into the absolute-world
   * coords the visuals layer expects, and resolves source ids → world positions
   * by reading the current skeleton.
   */
  private updateSnapVisuals(skeleton: PointoramaWallSketch, snap: SnapResult): void {
    if (!snap.lines || snap.lines.length === 0) {
      this.snapVisuals?.hide();
      return;
    }
    if (!this.snapVisuals) {
      // Pass a getter so the visuals layer always attaches to the helpers
      // group (relative-coord parent) when one exists. Falls back to the
      // viewer scene if we're not currently editing.
      this.snapVisuals = new WallSketchSnapVisuals(this.deps.viewer, () => this.helpersGroup);
    }
    const z = skeleton.getFloorHeight() + 0.025;
    const snapAbs = this.toAbsolute(snap.point, z);
    const snapV = new THREE.Vector3(snapAbs.x, snapAbs.y, snapAbs.z);

    const renders: SnapLineRender[] = snap.lines.map((line) => {
      const originAbs = this.toAbsolute(new THREE.Vector2(line.origin.x, line.origin.y), z);
      let sourceAnchor: THREE.Vector3 | undefined;
      let sourceEdgeEndpoints: { a: THREE.Vector3; b: THREE.Vector3 } | undefined;
      let sourceEdgeDirection: THREE.Vector2 | undefined;

      if (line.sourceEdgeId) {
        const edge = skeleton.getEdge(line.sourceEdgeId);
        const a = edge ? skeleton.getNode(edge.startNodeId) : undefined;
        const b = edge ? skeleton.getNode(edge.endNodeId) : undefined;
        if (edge && a && b) {
          sourceEdgeEndpoints = {
            a: new THREE.Vector3(a.position.x, a.position.y, z),
            b: new THREE.Vector3(b.position.x, b.position.y, z),
          };
          sourceAnchor = new THREE.Vector3((a.position.x + b.position.x) / 2, (a.position.y + b.position.y) / 2, z);
          const dx = b.position.x - a.position.x;
          const dy = b.position.y - a.position.y;
          const len = Math.hypot(dx, dy);
          if (len > 1e-6) sourceEdgeDirection = new THREE.Vector2(dx / len, dy / len);
        }
      }
      if (!sourceAnchor && line.sourceNodeId) {
        const node = skeleton.getNode(line.sourceNodeId);
        if (node) sourceAnchor = new THREE.Vector3(node.position.x, node.position.y, z);
      }

      return {
        origin: new THREE.Vector3(originAbs.x, originAbs.y, originAbs.z),
        direction: new THREE.Vector2(line.direction.x, line.direction.y),
        kind: line.kind,
        snapPoint: snapV.clone(),
        sourceAnchor,
        sourceEdgeEndpoints,
        sourceEdgeDirection,
        sourceNodeId: line.sourceNodeId,
      };
    });

    this.snapVisuals.update(renders, skeleton);
  }

  /**
   * Lazily build (or reuse) the helpers parent group and pin its world
   * position to the session reference point. All visual helpers — preview
   * marker, snap marker, extrude line, construction guides — should attach
   * to this group and use coords *relative* to the reference point. That
   * keeps SphereMarker auto-scale and Line2 vertex math in float32-friendly
   * territory even when the actual scene sits at UTM coordinates.
   *
   * Safe to call multiple times. Caller must have set `this.referencePoint`
   * (i.e. be inside an edit session); returns null otherwise.
   */
  private ensureHelpersGroup(): THREE.Group | null {
    if (!this.referencePoint) {
      // Outside edit mode (the create tool's hover marker renders before any
      // gesture) pin on the sketch's first node — the same anchor
      // enterEditMode picks, so a follow-up gesture agrees on coordinates.
      const first = this.deps.getSceneWallSketch()?.getNodes()[0];
      if (!first) return null;
      this.referencePoint = new THREE.Vector2(first.position.x, first.position.y);
    }
    if (!this.helpersGroup) {
      this.helpersGroup = new THREE.Group();
      this.helpersGroup.name = 'wall_sketch_edit_helpers';
      // Top-most UI scene so handles / hover / snap visuals always draw over
      // the floorplan skeleton mesh (and everything else).
      this.deps.viewer.uiScene.add(this.helpersGroup);
    }
    const skeleton = this.deps.getSceneWallSketch();
    const z = skeleton?.getFloorHeight() ?? 0;
    this.helpersGroup.position.set(this.referencePoint.x, this.referencePoint.y, z);
    return this.helpersGroup;
  }

  /**
   * Public accessor for the visuals module, which needs to attach its own
   * children (construction guides etc.) into the same relative-coord parent.
   * Returns null when not editing.
   */
  getHelpersGroup(): THREE.Group | null {
    return this.helpersGroup;
  }

  /**
   * Convert an absolute world point to coords relative to the helpers group's
   * world position. Helpers attaching geometry under `helpersGroup` should
   * pass their absolute positions through this.
   */
  worldToHelpersLocal(world: { x: number; y: number; z: number }): THREE.Vector3 {
    const group = this.helpersGroup;
    if (!group) return new THREE.Vector3(world.x, world.y, world.z);
    return new THREE.Vector3(world.x - group.position.x, world.y - group.position.y, world.z - group.position.z);
  }

  private disposeHelpers(): void {
    if (this.previewMarker) {
      this.helpersGroup?.remove(this.previewMarker);
      this.previewMarker.dispose();
      this.previewMarker = null;
    }
    if (this.snapMarker) {
      this.helpersGroup?.remove(this.snapMarker);
      this.snapMarker.dispose();
      this.snapMarker = null;
    }
    this.hideExtrudeLine();
    // Selection is owned by the skeleton itself; nothing to dispose here.
    if (this.snapVisuals) {
      this.snapVisuals.dispose(this.deps.getSceneWallSketch());
      this.snapVisuals = null;
    }
    if (this.helpersGroup) {
      this.deps.viewer.scene.scene.remove(this.helpersGroup);
      this.helpersGroup = null;
    }
    if (this.boxSelect) {
      this.boxSelect.dispose();
      this.boxSelect = null;
    }
    if (this.contextMenu) {
      this.contextMenu.dispose();
      this.contextMenu = null;
    }
    this.disposeSetWidthMode();
    this.disposeWallHandles();
    this.disposeBowHandles();
    this.disposeEdgeMoveHandles();
  }

  // ─── Box-select ─────────────────────────────────────────────────────

  /** Lazy-create the box-select controller (which owns the AreaSelector). */
  private ensureBoxSelect(): BoxSelectController {
    if (!this.boxSelect) {
      this.boxSelect = new BoxSelectController(this.deps.viewer, {
        boxStyle: {
          border: '1.5px dashed #00ddff',
          background: 'rgba(0, 221, 255, 0.08)',
        },
      });
    }
    return this.boxSelect;
  }

  /**
   * Begin a box-select drag. The controller captures modifier flags so the
   * drag-end commit knows whether to replace / add to / remove from the
   * selection. Stores a `box-select` drag state with no geometry-mutation
   * fields.
   */
  private startBoxSelect(event: MouseEvent): void {
    this.ensureBoxSelect().start(event);

    this.drag = {
      kind: 'box-select',
      // The remaining fields are unused for this drag kind; supplied as
      // dummies so the DragState shape isn't widened with optionals.
      dragOrigin: new THREE.Vector2(),
      cursorOffset: new THREE.Vector2(),
      originalPositions: new Map(),
      snapshotted: false,
      lastSnap: null,
    };
    this.deps.viewer.inputHandler?.blockOrbitControls?.();
  }

  /** Track cursor during a box-select drag — just forward to the controller. */
  private updateBoxSelect(event: MouseEvent): void {
    this.boxSelect?.update(event);
  }

  /**
   * Commit a box-select. Read the rectangle's pixel bounds from the
   * controller, project every node + edge endpoint to canvas pixels, filter
   * against the rect, then apply the captured modifier mode to update the
   * selection. When the resulting selection is non-empty, pop the context
   * menu anchored at the box's end-corner world position (caller passes it in).
   */
  private finishBoxSelect(menuAnchor: THREE.Vector3 | null): void {
    if (!this.boxSelect) return;
    const skeleton = this.deps.getSceneWallSketch();
    const result = this.boxSelect.finish();
    if (!skeleton || !result) return;

    const { mode, rect } = result;
    const { minX, maxX, minY, maxY } = rect;

    // Same interactivity gate as hover: hidden skeleton geometry (as-built
    // floor, nothing selected) must not be box-selectable — only the
    // OUTLINE-role elements are. The box select only mutates the selection
    // right below, so the no-selection state still holds here even though
    // the arming drag entered edit mode.
    const hitNodeIds: string[] = [];
    for (const node of skeleton.getNodes()) {
      if (!this.isNodeInteractive(skeleton, node.id)) continue;
      const px = this.hitTester.projectWorldToPixel(node.position);
      if (px && PixelHitTester.pointInRect(px, minX, minY, maxX, maxY)) hitNodeIds.push(node.id);
    }

    // Edge rule — CAD-style window vs crossing: by DEFAULT an edge is only
    // taken when it lies FULLY inside the rectangle (both endpoints). With
    // Ctrl held the CROSSING behavior applies: any pixel-space overlap
    // counts — either endpoint inside, or the segment passing through the
    // box (Cohen-Sutherland clip). Ctrl arrives as the controller's 'remove'
    // mode; HERE it means crossing-SELECT, so it applies as a replace —
    // box-removal isn't a wall-editor gesture (ctrl-click toggles instead).
    const crossing = mode === 'remove';
    const effectiveMode = mode === 'remove' ? 'replace' : mode;
    const hitEdgeIds: string[] = [];
    for (const edge of skeleton.getAllEdges()) {
      if (!this.isEdgeInteractive(skeleton, edge.id)) continue;
      const a = skeleton.getNode(edge.startNodeId);
      const b = skeleton.getNode(edge.endNodeId);
      if (!a || !b) continue;
      const ap = this.hitTester.projectWorldToPixel(a.position);
      const bp = this.hitTester.projectWorldToPixel(b.position);
      if (!ap || !bp) continue;
      const hit = crossing
        ? PixelHitTester.segmentIntersectsRect(ap, bp, minX, minY, maxX, maxY)
        : PixelHitTester.pointInRect(ap, minX, minY, maxX, maxY) && PixelHitTester.pointInRect(bp, minX, minY, maxX, maxY);
      if (hit) hitEdgeIds.push(edge.id);
    }

    if (effectiveMode === 'replace') {
      this.setSelection(hitNodeIds, hitEdgeIds);
    } else if (effectiveMode === 'add') {
      this.addToSelection(hitNodeIds, hitEdgeIds);
    } else {
      this.removeFromSelection(hitNodeIds, hitEdgeIds);
    }

    // If the box-drag left at least one item selected, pop the context
    // menu next to the cursor's world position; otherwise hide any
    // already-shown menu.
    const sel = this.selection;
    if (menuAnchor && sel && (sel.getSelectedNodeIds().size > 0 || sel.getSelectedEdgeIds().size > 0)) {
      this.showContextMenuForSelection(menuAnchor);
    } else {
      this.contextMenu?.hide();
    }
  }

  // ─── Undo/redo plumbing ─────────────────────────────────────────────

  private captureWallSketchData(): WallSketchUndoData | null {
    const skeleton = this.deps.getSceneWallSketch();
    // Empty sketches are capturable — drawing the first wall on a fresh
    // sketch must be able to undo/cancel back to empty.
    if (!skeleton) return null;
    return {
      nodes: skeleton.getNodes().map((n) => ({
        ...n,
        position: { ...n.position },
        roomIds: [...n.roomIds],
        edgeIds: [...n.edgeIds],
      })),
      edges: skeleton.getAllEdges().map((e) => ({
        ...e,
        roomIds: [...e.roomIds],
      })),
    };
  }

  private restoreWallSketchData(data: WallSketchUndoData): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return;
    skeleton.setData(data.nodes, data.edges, null);
    this.deps.onWallSketchRestored?.();
    if (this.editing) this.applyEditVisuals();
    this.deps.viewer.scene.dispatchEvent({
      type: 'edit_skeleton',
      value: {
        annotationIdentifier: skeleton.identifier,
        nodes: data.nodes,
        edges: data.edges,
      },
    });
  }

  private createWallSketchSnapshot(initialData: WallSketchUndoData): Snapshot {
    let data = initialData;
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        const currentData = this.captureWallSketchData() ?? { nodes: [], edges: [] };
        this.restoreWallSketchData(data);
        data = currentData;
        return snapshot;
      },
    };
    return snapshot;
  }

  /**
   * Identifier of the sketch this session is currently editing, or null when
   * not in edit mode. The React property panel uses this to decide whether
   * the selected element's data should come from the WORKING scene skeleton
   * (this session) instead of the persisted properties.
   */
  getEditedSketchId(): string | null {
    if (!this.editing) return null;
    return this.deps.getSceneWallSketch()?.identifier ?? null;
  }

  /**
   * Apply an element edit coming from OUTSIDE the canvas (the right-panel
   * property inputs) to the session's WORKING skeleton. Pushes an undo
   * snapshot and refreshes the edit visuals — the change commits with Enter
   * and reverts with Esc exactly like a canvas edit. Returns false (and does
   * nothing) when the session is not editing.
   */
  applyPanelEdit(nodes: SkeletonNodeData[], edges: SkeletonEdgeData[]): boolean {
    if (!this.editing) return false;
    const skeleton = this.deps.getSceneWallSketch();
    if (!skeleton) return false;
    this.pushWallSketchUndoSnapshot();
    skeleton.setData(nodes, edges, skeleton.getMetadata());
    this.deps.onWallSketchRestored?.();
    this.applyEditVisuals();
    this.selection?.reapplyVisuals();
    return true;
  }

  pushWallSketchUndoSnapshot(): void {
    const data = this.captureWallSketchData();
    if (!data) return;
    const mgr = this.deps.getUndoRedo();
    if (!mgr) return;
    mgr.pushState(this.createWallSketchSnapshot(data));
    if (this.editing) this.sessionPushCount++;
  }

  private dropSessionUndoEntries(): void {
    const mgr = this.deps.getUndoRedo();
    if (!mgr) return;
    for (let i = 0; i < this.sessionPushCount; i++) {
      mgr.popState();
    }
    this.sessionPushCount = 0;
  }

  // ─── Edit-mode visuals ──────────────────────────────────────────────

  private applyEditVisuals(): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (skeleton) {
      if (!this.originalLineColor) {
        this.originalLineColor = skeleton.lineMaterial.color.clone();
      }
      skeleton.lineMaterial.color = EDIT_MODE_COLOR;
      skeleton.lineMaterial.needsUpdate = true;
      for (const marker of skeleton.spheres) {
        marker.setColor(EDIT_MODE_COLOR);
      }
      // Wall-skinning fills are now always rendered transparent by the
      // skeleton itself — no per-edit override needed.
    }

    // setData() rebuilt the spheres — any source-node tints we cached point at
    // dead markers. Drop the bookkeeping; the next snap frame will retint
    // freshly. This must run AFTER the new spheres get their EDIT_MODE_COLOR
    // so the cached "previous color" we'd capture next frame is correct.
    this.snapVisuals?.forgetTints();

    // The loop above repainted EVERY sphere with EDIT_MODE_COLOR, clobbering the
    // highlight on any selected node. Reapply the selection so its visuals stay
    // in sync — otherwise selected nodes silently lose their highlight after any
    // edit that doesn't happen to remove a selected id. Callers that already
    // reapply afterwards just do it once more (idempotent).
    this.selection?.reapplyVisuals();

    if (!this.previousCursor) {
      this.previousCursor = this.deps.viewer.renderer.domElement.style.cursor;
    }
    this.deps.viewer.renderer.domElement.style.cursor = 'pointer';
  }

  private removeEditVisuals(): void {
    const skeleton = this.deps.getSceneWallSketch();
    if (skeleton && this.originalLineColor) {
      skeleton.lineMaterial.color = this.originalLineColor;
      skeleton.lineMaterial.needsUpdate = true;
      for (const marker of skeleton.spheres) {
        marker.setColor(this.originalLineColor);
      }
    }
    this.originalLineColor = null;

    this.deps.viewer.renderer.domElement.style.cursor = this.previousCursor || '';
    this.previousCursor = '';
  }
}
